// src/i18n/translations.js
// Add new keys as you go. Pages call t('nav.buy'), t('home.hero.titleA') etc.
// English is the source of truth — Greek falls back to English if a key is missing.

export const translations = {
  en: {
    nav: {
      // Top-level CTAs
      listProperty: 'List Property',
      findBroker: 'Find Agent',
      valuation: 'Estimate',
      findAgent: 'Find Agent',
      // Icon labels
      notifications: 'Notifications',
      watchlist: 'Watchlist',
      profile: 'Profile',
      // Notifications dropdown
      markAllRead: 'Mark all read',
      noNotifications: 'No notifications yet.',
      viewAllNotifications: 'View all notifications & messages',
      // Watchlist dropdown
      myWatchlist: 'My Watchlist',
      savedSuffix: 'saved',
      noSavedProperties: 'No saved properties yet.',
      browseListings: 'Browse listings',
      viewAllSaved: 'View all saved properties',
      perMonthShort: 'mo',
      // Profile dropdown
      myProfile: 'My Profile',
      myListings: 'My Listings',
      inbox: 'Inbox',
      settings: 'Settings',
      logout: 'Log Out',
      signIn: 'Sign In',
      signInSub: 'Access your account',
      register: 'Register',
      registerSub: 'Create a free account',
      // Drawer
      menu: 'Menu',
      // Notification tabs
      alerts: 'Alerts',
      messages: 'Messages',
      noAlerts: 'No alerts yet.',
      noMessages: 'No messages yet.',
      noMessagesYet: 'No messages yet',
      openFullInbox: 'Open Full Inbox',
      goToMessages: 'Go to Messages',
      // Time labels
      justNow: 'Just now',
      minutesShort: 'm ago',
      hoursShort: 'h ago',
      // Fallback names
      agent: 'Agent',
      buyer: 'Buyer',
      // Old keys kept for compatibility
      buy: 'Buy',
      rent: 'Rent',
      sell: 'Sell',
      tools: 'Tools',
      pro: 'Pro',
    },
    home: {
      hero: {
        titleA: 'Find Your',
        titleB: 'Perfect',
        titleC: 'Property Today',
        subtitle: 'Browse Listings · Find Agents · Connect with Technicians',
      },
      search: {
        buy: 'Buy',
        rent: 'Rent',
        location: 'City or address…',
        propertyType: 'Property type',
        priceRange: 'Price range',
        upTo: 'Up to',
        from: 'From',
        to: 'to',
        button: 'Search',
        types: {
          house: 'House',
          apartment: 'Apartment',
          villa: 'Villa',
          commercial: 'Commercial',
          underConstruction: 'Under Construction',
        },
      },
      sectionLabel: 'WHAT SETS US APART',
      services: {
        title: 'Our Services',
        subtitle:
          'CosmoEstates is more than a real estate marketplace. We give you the tools to make smarter, more confident property decisions in Cyprus.',
        valuation: {
          title: 'Property Price Estimate',
          description:
            "Get a free market-based price estimate for any property in Cyprus. Fill in a few details and we'll email you a starting-point estimate — useful as a ballpark, not a formal valuation.",
          bullets: [
            'Instant indicative estimate',
            'Estimate sent directly to your email',
            '100% free — no account needed',
          ],
          cta: 'Get My Free Estimate',
        },
        renovation: {
          title: 'Renovation Tool',
          description:
            'Plan your renovation with confidence. Get an indicative total renovation cost range based on your selections and connect directly with verified technicians for every step of the project.',
          bullets: ['Connect with verified technicians', 'Indicative total renovation cost range', 'Room-by-room cost breakdown'],
          cta: 'Try Renovation Tool',
        },
        neighbourhood: {
          title: 'Neighbourhood Intelligence',
          description:
            'Before you buy, know everything about the area. Our data engine scores every neighbourhood in Cyprus on transport, schools, amenities and expat community density, alongside the official Property Price Index.',
          bullets: [
            'Transport & connectivity scores',
            'School catchment maps',
            'Property Price Index (CBC data)',
            'Expat community density',
          ],
          cta: 'Explore Neighbourhoods',
        },
        mortgage: {
          title: 'Financial Compass',
          description:
            "Plan your purchase with confidence — estimate monthly repayments, total interest and overpayment scenarios for any property in Cyprus.",
          bullets: [
            'Monthly repayment estimate',
            'Interest vs principal breakdown',
            'Year-by-year chart',
            'No account needed',
          ],
          cta: 'Open Calculator',
        },
      },
      boostedListings: {
        title: 'Featured Listings',
        viewAll: 'View all listings',
        viewDetails: 'View Details',
      },
      style: {
        title: 'Find Your Style',
        mediterranean: { tag: 'SUN-DRENCHED LIVING BY THE SEA', name: 'Mediterranean Houses' },
        modern: { tag: 'ARCHITECTURE MEETS ISLAND LUXURY', name: 'Modern Villas' },
        luxury: { tag: 'ELEVATED CITY AND COASTAL LIVING', name: 'Luxury Apartments' },
      },
      pro: {
        eyebrow: 'COSMOESTATES PRO',
        title: 'The smarter way to buy in Cyprus',
        subtitle:
          'Upgrade to Pro and unlock every tool you need to make confident, data-driven property decisions — all in one place.',
        getStarted: 'Get Started with Pro',
        seePlans: 'See all plans',
        hidePlans: 'Hide plans',
        getStartedWith: 'Get Started with the {label} Plan',
        whatsIncluded: "What's included",
        features: {
          renovation: {
            title: 'Renovation Tool',
            description:
              'Plan renovations with cost estimates, ROI projections, and area-specific guidance.',
          },
          neighbourhood: {
            title: 'Neighbourhood Intelligence',
            description:
              'Full scores on transport, schools, amenities and expat density. Plus the official Property Price Index (CBC data), sold history and investment yield estimates per district.',
          },
          alerts: {
            title: 'Priority Listing Alerts',
            description:
              'Get notified the moment a matching property hits the market. Never miss a deal again.',
          },
          inbox: {
            title: 'Priority Seller Inbox',
            description:
              "Your message appears at the top of every seller's inbox. Pro members get seen first, responded to faster, and stand out in competitive situations.",
          },
          reports: {
            title: 'Listing Detail Reports',
            description:
              'Generate a full PDF report for any property listing — price history, neighbourhood scores, investment yield estimate, and key details in one shareable document.',
          },
        },
      },
      sponsored: {
        label: 'SPONSORED',
        title: 'Your ad could be here',
        description: 'Reach thousands of property buyers and renters across Cyprus every day.',
        cta: 'Advertise with us',
      },
      recent: {
        eyebrow: 'Recent Listings',
        subtitle: 'The latest properties added to CosmoEstates — fresh off the market.',
      },
      recentlyViewed: {
        eyebrow: 'Similar to what you viewed',
        subtitle: 'Picked from listings that match the properties you recently browsed.',
      },
      adSponsored: 'Sponsored',
      adTitle: 'Your ad could be here',
      adSub: 'Reach thousands of property buyers and renters across Cyprus every day.',
      adCta: 'Advertise with us',
    },
    maskedPhone: {
      loginToView: 'Log in to view contact',
    },
    listings: {
      pageTitle: 'All Properties',
      propertiesFound: 'properties found',
      propertiesIn: 'Properties in "{q}"',
      listingType: 'LISTING TYPE',
      propertyType: 'PROPERTY TYPE',
      sortBy: 'SORT BY',
      filtersBtn: 'Filters',
      filters: {
        all: 'All',
        forSale: 'For Sale',
        forRent: 'For Rent',
        house: 'House',
        apartment: 'Apartment',
        villa: 'Villa',
        newestFirst: 'Newest First',
        priceLow: 'Price: Low to High',
        priceHigh: 'Price: High to Low',
      },
      advertisement: 'Advertisement',
      placeAd: 'Place Your Ad Here',
      adSub: 'Reach thousands of property seekers in Cyprus every day',
      advertise: 'Advertise with us',
      noProperties: 'No properties found',
      adjustFilters: 'Try adjusting your filters or search in a different area.',
      viewProperty: 'View property',
    },
    property: {
      back: 'Back',
      save: 'Save',
      saved: 'Saved',
      askingPrice: 'ASKING PRICE',
      defaultMessage: "Hi, I'm interested in this property. Could we arrange a viewing?",
      sendMessage: 'Send Message',
      bedrooms: 'BEDROOMS',
      bathrooms: 'BATHROOMS',
      floorArea: 'FLOOR AREA',
      type: 'TYPE',
      featuresAndAmenities: 'Features and Amenities',
      similarProperties: 'Similar Properties',
    },
    footer: {
      tagline: 'Find the place you can call home.',
      findYourHome: 'FIND YOUR HOME',
      services: 'SERVICES',
      company: 'COMPANY',
      housesForSale: 'Houses for Sale',
      apartments: 'Apartments',
      villas: 'Villas',
      forRent: 'For Rent',
      commercial: 'Commercial',
      propertyValuation: 'Property Valuation',
      renovationSimulator: 'Renovation Tool',
      neighbourhoodIntel: 'Neighbourhood Intelligence',
      findABroker: 'Find an Agent',
      listAProperty: 'List a Property',
      whoWeAre: 'Who We Are',
      contactUs: 'Contact Us',
      privacyPolicy: 'Privacy Policy',
      termsOfUse: 'Terms of Service',
      cookiesPolicy: 'Cookies Policy',
      joinAsAgent: 'Join as an Agent',
      faq: 'FAQ',
      legal: 'Legal',
      terms: 'Terms',
      privacy: 'Privacy',
      cookies: 'Cookies',
      disclaimer: 'Disclaimer',
      acceptableUse: 'Acceptable Use',
      billingRefunds: 'Billing & Refunds',
      manageCookies: 'Manage cookies',
      copyright: '© 2026 CosmoEstates. All rights reserved. — Cyprus Property Portal',
    },
    common: {
      loading: 'Loading…',
      error: 'Something went wrong',
      retry: 'Try again',
      cancel: 'Cancel',
      save: 'Save',
      close: 'Close',
      next: 'Next',
      back: 'Back',
    },
    cta: {
      title: 'Are you an agent or property owner?',
      sub: 'List your property for free and reach over 2.4 million active buyers and renters every month. Get your listing live in minutes.',
      freeListings: 'Free listings',
      verifiedBadge: 'Verified badge',
      analytics: 'Analytics dashboard',
      directEnquiries: 'Direct enquiries',
      postListing: 'Post a Free Listing',
      learnMore: 'Learn More',
    },
    categories: {
      title: 'Browse by Category',
      sub: 'Explore thousands of listings across every property type',
      viewAll: 'View all categories →',
      listingsSuffix: 'listings',
      names: {
        houses: 'Houses',
        apartments: 'Apartments',
        villas: 'Villas',
        commercial: 'Commercial',
        newBuilds: 'New Builds',
        landPlots: 'Land & Plots',
        holiday: 'Holiday',
        offices: 'Offices',
        parking: 'Parking',
        foreclosure: 'Foreclosure',
      },
    },
    partners: {
      discover: 'Discover our Partners',
      viewAll: 'View All Partners',
      eyebrow: 'Trusted Partners',
      title: "Working with Cyprus's best",
      subtitle: 'Verified agencies across the island, hand-picked for quality and service.',
      discoverAll: 'Discover all partners →',
    },
    allPartners: {
      badge: 'Our Partners',
      title: 'Meet the Partners of CosmoEstates',
      subtitle: 'Browse the agencies and professionals working with CosmoEstates to connect buyers, renters, and sellers across Cyprus.',
      loading: 'Loading partners…',
      viewListings: 'View Listings',
      fairnessNote: 'Partner order is randomised on every page load to ensure all agencies receive equal visibility.',
      noPartnersFound: 'No partners found',
    },
    card: {
      beds: 'beds',
      baths: 'baths',
      bedrooms: 'bedrooms',
      bathrooms: 'bathrooms',
      forSale: 'For Sale',
      forRent: 'For Rent',
      view: 'View →',
      viewDetails: 'View Details →',
      perMonth: '/mo',
      boosted: 'Boosted',
      featured: 'Featured',
      soldBadge: 'SOLD',
      partnerBadge: 'Partner',
      prevPhoto: 'Previous photo',
      nextPhoto: 'Next photo',
      savePropertyAria: 'Save property',
    },
    progate: {
      badge: 'CosmoEstates Pro',
      signInRequired: 'Sign in required',
      proRequired: 'Pro access required',
      signInMsg: 'Sign in and get premium to access {feature}.',
      proMsg: '{feature} is a Pro-only feature. Upgrade to CosmoEstates Pro to unlock it.',
      signIn: 'Sign In',
      createAccount: 'Create Account',
      viewPlans: 'View Pro plans',
      noteAuth: 'Upgrade to Pro after sign in',
      notePro: 'Cancel anytime · Secure payment via Stripe',
      features: {
        renovation: 'Renovation Tool',
        neighbourhood: 'Neighbourhood Intelligence',
        alerts: 'Priority Listing Alerts',
        inbox: 'Priority Seller Inbox',
        reports: 'Listing Detail Reports',
      },
    },
    login: {
      welcomeBack: 'Welcome back',
      sub: 'Sign in to access your watchlist and notifications.',
      emailAddress: 'Email Address',
      emailRequired: 'Email is required',
      validEmail: 'Enter a valid email',
      password: 'Password',
      passwordRequired: 'Password is required',
      forgotPassword: 'Forgot password?',
      signIn: 'Sign In',
      noAccount: "Don't have an account?",
      registerNow: 'Register now',
      continueWithGoogle: 'Continue with Google',
      orSignInEmail: 'or sign in with email',
      heroTitleA: 'Start your Cyprus',
      heroTitleB: 'property journey',
      heroTitleC: 'here',
      heroSubtitle: 'Discover homes, connect with agents, and find your place under the Cyprus sun.',
    },
    register: {
      createAccount: 'Create your account',
      startTrialTitle: 'Start your free trial',
      sub: 'Join thousands of smart property seekers in Cyprus.',
      trialSub: '5 days of full Pro access. No credit card required.',
      trialBanner: "You're signing up for a free 5-day Pro trial",
      trialDesc: 'Get instant access to all Pro features — renovation reports, neighbourhood intelligence, priority alerts, and more. No card required.',
      firstName: 'First Name',
      lastName: 'Last Name',
      emailAddress: 'Email Address',
      phoneNumber: 'Phone Number',
      phoneOptional: '(optional)',
      password: 'Password',
      passwordPlaceholder: 'At least 6 characters',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      validEmail: 'Enter a valid email',
      passwordMin: 'Password must be at least 6 characters',
      startFreeTrial: 'Start Free Trial',
      createAccountBtn: 'Create Account',
      trialNote: "Trial lasts 5 days from signup. After it expires, you'll revert to the free plan automatically. Upgrade to Pro any time to keep full access.",
      alreadyHaveAccount: 'Already have an account?',
      signIn: 'Sign in',
      continueWithGoogle: 'Continue with Google',
      orSignUpEmail: 'or sign up with email',
      heroTitleA: 'Find your home',
      heroTitleB: 'under the Cyprus',
      heroTitleC: 'sun',
      heroSubtitle: 'Join thousands exploring properties across Limassol, Paphos, Nicosia and beyond.',
    },
    forgot: {
      title: 'Reset your password',
      sub: "Enter your email and we'll send you a reset link.",
      emailAddress: 'Email Address',
      sendLink: 'Send Reset Link',
      rememberIt: 'Remember it?',
      signIn: 'Sign in',
      checkEmail: 'Check your email',
      sentMsg: 'We sent a reset link to {email}. Click it to set a new password.',
      notReceived: "Didn't receive it? Check spam or",
      tryAgain: 'try again',
      validEmail: 'Enter a valid email address',
    },
    profile: {
      title: 'My Profile',
      sub: 'Manage your personal information',
      personalDetails: 'Personal Details',
      firstName: 'First Name',
      lastName: 'Last Name',
      emailAddress: 'Email Address',
      emailCannotChange: 'Email cannot be changed here',
      phoneNumber: 'Phone Number',
      changesSaved: 'Changes saved',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      proMembership: 'Pro Membership',
      proActive: 'Pro Active',
      freePlan: 'Free Plan',
      proActiveMsg: 'You have full access to all CosmoEstates Pro features.',
      freeMsg: 'Upgrade to CosmoEstates Pro to unlock renovation reports, neighbourhood intelligence, priority alerts and more.',
      upgradePro: 'Upgrade to Pro',
      manageSubscription: 'Manage subscription',
      portalLoading: 'Opening…',
      portalNotSignedIn: 'Please sign in again to manage your subscription.',
      portalNoSubscription: 'No subscription is linked to your account. Email support@cosmoestates.cy and we will resolve it.',
      portalFailed: 'Could not open subscription management. Please try again or contact support.',
      portalMissingBefore: "We can't find your subscription details in our system. To cancel or manage your Pro subscription, please email ",
      portalMissingAfter: " and we'll handle it within 24 hours.",
      failedSave: 'Failed to save changes. Please try again.',
    },
    checkout: {
      back: 'Back',
      choosePlan: 'Choose your plan',
      unlockPro: 'Unlock the full CosmoEstates Pro experience',
      emailAddress: 'Email address',
      everythingIncluded: 'Everything included:',
      paySecure: 'Pay €{amount} — Secure Checkout',
      ssl: '256-bit SSL',
      pci: 'PCI DSS',
      poweredByStripe: 'Powered by Stripe',
      terms: "You'll be redirected to Stripe's secure payment page. By completing your purchase you agree to our Terms of Service. Cancel anytime from your profile.",
      termsLink: 'Terms of Service',
      orderSummary: 'Order Summary',
      productName: 'CosmoEstates Pro',
      priceVat: 'Price (VAT incl.)',
      total: 'Total',
      youSave: 'You save {discount}',
      moreFeatures: '+ {n} more features',
      planSuffix: 'plan',
      freeTrial: 'Free Trial',
      trialDays: '5 days completely free',
      trialSub: 'Full Pro access. No credit card. Cancel anytime.',
      yourName: 'Your name',
      startTrialBtn: 'Start My 5-Day Free Trial',
      noPayment: "No payment info needed. After 5 days you'll be moved to the free plan unless you upgrade.",
      whatYouGetFree: 'What you get free',
      daysProAccess: '5 Days Pro Access',
      noCreditCard: 'No credit card required.',
      viewPaidPlans: 'View paid plans instead',
      trialLive: 'Your trial is live!',
      trialMsg: 'You have 5 days of free Pro access. No credit card required and explore everything CosmoEstates Pro has to offer.',
      account: 'Account',
      trialEnds: 'Trial ends',
      cardNeeded: 'Card needed?',
      no: 'No',
      trialNote: "We'll send a reminder before your trial ends. You can upgrade or cancel anytime from your profile.",
      startExploring: 'Start Exploring',
      validEmail: 'Valid email required',
      nameRequired: 'Name required',
      features: {
        priorityInbox: 'Priority Inbox',
        neighbourhoodIntel: 'Neighbourhood Intelligence',
        smartAlerts: 'Smart Alerts',
        priceDropAlerts: 'Price Drop Alerts',
        pdfReports: 'PDF Reports',
      },
      mostPopular: 'Most Popular',
      plans: {
        monthly: { label: 'Monthly', note: 'Billed monthly' },
        quarterly: { label: 'Quarterly', note: 'EUR 10.19 / month', badge: 'Most Popular', discount: 'Save 21%' },
        yearly: { label: 'Yearly', note: 'EUR 9.54 / month', discount: 'Save 26%' },
      },
    },
    settings: {
      title: 'Settings',
      sub: 'Manage your account preferences',
      notifications: 'Notifications',
      notif: {
        newListings: 'New matching listings',
        newListingsDesc: 'Get alerted when a property matching your search goes live',
        priceDrops: 'Price drops',
        priceDropsDesc: 'Be notified when a saved property drops in price',
        priceDropsLocked: 'Upgrade to Pro to enable price drop alerts',
        messages: 'Messages',
        messagesDesc: 'Receive notifications for new messages from sellers',
        newsletter: 'Weekly newsletter',
        newsletterDesc: 'Market updates and featured properties every week',
      },
      security: 'Security',
      dangerZone: 'Delete account',
      dangerDesc: 'Permanently delete your account and all associated data including your listings, watchlist and notifications. This cannot be undone.',
      confirmDelete: 'Are you absolutely sure? Type DELETE to confirm:',
      deletePlaceholder: 'Type DELETE',
      deleting: 'Deleting...',
      confirmDeleteBtn: 'Yes, Delete My Account',
      deleteBtn: 'Delete Account',
      cancel: 'Cancel',
      settingsSaved: 'Settings saved',
      saveSettings: 'Save Settings',
      saving: 'Saving…',
      password: {
        signedInAs: 'Signed in as {email}. For security, we\'ll email you a verification code to confirm this change.',
        updated: 'Password updated successfully.',
        newPassword: 'New Password',
        confirmPassword: 'Confirm New Password',
        placeholder: 'At least 6 characters',
        confirmPlaceholder: 'Repeat your new password',
        updating: 'Updating...',
        updateBtn: 'Update Password',
        minLength: 'Password must be at least 6 characters',
        noMatch: 'Passwords do not match',
        sessionExpired: 'Session expired — log out and back in, then try again.',
        error: 'Something went wrong. Please try again.',
        sendCode: 'Send verification code',
        sending: 'Sending code...',
        codeSent: 'We sent a verification code to {email}. Enter it below with your new password.',
        code: 'Verification code',
        codePlaceholder: 'Verification code',
        codeRequired: 'Enter the verification code we sent you.',
        invalidCode: 'Invalid or expired code. Please request a new one.',
        resendCode: 'Send a new code',
        reauthFailed: 'Couldn\'t send verification code. Please try again.',
        cancelChange: 'Cancel',
        resetTitle: 'Reset your password',
        resetIntro: 'We\'ll email an 8-digit verification code to {email} to confirm it\'s you. You\'ll then enter your current password and choose a new one.',
        getCodeBtn: 'Get verification code',
        currentPassword: 'Current Password',
        currentPasswordPlaceholder: 'Enter your current password',
        currentPasswordRequired: 'Enter your current password.',
        wrongCurrentPassword: 'Current password is incorrect.',
        mustBeDifferent: 'New password must be different from your current password.',
        tooWeak: 'Password is too weak. Use a longer, harder-to-guess password.',
      },
    },
    saved: {
      title: 'Saved Properties',
      propertySaved: '{n} property saved',
      propertiesSaved: '{n} properties saved',
      sortBy: 'Sort by',
      savedOrder: 'Saved Order',
      advertisement: 'Advertisement',
      placeAd: 'Place Your Ad Here',
      adSub: 'Reach thousands of property seekers in Cyprus every day',
      getInTouch: 'Get in touch →',
      noSaved: 'No saved properties yet',
      heartProperty: 'Heart a property to save it here.',
      browseListings: 'Browse Listings',
    },
    agentDashboard: {
      loading: 'Loading your dashboard…',
      pageTitle: 'My Listings',
      pageSubViews: '{views} total views · {active} active',
      tabs: {
        listings: 'Listings',
        agentProfile: 'Agent Profile',
      },
      markSold: {
        title: 'Mark as Sold?',
        message: 'The listing will show a \'SOLD\' banner for 4 hours, then automatically archive.',
        confirm: 'Yes, mark as sold',
      },
      restore: {
        title: 'Restore listing?',
        message: 'The listing will be restored to active and visible in public search results again.',
        confirm: 'Yes, restore',
      },
      delete: {
        title: 'Delete this listing?',
        message: 'This will permanently remove the listing. This action cannot be undone.',
        confirm: 'Yes, delete',
      },
    },
    valuation: {
      title: 'Free Property Price Estimate',
      eyebrow: 'FREE PROPERTY ESTIMATE',
      subtitle: 'A free ballpark price estimate for any property in Cyprus. A starting point, not a formal valuation.',
      trust1: '100% Free',
      trust2: 'Market-Based',
      trust3: 'Instant Results',
      trust4: 'No Sign-up Needed',
      back: 'Back',
      backHome: 'Back to Home',
      continue: 'Continue',
      sending: 'Sending...',
      getValuation: 'Get My Estimate',
      cityAvg: 'city avg',
      steps: ['Type', 'Location', 'Specs', 'Condition', 'Email'],
      step1Title: 'What type of property is it?',
      step2Title: 'Where is the property located?',
      step3Title: 'Size and specifications',
      step4Title: 'What is the current condition?',
      step5Title: 'Where should we send your report?',
      step5Sub: "We'll send a price range for your property, the going rate per m² in your area, and an indicative monthly rent range — a starting point, not a formal valuation.",
      gdpr: 'By submitting you agree to receive your valuation by email. We respect your privacy.',
      consent: {
        label: 'I allow this information to be forwarded to real estate agents',
        detail: 'Vetted Cyprus agents who specialize in your area will contact you within 48 hours. You can change this anytime in your profile settings.',
      },
      cityLabel: 'City',
      selectCity: 'Select city...',
      areaLabel: 'Area',
      optional: '(optional)',
      usingCityAvg: 'Using {city} average',
      plotSize: 'Plot Size (m²)',
      coveredArea: 'Covered Area (m²)',
      floorLabel: 'Floor (optional)',
      bedroomsLabel: 'Bedrooms',
      bathroomsLabel: 'Bathrooms',
      featuresLabel: 'Property Features',
      firstNameLabel: 'First Name',
      emailLabel: 'Email Address',
      resultTitle: 'Your Estimate is Ready',
      resultBasedOn: 'Based on',
      salePriceEstimate: 'Sale Price Estimate',
      monthlyRentEstimate: 'Monthly Rent Estimate',
      perMonth: 'per month',
      summaryProperty: 'Property',
      summaryCity: 'City',
      summaryPlotSize: 'Plot Size',
      summaryCoveredArea: 'Covered Area',
      summaryBedrooms: 'Bedrooms',
      summaryCondition: 'Condition',
      summaryFeatures: 'Features',
      reportSent: 'An indicative estimate has been sent to',
      propertyTypes: {
        Apartment: 'Apartment',
        House: 'House',
        Villa: 'Villa',
        Commercial: 'Commercial',
        Land: 'Land',
      },
      conditions: {
        brandNew: 'Brand New',
        brandNewDesc: 'Newly built or never lived in',
        excellent: 'Excellent',
        excellentDesc: 'Recently renovated, no work needed',
        good: 'Good',
        goodDesc: 'Well maintained, minor updates',
        needsRenovation: 'Needs Renovation',
        needsRenovationDesc: 'Requires significant work',
      },
      features: {
        seaView: 'Sea View',
        privatePool: 'Private Pool',
        coveredParking: 'Covered Parking',
        communalPool: 'Communal Pool',
        balconyTerrace: 'Balcony / Terrace',
        airConditioning: 'Air Conditioning',
        solarPanels: 'Solar Panels',
        fireplace: 'Fireplace',
      },
      heroEyebrow: 'CYPRUS PROPERTY TOOL',
      heroTitle: 'Property Price Estimate',
      heroSubtitle: "Get a free orientation on your property's likely price range. Our estimate is data-informed but is not a formal appraisal. For a certified valuation, ",
      requestPro: 'contact a licensed appraiser',
      reasonLabel: "What's your reason for getting an estimate?",
      reasonOptional: 'optional',
      reasons: {
        just_exploring: 'Just exploring',
        thinking_about: 'Thinking about selling',
        within_6_months: 'Within 6 months',
        within_1_year: 'Sell within 1 year',
      },
      cities: {
        Limassol: 'Limassol',
        Paphos: 'Paphos',
        Nicosia: 'Nicosia',
        Larnaca: 'Larnaca',
        'Ayia Napa': 'Ayia Napa',
        Protaras: 'Protaras',
        Paralimni: 'Paralimni',
      },
      areaPlaceholder: 'e.g. Germasogeia, Mackenzie...',
      plotPlaceholder: 'e.g. 500',
      coveredPlaceholder: 'e.g. 120',
      landPlotPlaceholder: 'e.g. 1000',
      firstNamePlaceholder: 'e.g. Nikolas',
      emailPlaceholder: 'you@example.com',
      disclaimerNote: 'Note:',
      disclaimerText: 'This is an automated estimate based on Cyprus market data and recent comparable transactions. It is',
      disclaimerNotProfessional: 'not a professional appraisal',
      disclaimerTextCont: 'and should not be used for legal, financial, or transaction purposes. For a formal valuation, consult a licensed RICS surveyor.',
      privacyPrefix: "I agree to CosmoEstates'",
      privacyPolicy: 'Privacy Policy',
      privacyAnd: 'and',
      termsOfService: 'Terms of Service',
      privacySuffix: ', and consent to my data being processed to generate this estimate.',
      privacyAlert: 'Please agree to the Privacy Policy and Terms of Service to continue.',
      successTitle: 'Your estimate is on the way',
      successMsg: "We're preparing your indicative property estimate and will email it to",
      successMsgSuffix: 'within the next few minutes.',
      successHint: "Check your inbox (and spam folder). You'll get a sale price range, an indicative monthly rent range, and the going rate per m² in your area. Treat it as a starting point — ",
      successBackHome: 'Back to home →',
      // Stage 2 additions — new form fields
      selectOption: 'Select…',
      dontKnow: "I don't know",
      improvesAccuracy: 'improves accuracy',
      yearBuiltLabel: 'Year built',
      yearBuilt: {
        opt2020newer:  '2020 or newer',
        opt2010_2019:  '2010–2019',
        opt2000_2009:  '2000–2009',
        opt1990_1999:  '1990–1999',
        optBefore1990: 'Before 1990',
        optUnknown:    "I don't know",
      },
      energyRatingLabel: 'Energy rating',
      titleDeedLabel: 'Title deed',
      titleDeedTooltip: 'Title deed status significantly affects Cyprus property values.',
      titleDeed: {
        optAvailable: 'Available',
        optPending:   'Pending',
        optNone:      'None',
      },
      parkingLabel: 'Parking',
      parking: {
        optCovered: 'Covered / Private',
        optOpen:    'Open / Assigned',
        optStreet:  'Street only',
        optNone:    'None',
      },
      furnishedLabel: 'Furnished',
      furnished: {
        optFully:       'Fully furnished',
        optPartially:   'Partially furnished',
        optUnfurnished: 'Unfurnished',
      },
      distanceToSeaLabel: 'Distance to sea',
      seaDistance: {
        optSeafront: 'Seafront (0–100m)',
        optWalking:  'Walking distance (100–500m)',
        optDrive:    'Short drive (500m–2km)',
        optInland:   'Inland (2km+)',
      },
      // New Step 5 (optional) — sits between condition and contact info.
      step5TitleV2: 'Details that improve your estimate',
      step5SubV2: 'Skip if unsure — but adding these improves your estimate accuracy.',
      step5Empty: 'No additional details apply to this property. Continue to send the estimate.',
      renovationYearLabel: 'Last renovation',
      renovation: {
        opt2020plus:   '2020+',
        opt2015_2019:  '2015–2019',
        opt2010_2014:  '2010–2014',
        optOriginal:   'Original / never renovated',
        optNotSure:    'Not sure',
      },
      orientationLabel: 'Main orientation',
      orientation: {
        optSouth:   'South',
        optNorth:   'North',
        optEast:    'East',
        optWest:    'West',
        optCorner:  'Corner (multiple)',
        optNotSure: 'Not sure',
      },
      buildingTypeLabel: 'Building type',
      buildingType: {
        optLow:      'Low-rise (1–3 floors)',
        optMid:      'Mid-rise (4–8 floors)',
        optHigh:     'High-rise (9+)',
        optBoutique: 'Boutique building (<10 units)',
      },
      confidence: {
        high:         'High confidence',
        medium:       'Medium confidence',
        low:          'Low confidence',
        estimateOnly: 'Estimate only',
        subHigh:      'Based on a strong set of comparables and detailed inputs.',
        subMedium:    'Based on solid market data. For more accuracy, provide the optional details in Step 5.',
        subLow:       'Limited comparables in this area — treat as directional.',
        subEstimate:  "Not enough data in this area for a data-backed estimate.",
      },
      result: {
        eyebrow:            'YOUR ESTIMATE',
        title:              'Property price estimate',
        saleRange:          'Indicative sale range',
        rentRange:          'Indicative monthly rent',
        perMonth:           ' /mo',
        pricePerSqm:        'Going rate per m²',
        howCalculated:      'How we calculated this estimate',
        baseLabel:          'Base per m² ({source})',
        basedOnN:           'Based on {n} recent listings',
        totalAdjustment:    'Total adjustment',
        cappedNote:         'Total adjustment was capped from {raw} to stay within safe bounds.',
        comparablesTitle:   'Similar listings we used as reference',
        comparablesLoading: 'Loading similar listings…',
        comparablesEmpty:   "We don't have enough listings in your area to show comparables yet.",
        findAgentCTA:       'Talk to a certified agent',
        newEstimate:        'Start a new estimate',
        legal:              "This estimate is data-informed but not a formal appraisal. For a certified valuation, contact a licensed appraiser.",
      },
      errors: {
        propertyType:  'Please select a property type',
        city:          'City is required',
        plotSize:      'Plot size is required',
        coveredArea:   'Covered area is required',
        condition:     'Please select a condition',
        firstName:     'First name is required',
        email:         'Email is required',
        emailInvalid:  'Enter a valid email',
        yearBuilt:     'Please select when the property was built',
        titleDeed:     'Please select the title deed status',
        parkingType:   'Please select the parking type',
        furnished:     'Please select the furnishing status',
        distanceToSea: 'Please select the distance to the sea',
      },
    },
    findAgent: {
      stepIndicator: 'Step {step} of {total}',
      continue: 'Continue →',
      back: '← Back',
      finding: 'Finding your agents...',
      findMyAgents: 'Find My Agents',
      errorGeneric: 'Something went wrong. Please try again.',
      step1Title: 'What are you looking for?',
      step1Sub: "Let's find the right agent for your situation.",
      lookingFor: {
        sell: 'Sell my property',
        sellSub: 'List and find the right buyer',
        buy: 'Buy a property',
        buySub: 'Find your next home',
        rent: 'Rent a property',
        rentSub: 'Find a place to live',
        rent_out: 'Rent out my property',
        rent_outSub: 'Find good tenants',
      },
      step2Title: 'Tell us about the property',
      step2Sub: 'Just the basics — your matched agents will refine the details.',
      propertyTypeLabel: 'Property type',
      propertyTypes: {
        apartment: 'Apartment',
        house: 'House',
        villa: 'Villa',
        land: 'Land',
        commercial: 'Commercial',
      },
      cityLabel: 'City',
      selectCity: 'Select a city',
      districtLabel: 'District',
      districtPlaceholder: 'e.g., Germasogeia',
      optional: '(optional)',
      bedroomsLabel: 'Bedrooms',
      monthlyBudget: 'Monthly budget (EUR)',
      budgetRange: 'Budget range (EUR)',
      fromPlaceholder: 'From',
      toPlaceholder: 'To',
      step3TitleSell: 'Tell us about your situation',
      step3TitleBuy: 'A bit more about you',
      step3SubSell: 'This helps your matched agents prepare the right approach.',
      step3SubBuy: 'This helps us pick the agents best suited to you.',
      situationLabel: 'Which best describes you?',
      timeframeSellLabel: 'When do you want to be on the market?',
      timeframeBuyLabel: 'When are you looking to move?',
      situations: {
        sell: {
          specific_timeline: 'I need to sell within a specific timeline',
          exploring: "I'm exploring my options",
          inheritance: "I've inherited the property",
          relocate: 'I want to relocate',
        },
        buy: {
          first_time: "I'm a first-time buyer",
          investor: "I'm investing for rental income",
          relocating_to_cyprus: "I'm relocating to Cyprus",
          golden_visa: 'Buying for residency / golden visa',
        },
        rent: {
          short_term: 'Short-term (under 6 months)',
          long_term: 'Long-term (1 year or more)',
          relocating_to_cyprus: "I'm relocating to Cyprus",
          students_or_family: 'Renting with family or students',
        },
        rent_out: {
          short_term_rental: 'Set up as short-term rental (Airbnb)',
          long_term_rental: 'Find long-term tenants',
          need_management: 'I need full property management',
          just_tenant_matching: 'Just tenant matching, I manage',
        },
      },
      timeframesSell: {
        immediate: 'As soon as possible',
        within_3_months: 'Within 3 months',
        within_6_months: 'Within 6 months',
        within_12_months: 'Within 12 months',
        within_24_months: 'Within 24 months',
        just_exploring: "I'm flexible",
      },
      moveIn: {
        asap: 'As soon as possible',
        within_6_months: 'Within 6 months',
        within_12_months: 'Within 12 months',
        within_24_months: 'Within 24 months',
        flexible: "I'm flexible",
      },
      step4Title: "What's most important to you?",
      step4Sub: 'Pick the one that matters most.',
      priorities: {
        sell: {
          best_price: 'Getting the best price, even if it takes time',
          fast_sale: 'Selling quickly, even if I leave some money on the table',
          full_process: 'Agent takes on the full process',
          language: 'Communicating in my preferred language',
          privacy: 'Keeping the sale private and low-key',
        },
        buy: {
          best_location: 'Finding the perfect location',
          best_value: 'Getting the best value for my money',
          exclusive: 'Access to off-market and exclusive listings',
          language: 'Communicating in my preferred language',
          full_process: 'Agent handles paperwork and legal coordination',
        },
        rent: {
          best_location: 'Finding the perfect location',
          best_value: 'Getting the best value for my budget',
          fast_response: 'Fast responses and quick viewings',
          language: 'Communicating in my preferred language',
          furnished_ready: 'Furnished and move-in ready',
        },
        rent_out: {
          vetted_tenants: 'Vetted, reliable tenants',
          max_yield: 'Maximizing rental yield',
          full_management: 'Hands-off — full property management',
          language: 'Communicating in my preferred language',
          fast_fill: 'Filling vacancy as fast as possible',
        },
      },
      step5Title: 'How can agents reach you?',
      step5Sub: '3 matched agents will contact you within 24-48 hours.',
      fullNameLabel: 'Full name',
      fullNamePlaceholder: 'Your name',
      emailLabel: 'Email',
      emailPlaceholder: 'you@email.com',
      phoneLabel: 'Phone',
      phonePlaceholder: '+357 99 123456',
      contactMethodLabel: 'Contact method',
      contactMethods: {
        email: 'Email',
        call: 'Call',
        whatsapp: 'WhatsApp',
      },
      consentLabel: 'I allow my contact details to be shared with up to 3 vetted real estate agents who match my requirements',
      success: {
        headline: "You're all set!",
        sublinePrefix: "We've matched you with",
        sublineMid: 'top-rated agents in',
        sublineSuffix: "They'll contact you within 24-48 hours with their proposed approach.",
        yourArea: 'your area',
        step1Title: 'Agents review your details',
        step1Text: 'Each matched agent prepares their personalized proposal.',
        step2Title: 'You receive their offers',
        step2Text: 'Within 24-48 hours via your preferred contact method.',
        step3Title: 'Compare and choose',
        step3Text: "Pick the agent who feels right — or none if you'd rather wait.",
        matchedHeading: 'Your matched agents',
        loading: 'Finding the best agents for you...',
        emptyPrefix: "We're personally matching you with agents in",
        emptySuffix: "You'll hear from us within 48 hours.",
        backHome: 'Back to homepage',
      },
    },
    featured: {
      boostedListings: 'Featured Listings',
      viewAll: 'View all',
      viewAllListings: 'View all listings',
    },
    authModal: {
      loginTitle: 'Welcome back',
      registerTitle: 'Create your account',
      loginSub: 'Sign in to access your watchlist and notifications.',
      registerSub: 'Join CosmoEstates to save properties and get alerts.',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      phone: 'Phone Number',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      repeatPassword: 'Repeat your password',
      forgotPassword: 'Forgot password?',
      signIn: 'Sign In',
      createAccount: 'Create Account',
      noAccount: "Don't have an account?",
      registerNow: 'Register now',
      haveAccount: 'Already have an account?',
      signInLink: 'Sign in',
      atLeast6: 'At least 6 characters',
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      phoneRequired: 'Phone number is required',
      passwordsNoMatch: 'Passwords do not match',
      passwordMin: 'Password must be at least 6 characters',
      emailInvalid: 'Enter a valid email address',
      passwordRequired: 'Password is required',
    },
    statsBar: {
      activeListings: 'Active Listings',
      propertyTypes: 'Property Types',
      citiesCovered: 'Cities Covered',
      registeredUsers: 'Registered Users',
    },
    boostSuccess: {
      activating: 'Activating your boost…',
      activatingSub: 'Please wait while we confirm your payment.',
      success: 'Your listing is boosted!',
      successSub: 'Your listing now gets priority placement in search results and rotates through the homepage featured carousel for 14 days.',
      viewMyListings: 'View My Listings',
      backHome: 'Back to Home',
    },
    partner: {
      notFound: 'Partner not found',
      back: 'Back',
      backHome: 'Back to Home',
      founded: 'Founded',
      contactUs: 'Contact Us',
      listings: 'Listings',
      citiesServed: 'Cities Served',
      loading: 'Loading...',
      noListings: 'No listings yet.',
    },
    notifications: {
      title: 'Notifications',
      pageTitle: 'Inbox',
      inbox: 'Inbox',
      alerts: 'Alerts',
      loading: 'Loading...',
      noMessagesYet: 'No messages yet',
      markAllRead: 'Mark all as read',
      noAlerts: 'No alerts yet.',
      noAlertsSub: 'Price drops, new matches and account activity will appear here.',
      selectConvo: 'Select a conversation',
      selectConvoSub: 'Choose a message thread from the left to read and reply',
      preview: 'Preview',
      demoPlaceholder: 'This is a demo conversation…',
      typeMessage: 'Type a message…',
      back: 'Back',
      loadingConvos: 'Loading conversations…',
      noConvos: 'No conversations yet',
      noConvosSub: 'Contact an agent on any listing to start a conversation',
      browseListings: 'Browse listings',
      sayHello: 'No messages yet. Say hello!',
      justNow: 'Just now',
    },
    property: {
      notFound: 'Property not found',
      backToListings: 'Back to Listings',
      noPhoto: 'No photo uploaded yet for this room',
      photoLabel: 'Photo',
      back: 'Back',
      saved: 'Saved',
      save: 'Save',
      report: 'Report',
      perMonth: 'Per Month',
      perMonthShort: '/month',
      askingPrice: 'Asking Price',
      messageSent: 'Message sent! Redirecting to your inbox...',
      defaultMessage: "Hi, I'm interested in this property. Could we arrange a viewing?",
      sending: 'Sending...',
      messageLandlord: 'Message Landlord',
      sendMessage: 'Send Message',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      floorArea: 'Floor Area',
      type: 'Type',
      description: 'Description',
      features: 'Features and Amenities',
      similarProperties: 'Similar Properties',
      rooms: {
        livingRoom: 'Living Room',
        masterBedroom: 'Master Bedroom',
        kitchen: 'Kitchen',
        bathroom: 'Bathroom',
        gardenTerrace: 'Garden / Terrace',
        exterior: 'Exterior',
      },
    },
    inbox: {
      title: 'Inbox',
      sub: 'Messages from buyers and agents about your listings.',
      loading: 'Loading messages…',
      empty: 'No messages yet',
      emptySub: 'When buyers or agents reach out about a listing, the conversation lands here.',
      browseListings: 'Browse listings',
      unreadOne: '1 unread',
      unreadMany: '{n} unread',
    },
    myListings: {
      title: 'My Listings',
      sub: 'Properties you have listed on CosmoEstates',
      loading: 'Loading your listings…',
      noListings: 'No listings yet',
      noListingsSub: "You haven't posted any properties. Get started and reach thousands of buyers.",
      postListing: '+ Post a Listing',
      addListing: '+ Add Listing',
      boostListings: 'Boost Listings',
      boost: 'Boost',
      boosted: 'Boosted',
      daysLeft: '{n} days left',
      edit: 'Edit',
      view: 'View',
      delete: 'Delete',
      markSold: 'Mark Sold',
      restore: 'Restore',
      filterActive: 'Active',
      filterSold: 'Sold',
      filterArchived: 'Archived',
      filterAll: 'All',
      filterEmpty: 'No listings in this view.',
      statusSold: 'SOLD',
      statusArchived: 'ARCHIVED',
      markSoldTitle: 'Mark as Sold?',
      markSoldMessage: 'The listing will show as SOLD for 4 hours and then auto-archive. You can restore it any time from the Archived filter.',
      markSoldConfirm: 'Yes, mark as sold',
      restoreTitle: 'Restore listing?',
      restoreMessage: 'The listing will return to Active and become visible to the public again.',
      restoreConfirm: 'Yes, restore',
      untitled: 'Untitled',
      listingCount: '{n} listing',
      listingsCount: '{n} listings',
      tipsTitle: 'Listing Tips',
      tips: [
        { title: 'Add professional photos', desc: 'Listings with high quality photos get more enquiries.' },
        { title: 'Write a detailed description', desc: 'Highlight unique features, nearby amenities and condition.' },
        { title: 'Price it right', desc: 'Use our free valuation tool to find the right price.' },
      ],
      deleteConfirm: 'Are you sure you want to delete this listing? This cannot be undone.',
      boostModal: {
        title: 'Boost Your Listings',
        sub: 'Get priority placement in search results and rotate through the featured carousel for 14 days.',
        choosePackage: 'Choose Package',
        thirtyDays: '14 days · up to {n} listing',
        thirtyDaysPlural: '14 days · up to {n} listings',
        selectListings: 'Select Listings ({selected}/{max})',
        active: '✓ Active',
        selected: '✓ Selected',
        select: 'Select',
        continue: 'Continue to Checkout →',
        selectOne: 'Please select at least one listing.',
      },
    },
    boostSelect: {
      title: 'Boost Listings',
      sub: 'Pick exactly 1 listing to boost for 14 days.',
      loading: 'Loading your listings…',
      noListings: 'No listings to boost',
      noListingsSub: 'Post a listing first, then come back to boost it.',
      noListingsCta: '+ Post a Listing',
      signInTitle: 'Sign in to boost listings',
      signInSub: 'You need an account to boost listings on CosmoEstates.',
      signIn: 'Sign In',
      maxNotice: 'You can boost 1 listing at a time.',
      alreadyBoosted: 'Already boosted',
      selectedCount: '{n} selected',
      perListing: '€10.99 per listing · 14 days',
      total: 'Total',
      boostSelected: 'Boost selected →',
      redirecting: 'Redirecting to checkout…',
      back: 'Back to My Listings',
    },
    reset: {
      invalidLink: 'This reset link is invalid or has expired.',
      minLength: 'Password must be at least 6 characters',
      noMatch: 'Passwords do not match',
      success: 'Password updated!',
      successSub: 'Redirecting you to login...',
      title: 'Set new password',
      sub: 'Choose a strong password for your account.',
      newPassword: 'New Password',
      confirmPassword: 'Confirm New Password',
      placeholder: 'At least 6 characters',
      confirmPlaceholder: 'Repeat your new password',
      submit: 'Update Password',
      requestNew: 'Request a new reset link',
    },
    verifyEmail: {
      noEmailFound: 'No email found. Please sign up again.',
    },
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: April 2026',
      comingSoon: 'Coming soon.',
    },
    terms: {
      title: 'Terms of Service',
      lastUpdated: 'Last updated: April 2026',
      comingSoon: 'Coming soon.',
    },
    contact: {
      tag: 'Get In Touch',
      title: 'Contact Us',
      sub: 'Have a question, found a bug, or want to share an idea? We read every message.',
      categoryLabel: 'What is this about?',
      category: {
        support: 'Support',
        tips: 'Tips & Suggestions',
        bug: 'Bug Report',
        improvement: 'Improvement Idea',
      },
      nameLabel: 'Your Name',
      namePlaceholder: 'e.g. Maria Ioannou',
      emailLabel: 'Your Email',
      emailPlaceholder: 'e.g. maria@example.com',
      messageLabel: 'Message',
      messagePlaceholder: 'Tell us as much as you like…',
      submit: 'Send Message',
      sending: 'Sending…',
      errorMsg: 'Something went wrong. Please try again.',
      successTitle: 'Message sent!',
      successSub: 'Thank you — we\'ll get back to you soon.',
      sendAnother: 'Send another message',
    },
    about: {
      tag: 'Who We Are',
      title: "Cyprus's new",
      titleHighlight: 'property marketplace',
      sub: 'Built for Buyers, Sellers, Agents and Companies.',
      missionLabel: 'Our Mission',
      missionTitle: 'Making Cyprus property accessible to everyone',
      missionText: 'We built CosmoEstates to make the search for a new home in Cyprus easy and flawless. How we ensure it? By integrating tools like AI, intelligent search and more ways to be well informed about the current Housing Markets and help to find a Place that fills your criteria.',
      statsListings: 'Active listings',
      statsUsers: 'Users',
      statsAI: 'Powered insights',
      statsTools: 'Unique Pro Tools',
      valuesTitle: 'What drives us',
      value1Title: 'Transparency',
      value1: 'Real prices, real data, no hidden fees. What you see is what you get.',
      value2Title: 'Intelligence',
      value2: 'AI-powered neighbourhood insights and market data to help you decide with confidence.',
      value3Title: 'Community',
      value3: 'Connecting buyers, sellers and agents across Cyprus in one trusted platform.',
      ctaTitle: 'Ready to find your home in Cyprus?',
      ctaSub: 'Browse hundreds of properties across Cyprus.',
      ctaBtn: 'Browse Listings',
      diffSectionTitle: 'What Makes Us Different',
      diff1Title: 'Tools to make easier decisions',
      diff1Body: 'Renovation cost estimates, neighbourhood scores from public sources, and price comparables — built to give buyers context, not just listings.',
      diff2Title: 'Find verified agents',
      diff2Body: 'Agents are vetted before they go live. Get offers from agents to help you buy, sell, find to rent, or rent out your own property.',
      diff3Title: 'High-quality listings',
      diff3Body: 'Listings are reviewed to avoid spam and low quality listings.',
      diff4Title: 'Built for Cyprus',
      diff4Body: 'Designed specifically for the Cyprus real estate market.',
      coverageSectionTitle: 'What We Cover',
      coverageDistricts: 'Districts covered',
      coverageListings: 'Listings on the platform',
      coverageFounded: 'Founded',
      getStartedTitle: 'Get Started',
      ctaBuyersHeading: 'Buyers',
      ctaBuyersBody: 'Browse Cyprus listings with sourced neighbourhood data.',
      ctaBuyersBtn: 'Browse listings →',
      ctaSellersHeading: 'Sellers',
      ctaSellersBody: 'List your property and reach serious buyers.',
      ctaSellersBtn: 'List a property →',
      ctaAgentsHeading: 'Agents',
      ctaAgentsBody: 'Join the platform as a verified agent. Get matched with qualified buyers.',
      ctaAgentsBtn: 'Apply as an agent →',
    },
    listProperty: {
      title: 'List Your Property',
      sub: 'Fill in the details below to post your listing.',
      editTitle: 'Edit Listing',
      editSub: 'Update your property details below.',
      signInTitle: 'Sign in to list your property',
      signInSub: 'You need an account to post a listing on CosmoEstates.',
      editSignInTitle: 'Sign in to edit listings',
      loadingListing: 'Loading listing...',
      signIn: 'Sign In',
      register: 'Register',
      steps: ['Property', 'Location', 'Details', 'Contact', 'Preview'],
      step1Title: 'Property Details',
      step1Desc: "Start with the basics — what you're listing and what makes it stand out. The clearer you are, the more relevant the buyers we'll send your way.",
      step1TipsLabel: 'What to write',
      step1Tip1: 'Be specific in the title — buyers scan results in seconds',
      step1Tip2: 'Include the city or neighbourhood for instant context',
      step1Tip3: 'Avoid all-caps, emojis, or sales language',
      step2Title: 'Location',
      step2Desc: 'Location is the biggest factor in property search. Buyers filter by city first, then drill into specific neighbourhoods. The more specific, the better matched the leads.',
      step2TipsLabel: 'About the address',
      step2Tip1: 'The full address stays private until a buyer messages you',
      step2Tip2: 'Your listing appears on the Cyprus map based on the city',
      step2Tip3: 'Specific neighbourhoods help buyers find you in filters',
      step3Title: 'Description and Features',
      step3Desc: 'A vivid description and great photos are what make buyers stop scrolling. Lead with the most distinctive feature — the sea view, the renovated kitchen, the rooftop terrace.',
      step3TipsLabel: 'Photos and description',
      step3Tip1: 'Shoot during the day with windows and blinds open',
      step3Tip2: 'The first photo becomes the listing cover image',
      step3Tip3: 'Mention nearby beaches, schools, or transport links',
      step4Title: 'Contact Details',
      step4Desc: 'Buyers reach you through CosmoEstates messaging first, send and receive messages at your own pace.',
      step4TipsLabel: 'How contact works',
      step4Tip1: 'Buyers reach you through CosmoEstates messaging first',
      step4Tip2: 'You can reply at your own pace from your inbox',
      step5Title: 'Preview and Confirm',
      step5Sub: 'Review your listing below. Click any section to edit it.',
      editStep5Sub: 'Review your changes. Click any section to edit.',
      forSale: 'For Sale',
      forRent: 'For Rent',
      propertyType: 'Property Type',
      selectType: 'Select type...',
      listingTitle: 'Listing Title',
      monthlyRent: 'Monthly Rent (EUR)',
      askingPrice: 'Asking Price (EUR)',
      city: 'City',
      regionDistrict: 'Region / District',
      fullAddress: 'Full Address',
      plotSize: 'Plot Size (m²)',
      coveredArea: 'Covered Area (m²)',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      select: 'Select...',
      description: 'Description',
      keyFeatures: 'Key Features',
      commaSeparated: '(comma separated)',
      photos: 'Photos',
      photosUpTo: '(up to 10)',
      addPhotos: 'Add photos',
      addMore: '+ Add more',
      mainPhoto: 'Main',
      yourName: 'Your Name',
      fullName: 'Full name',
      phoneNumber: 'Phone Number',
      emailAddress: 'Email Address',
      agencyName: 'Agency Name',
      optional: '(optional)',
      edit: 'Edit',
      back: 'Back',
      continue: 'Continue',
      submitting: 'Submitting...',
      submitListing: 'Submit Listing',
      saving: 'Saving...',
      saveChanges: 'Save Changes',
      successTitle: 'Listing Submitted!',
      successSub: 'Your property is now live on CosmoEstates.',
      editSuccessTitle: 'Listing Updated!',
      editSuccessSub: 'Your changes have been saved successfully.',
      backToMyListings: 'Back to My Listings',
      viewListing: 'View Listing',
      boostTitle: 'Want more visibility?',
      boostSub: 'Boost your listing to get priority placement in search results and rotate through the featured carousel for 14 days.',
      boostCta: 'Boost This Listing',
      skipForNow: 'Skip for now',
      previewType: 'Type',
      previewTitle: 'Title',
      previewPrice: 'Price',
      previewAddress: 'Address',
      previewPlotSize: 'Plot Size',
      previewCoveredArea: 'Covered Area',
      previewBedsBaths: 'Beds / Baths',
      previewPhotos: 'Photos',
      previewName: 'Name',
      previewPhone: 'Phone',
      previewEmail: 'Email',
      previewAgency: 'Agency',
      previewSectionProperty: 'Property Details',
      previewSectionLocation: 'Location',
      previewSectionDetails: 'Description and Features',
      previewSectionContact: 'Contact',
      photoSingular: 'photo',
      photoPlural: 'photos',
      bed: 'bed',
      bath: 'bath',
      errors: {
        propertyType: 'Select a property type',
        title: 'Title is required',
        titleShort: 'Please enter a proper title (at least {min} characters).',
        titleGibberish: 'Please enter a real title — this looks like placeholder text.',
        price: 'Price is required',
        city: 'City is required',
        address: 'Address is required',
        bedrooms: 'Number of bedrooms is required',
        bathrooms: 'Number of bathrooms is required',
        size: 'Covered area is required',
        plotSize: 'Plot size is required',
        description: 'Description is required',
        descriptionShort: 'Description needs at least {min} characters (currently {have}).',
        descriptionGibberish: 'Please write a real description — this looks like placeholder text.',
        photos: 'Please add at least {min} photos to publish your listing.',
        photosNeed: 'Add {missing} more photo{plural} to publish ({have} of {min}).',
        contactName: 'Name is required',
        contactPhone: 'Phone is required',
        contactEmail: 'Valid email required',
      },
    },
    categoryGrid: {
      title: 'Browse by',
      titleHighlight: 'Category',
      sub: 'Explore every type of property in one place',
      viewAll: 'View all categories →',
      listingsSuffix: 'listings',
    },
    findBroker: {
      eyebrow: 'VERIFIED PROFESSIONALS',
      trustVerified: 'CREAA Verified',
      trustResponse: 'Fast Response',
      trustFree: 'Free to Contact',
      title: 'Find an Agent',
      sub: 'Connect with verified real estate professionals across Cyprus.',
      applyAsBroker: 'Apply as an Agent',
      loading: 'Loading agents...',
      noVerified: 'No verified agents yet',
      noVerifiedSub: 'We are onboarding our first agents. Check back soon.',
      contact: 'Contact',
      modal: {
        eyebrow: 'COSMOESTATES',
        title: 'Apply as an Agent',
        sub: 'Fill in your details and we will review your application.',
        fullName: 'Full Name',
        agencyName: 'Agency Name',
        phone: 'Phone Number',
        email: 'Email Address',
        etek: 'ETEK Number (optional)',
        speciality: 'Speciality (optional)',
        languages: 'Languages',
        city: 'City',
        selectCity: 'Select city...',
        description: 'About You',
        descriptionPlaceholder: 'Tell clients about your experience, track record, and what makes you stand out as a real estate professional in Cyprus...',
        submit: 'Submit Application',
        successTitle: 'Application Submitted!',
        successSub: 'We will review your application and get back to you within 2 business days.',
        done: 'Done',
      },
      errors: {
        required: 'Required',
        validEmail: 'Valid email required',
      },
      viewProfile: 'View Profile',
    },
    brokerProfile: {
      back: 'Back to Agents',
      verified: 'Verified Agent',
      call: 'Call',
      emailBtn: 'Email',
      about: 'About',
      speciality: 'Speciality',
      languages: 'Languages',
      etek: 'ETEK No.',
      location: 'Based in',
      notFound: 'Agent not found.',
      noBio: 'No biography available.',
      noSpeciality: 'General Practice',
      editProfile: 'Edit Profile',
      saveChanges: 'Save Changes',
      saving: 'Saving...',
      saved: 'Profile updated',
      cancelEdit: 'Cancel',
      editPhone: 'Phone Number',
      editDescription: 'About You',
      editDescPlaceholder: 'Tell clients about your experience, track record, and what sets you apart...',
      editSpeciality: 'Speciality',
      editSpecPlaceholder: 'e.g. Luxury Villas, Commercial',
      editLanguages: 'Languages',
      editLangPlaceholder: 'e.g. English, Greek, Russian',
      yourListings: 'Your Active Listings',
      noListings: 'No active listings yet.',
    },
    supportChat: {
      initialMessage: "Hi! I'm your CosmoEstates assistant. Ask me anything about buying, selling or renting property in Cyprus — I'm here 24/7.",
      headerName: 'CosmoEstates Support',
      headerStatus: 'AI Assistant · Always online',
      placeholder: 'Ask anything about Cyprus property...',
      quickReplies: [
        'How do I buy property in Cyprus?',
        'Best areas for expats?',
        'How does the valuation work?',
        'What are transfer fees in Cyprus?',
      ],
    },
    boostCheckout: {
      back: 'Back',
      heroEyebrow: 'YOUR LISTING BOOST',
      heroTitle: 'Get your listing seen by more buyers',
      heroSub: 'Secure checkout powered by Stripe · One-time payment, no subscription',
      formTitle: 'Choose your package',
      formSubtitle: 'Boost a single listing',
      title: 'Boost Your Listing',
      sub: 'Priority placement in search & featured carousel rotation for 14 days',
      emailLabel: 'Email address',
      included: 'Everything included:',
      redirecting: 'Redirecting to Stripe…',
      pay: 'Pay €{price} — Secure Checkout',
      ssl: '🔒 256-bit SSL · Powered by Stripe',
      oneTime: 'one-time',
      thirtyDayUpTo: '14 days · up to {n} {unit}',
      listingUnit: 'listing',
      listingsUnit: 'listings',
      orderSummary: 'Order Summary',
      listingBoost: 'Listing Boost',
      duration: 'Duration',
      thirtyDays: '14 days',
      listings: 'Listings',
      upTo: 'Up to',
      priceVat: 'Price (VAT incl.)',
      total: 'Total',
      termsNote: 'One-time payment. No subscription.',
      features: [
        {
          title: 'Better placement',
          desc: 'Boosted listings rotate above the standard pool in search results, giving your property a clear visibility advantage.',
        },
        {
          title: 'Featured carousel rotation',
          desc: 'Your listing rotates through the homepage Featured Listings carousel — boosted listings get priority placement when buyers visit.',
        },
      ],
    },
    renovation: {
      back: 'Back',
      title: 'Renovation Tool',
      sub: 'Build a detailed room-by-room renovation budget with real Cyprus pricing and quality levels.',
      propertyLocation: 'Property Location',
      step1: 'What are you renovating?',
      step1Add: 'Add another space',
      step2: 'Select work items',
      step3: 'Quality level',
      outdoorArea: 'Outdoor area',
      totalFloorArea: 'Total floor area',
      roomSize: 'Room size',
      continueBtn: 'Continue',
      selectedCount: 'selected',
      estimatedCostRoom: 'Estimated cost for this room',
      addToPlan: 'Add',
      toPlan: 'to plan',
      yourPlan: 'Your renovation plan',
      itemsLabel: 'items',
      tipsTitle: 'Renovation tips',
      previous: 'Previous',
      next: 'Next',
      costSummary: 'Cost Summary',
      emptyPlan: 'Add rooms to your plan to see a full cost breakdown.',
      estimatedTotal: 'Estimated Total :',
      downloadReport: 'Download Report',
      contingency: 'Add 10–15% contingency for unexpected costs',
      costByQuality: 'Cost by quality level',
      rooms: {
        kitchen: 'Kitchen',
        bathroom: 'Bathroom',
        bedroom: 'Bedroom',
        living: 'Living Room',
        outdoor: 'Outdoor / Pool',
        whole: 'Full Property',
      },
      quality: {
        budget: { label: 'Budget', desc: 'Functional finishes, local materials', tag: 'Economical' },
        mid: { label: 'Mid-Range', desc: 'Quality brands, modern finishes', tag: 'Most common' },
        luxury: { label: 'Luxury', desc: 'Premium imported materials & design', tag: 'High-End' },
      },
      renovItems: {
        kitchen: {
          cabinets: 'Cabinets & Worktops',
          appliances: 'Appliances',
          tiling: 'Floor & Wall Tiling',
          plumbing: 'Plumbing',
          electrics: 'Electrics',
          painting: 'Painting',
        },
        bathroom: {
          fixtures: 'Sanitaryware & Taps',
          tiling: 'Floor & Wall Tiling',
          shower: 'Shower / Bath',
          plumbing: 'Plumbing',
          electrics: 'Electrics & Lighting',
          vanity: 'Vanity & Storage',
        },
        bedroom: {
          flooring: 'Flooring',
          painting: 'Painting',
          wardrobes: 'Built-in Wardrobes',
          electrics: 'Electrics & Lighting',
          ac: 'Air Conditioning',
        },
        living: {
          flooring: 'Flooring',
          painting: 'Painting',
          electrics: 'Electrics & Lighting',
          ac: 'Air Conditioning',
          fireplace: 'Fireplace',
          windows: 'Windows & Doors',
        },
        outdoor: {
          pool: 'Swimming Pool',
          landscaping: 'Landscaping',
          paving: 'Paving / Decking',
          pergola: 'Pergola / Shade',
          fence: 'Fencing / Gates',
          bbq: 'BBQ / Outdoor Kitchen',
        },
        whole: {
          structural: 'Structural / Walls',
          flooring: 'All Flooring',
          painting: 'Full Paint',
          kitchen: 'Full Kitchen',
          bathrooms: 'Bathrooms (all)',
          electrics: 'Full Rewire',
          plumbing: 'Full Plumbing',
          windows: 'All Windows & Doors',
          ac: 'Full AC System',
        },
      },
      tipCategories: {
        Budget: 'Budget',
        ROI: 'Value',
        Planning: 'Planning',
        Rental: 'Rental',
        Materials: 'Materials',
      },
      tips: [
        { title: 'Always get 3 quotes', body: 'Labour costs in Cyprus vary by up to 40% between contractors. Always get at least three written quotes before committing — and check they include materials, waste removal, and VAT.' },
        { title: 'Buy tiles at end of season', body: 'Tile suppliers in Cyprus heavily discount end-of-line stock in October and March. Buying slightly more than you need also protects against breakage and future replacements.' },
        { title: 'Keep plumbing in the same place', body: 'Moving pipes in a bathroom or kitchen can add €1,500–€3,000 to your bill. If you can work around existing pipe locations, you will save significantly.' },
        { title: 'Renovate in winter', body: 'Contractors in Cyprus are busiest April–September. Booking work in November–January typically gets you 10–20% lower labour rates and faster turnaround times.' },
        { title: 'Kitchens add the most value', body: 'A mid-range kitchen renovation in Cyprus typically adds 1.5–2x its cost to property value. Focus budget here before any other room if resale value is your goal.' },
        { title: 'A pool adds 8–12% to value', body: 'In Limassol and Paphos, adding a pool to a villa or detached house increases asking price by 8–12% on average. It also dramatically improves rental yield for short-term lets.' },
        { title: 'Neutral colours sell faster', body: 'Properties painted in warm neutral tones (off-white, greige, warm grey) sell on average 15 days faster in Cyprus than those with bold or dated colour schemes.' },
        { title: 'Double glazing pays for itself', body: 'Upgrading to double-glazed aluminium windows reduces air-conditioning costs by up to 30% annually in Cyprus. Buyers increasingly ask about this — it is a strong selling point.' },
        { title: 'Get permits before you start', body: 'Structural changes, extensions, and pool installations require a building permit from your local municipality. Unpermitted work can block property sales and attract fines.' },
        { title: 'Upgrade electrics proactively', body: 'Many older properties in Cyprus run on 40-amp single-phase systems. Upgrading to three-phase while walls are open adds €800–€1,200 but saves you opening them again later.' },
        { title: 'Insulate before you tile', body: 'Roof insulation is dramatically easier and cheaper to install before interior finishes go in. In Cyprus summer heat, good insulation reduces AC costs by up to 25%.' },
        { title: 'Install a water softener early', body: 'Cyprus has some of the hardest water in Europe. Installing a water softener during a bathroom or kitchen renovation protects fixtures, tiles, and appliances from limescale damage.' },
        { title: 'En-suite adds €150/week to rent', body: 'Adding an en-suite bathroom to a master bedroom in Limassol or Paphos typically increases short-term rental income by €100–€200 per week during peak season.' },
        { title: 'Solar panels attract tenants', body: 'Properties with solar water heaters and photovoltaic panels command 8–12% higher long-term rents in Cyprus. Tenants value lower electricity bills in the hot climate.' },
        { title: 'Fast WiFi is now a deal-breaker', body: 'For short-term lets, properties with fibre broadband (100Mbps+) earn significantly better reviews and can charge a 5–10% premium. Cyta fibre is available in most urban areas.' },
        { title: 'Shading doubles summer bookings', body: 'Properties with pergolas, sail shades or covered terraces see up to 2x more summer bookings on Airbnb compared to those with exposed outdoor areas in the Cyprus heat.' },
        { title: 'Local stone is half the price', body: 'Imported marble from Italy costs 3–4x more than local Cypriot stone, which is equally attractive and better suited to the climate. Ask your supplier about Lefkara or Paphos stone.' },
        { title: 'Porcelain beats ceramic outdoors', body: 'Ceramic tiles crack in the Cyprus temperature swings between winter and summer. Porcelain tiles (PEI 4 or 5 rating) are essential for outdoor terraces and pool surrounds.' },
        { title: 'Marine-grade fittings near coast', body: 'Within 2km of the coast, standard steel fixtures corrode within 3–5 years. Specify marine-grade stainless steel or aluminium for railings, screws, and outdoor fittings.' },
        { title: 'Large format tiles look premium', body: '60x60cm or 80x80cm tiles in a bathroom or kitchen create a high-end look with fewer grout lines. The cost difference over 30x30cm tiles is often only €5–€8 per m².' },
      ],
      notAQuoteBanner: 'Indicative estimate — not a quote. Based on typical Cyprus contractor rates (2024–2025). Actual quotes vary by property condition, materials, and contractor. Get 2–3 written quotes before committing — do not use these figures as price targets in negotiations.',
    },
    mortgage: {
      back: 'Back',
      title: 'Mortgage Tools',
      heroTitle: 'Financial Compass',
      sub: 'Calculate repayments and see how overpaying saves you money.',
      tabMortgage: 'Mortgage Calculator',
      tabAffordability: 'Affordability Checker',
      tabOverpayment: 'Overpayment Calculator',
      yourDetails: 'Your Details',
      propPrice: 'Property Price',
      deposit: 'Deposit',
      loanAmtHint: 'Loan amount:',
      interestRate: 'Interest Rate (p.a.)',
      interestRateInfo: 'The annual interest rate offered by your bank. In Cyprus, typical mortgage rates range from 3.5% to 5.5% for primary residences.',
      mortgageTerm: 'Mortgage Term',
      years: 'years',
      monthlyPayment: 'Monthly Payment',
      perMonthFor: 'per month for',
      loanAmount: 'Loan Amount',
      totalInterest: 'Total Interest',
      totalRepayment: 'Total Repayment',
      downloadReport: 'Mortgage Report',
      generatingPdf: 'Generating PDF…',
      proOnly: 'Available for Pro subscribers',
      repaymentBreakdown: 'Repayment Breakdown',
      principal: 'Principal',
      interest: 'Interest',
      monthlyLabel: 'Monthly',
      perMonth: '€ / month',
      yearByYear: 'Year-by-Year Repayments',
      year: 'Year',
      yourFinances: 'Your Finances',
      monthlyIncome: 'Monthly Net Income',
      monthlyIncomeInfo: 'Your take-home pay after tax each month.',
      monthlyExpenses: 'Monthly Expenses',
      disposableIncome: 'Disposable income:',
      monthlyExpensesInfo: 'Regular committed outgoings — rent, loans, subscriptions, living costs.',
      availableDeposit: 'Available Deposit',
      whatCanAfford: 'What You Can Afford',
      maxPropPrice: 'Maximum Property Price',
      basedOnDTI: 'based on a {pct}% debt-to-income ratio',
      maxMonthly: 'Max Monthly Payment',
      pctOfDisposable: '35% of disposable income',
      maxLoan: 'Max Loan',
      yourDeposit: 'Your Deposit',
      depositPct: 'Deposit %',
      incomeAlloc: 'Income Allocation',
      mortgageLabel: 'Mortgage',
      expensesLabel: 'Expenses',
      remainingLabel: 'Remaining',
      affordNote: 'Based on Cyprus bank lending criteria: max 35% of net disposable income.',
      adjustToSee: 'Adjust your income and expenses to see what you can afford.',
      yourMortgage: 'Your Mortgage',
      loanAmount2: 'Loan Amount',
      extraMonthly: 'Extra Monthly Payment',
      extraMonthlyInfo: 'How much extra you pay on top of your standard monthly repayment.',
      standardMonthly: 'Standard Monthly',
      withOverpayment: 'With Overpayment',
      whatOverpaySaves: 'What Overpaying Saves You',
      timeSaved: 'Time Saved',
      noSaving: 'No saving',
      mortgageEnds: 'Mortgage ends',
      interestSaved: 'Interest Saved',
      lessPaidToBank: 'Less paid to the bank',
      remainingBalance: 'Remaining Balance Over Time',
      standard: 'Standard',
      withOverpaymentLegend: 'With Overpayment',
      overpayNote: 'Overpaying your mortgage reduces the outstanding balance faster, saving significant interest.',
      ctaTitle: 'Ready to find your perfect property?',
      ctaSub: 'Browse available listings in Cyprus and start your journey today.',
      browseProperties: 'Browse Properties',
      freeValuation: 'Get Free Price Estimate',
      rateCaption: 'Example only — Cyprus avg ~3.15% (new loans, early 2026). Check current rates with a bank.',
      dtiGuidelineNote: '35% is a guideline, not a rule — actual lending limits vary by bank and are set individually.',
      purchaseCostsDisclaimer: 'Estimates loan payments only — does not include Cyprus purchase costs (transfer fees, stamp duty, VAT on new builds, legal fees). Speak to a licensed mortgage advisor for personalised advice.',
    },
    propertyReport: {
      generating: 'Generating...',
      downloadPdf: 'Download PDF',
      close: 'Close',
      coverTag: 'Property Intelligence Report',
      forRent: 'For Rent',
      forSale: 'For Sale',
      askingPrice: 'Asking price',
      yieldSuffix: '% yield',
      estRentalYield: 'Est. rental yield',
      generated: 'Generated',
      section01: 'Property overview',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      floorArea: 'Floor area',
      type: 'Type',
      featuresAmenities: 'Features and amenities',
      section02: 'Price analysis',
      thisProperty: 'This property',
      cityAverage: '{city} average',
      aboveAvg: 'Priced {pct}% above the {city} city average per m² — typical for properties with premium features or location.',
      belowAvg: 'Priced {pct}% below the {city} city average per m² — potentially good value in the current market.',
      atAvg: 'Priced exactly at the {city} city average per m².',
      marketTrend: '{city} market trend (12 months)',
      annualGrowth: 'annual growth',
      section03: 'Investment overview',
      estAnnualYield: 'Est. annual yield',
      estRentalIncome: 'Est. rental income',
      estAnnualIncome: 'Est. annual income',
      yieldNote: 'Yield estimate based on average rental returns for {type} properties in {city} as of {today}.',
      section04: 'Mortgage summary',
      deposit: 'Deposit',
      amount: 'Amount',
      loan: 'Loan',
      monthlyPayment: 'Monthly payment',
      recommended: 'recommended',
      mortgageNote: 'Based on 4.5% interest over 25 years. Consult a licensed mortgage advisor for personalised advice.',
      section05: 'Neighbourhood',
      generatingAI: 'Generating AI insights for {region}...',
      schoolsNearby: 'Schools nearby',
      transport: 'Transport',
      airport: 'Airport',
      expatCommunity: 'Expat community',
      intlResidents: '% international residents',
      aiNote: 'Neighbourhood insights generated by AI · Based on available data for {city}, Cyprus',
      footerText: 'cosmoestates.cy · Property Intelligence Report · Generated {today}',
      footerDisc: 'All data is indicative. CosmoEstates accepts no liability for decisions made based on this report.',
      scoreLabels: {
        noise: 'Noise level',
        schools: 'Schools',
        amenities: 'Amenities',
        expat: 'Expat community',
      },
      scoreRating: {
        excellent: 'Excellent',
        veryGood: 'Very Good',
        good: 'Good',
        fair: 'Fair',
      },
    },
    renovationReport: {
      generating: 'Generating...',
      downloadPdf: 'Download PDF',
      close: 'Close',
      coverTag: 'Renovation Cost Report',
      roomPlanTitle: '{n}-Room Renovation Plan',
      finishSub: '{city}, Cyprus · {quality} finish',
      roomsPlanned: 'Rooms planned',
      baseEstimate: 'Base estimate',
      withContingency: 'With contingency',
      section01: 'Room by room breakdown',
      section02: 'Cost summary',
      contingencyLabel: '10% contingency buffer',
      totalBudget: 'Total recommended budget',
      estimatesNote: 'All estimates are based on {quality} finish materials and labour rates in {city}, Cyprus as of {today}. Premium finishes may increase costs by 30–50%.',
      valueNote: 'At this budget, a full renovation could increase the property\'s rental value by an estimated €{rentMin}–€{rentMax}/month and its sale value by €{saleMin}–€{saleMax}.',
      footerText: 'cosmoestates.cy · Renovation Cost Report · Generated {today}',
      footerDisc: 'All estimates are indicative. CosmoEstates accepts no liability for actual renovation costs.',
    },
    neighbourhood: {
      back: 'Back',
      title: 'Neighbourhood Intelligence',
      sub: 'Full scores on transport, schools, amenities and expat density, alongside the official Property Price Index — find the area that truly fits your lifestyle.',
      signInTitle: 'Sign in to unlock',
      signInSub: 'Neighbourhood Intelligence is free for all registered CosmoEstates members. Sign in or create an account to access detailed scores, AI analysis, and investment data for every neighbourhood in Cyprus.',
      features: [
        'Transport & connectivity scores',
        'School catchment ratings',
        'Property Price Index (CBC data)',
        'Expat community density',
        'Amenity & transport ratings',
        'AI neighbourhood analysis',
      ],
      createAccount: 'Create Free Account',
      signIn: 'Sign In',
      freeNote: 'Free forever. No credit card required.',
      aiBadge: 'AI Analysis',
      aiNote: 'Results are AI-estimated — verify locally',
      clear: '✕ Clear',
      fullDetails: 'Full details',
      expatResidents: '% expat residents',
      transport: 'Transport:',
      priceTrend: 'Price Trend (6 qtrs)',
      estYield: 'Est. Rental Yield',
      searchPlaceholder: 'Search saved areas or type any Cyprus neighbourhood for AI analysis...',
      analysing: 'Analysing...',
      aiAnalyse: 'AI Analyse',
      noMatch: 'No saved areas match — press Enter or click AI Analyse to generate intelligence for',
      sortBy: 'Sort by:',
      sortOptions: {
        expat: 'Expat Density',
        schools: 'Schools',
        amenities: 'Amenities',
        transport: 'Transport',
        development: 'Development',
      },
      noNeighbourhoods: 'No neighbourhoods match your search.',
      allCities: 'All Cities',
      barLabels: {
        noise: 'Noise',
        schools: 'Schools',
        amenities: 'Amenities',
        expat: 'Expat',
        transport: 'Transport',
      },
      backToNeighbourhoods: 'Back to Neighbourhoods',
      overall: 'Overall',
      avgAcrossFactors: 'Average across 5 factors',
      whatMeans: 'What this means for you',
      howCompares: 'How {name} compares',
      allNeighbourhoods: 'all neighbourhoods',
      showLess: 'Show less',
      showMore: 'Show {n} more',
      nearbySchools: 'Nearby Schools',
      keyAmenities: 'Key Amenities',
      areaHighlights: 'Area Highlights',
      ctaTitle: 'Ready to explore properties in {name}?',
      ctaSub: 'Browse available listings in this neighbourhood and book a viewing today.',
      ctaBtn: 'Browse Properties in {name}',
      notFound: 'Neighbourhood not found.',
      transportChip: 'Transport:',
      expatsChip: '% expats',
      overPeriod: 'over period',
      scoreLabels: {
        noise: 'Noise Level',
        schools: 'School Quality',
        amenities: 'Amenities',
        expat: 'Expat Community',
      },
      scoreTips: {
        noise: 'Higher = quieter. Measures day/night sound levels from traffic, nightlife and industry.',
        schools: 'Rates the quality and availability of schools, including international curricula.',
        amenities: 'Covers retail, healthcare, dining, recreation and everyday conveniences.',
        expat: 'Measures the size and activity of the international community in the area.',
      },
    },
    faq: {
      badge: 'Help Centre',
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about buying, renting, and listing on CosmoEstates.',
      cat: {
        gettingStarted: 'Getting Started',
        buyingRenting: 'Buying & Renting',
        listingProperty: 'Listing a Property',
        proBoost: 'Pro Membership & Listing Boost',
        privacySafety: 'Privacy & Safety',
      },
      items: {
        gs0q: 'What is CosmoEstates?',
        gs0a: 'CosmoEstates is a Cyprus property portal connecting buyers, renters, sellers and landlords across all five districts — Limassol, Nicosia, Paphos, Larnaca and Famagusta. Alongside listings we offer a Property Price Estimate tool, a renovation cost calculator, neighbourhood data sourced from the Central Bank of Cyprus and OpenStreetMap, and a broker directory.',
        gs1q: 'What features require a Pro membership?',
        gs1a: 'Pro unlocks the premium layer of CosmoEstates: full Neighbourhood Intelligence (transport, schools, amenities, expat density plus the official Property Price Index), priority placement in seller inboxes so your messages get seen first, instant alerts when a matching property hits the market, and price-drop alerts on properties you have saved.',
        gs2q: 'Which areas of Cyprus are covered?',
        gs2a: 'We cover all five districts of the Republic of Cyprus: Limassol, Nicosia, Paphos, Larnaca and Famagusta (free area, including Ayia Napa, Protaras and Paralimni). New listings from agents and private owners are added daily.',
        br0q: 'How do I search for a property?',
        br0a: 'Use the search bar on the homepage to enter a city, neighbourhood, or keyword. On the Listings page you can then filter by price, property type (house, apartment, villa), and listing type (for sale or rent). Results are ranked by relevance and listing quality — not solely by who paid for a boost.',
        br1q: 'How do I contact a seller or landlord?',
        br1a: 'Each property detail page has a built-in messaging panel. Type your message and tap Send — the owner or agent receives it directly in their inbox. You need a CosmoEstates account to send messages. Pro members get a verified Pro badge on their messages.',
        br2q: "Can I save properties I'm interested in?",
        br2a: "Yes. Tap the heart icon on any property card or detail page to save it to your watchlist. You'll find all saved properties in the Saved section of your account.",
        br3q: 'What is the Property Price Estimate?',
        br3a: 'Our Property Price Estimation tool is free for everyone — fill in a few details about the property and we will email you an indicative market-value range. It is a starting-point estimate, not a formal Property Price Estimation.',
        br4q: "What's the Property Price Index?",
        br4a: 'The Central Bank of Cyprus publishes a quarterly Residential Property Price Index for each Cyprus district, with 2010 Q1 = 100 as the baseline. We display this series on every neighbourhood detail page so you can see real district-level price trends rather than platform-internal averages.',
        br5q: "Why don't you show a price per square meter?",
        br5a: 'Platform-derived €/m² averages are computed from listing prices, which is a small biased sample — they can mislead more than they inform. Instead we show the Central Bank of Cyprus Property Price Index for each district, which is the authoritative published price series.',
        lp0q: 'How do I list my property on CosmoEstates?',
        lp0a: 'Create an account, then click "List a Property" in the navigation menu. Fill in the property details, upload photos, and publish. Your listing goes live immediately.',
        lp1q: 'How many photos should I include?',
        lp1a: 'We recommend at least 8 photos covering all main rooms, the exterior, and any standout features such as a pool or garden.',
        lp2q: 'Can I edit or remove my listing after publishing?',
        lp2a: 'Yes. Go to "My Listings" in your account and click Edit on any listing to update details, swap photos, or adjust the price. You can also mark the property as sold or permanently delete a listing at any time. Changes go live instantly.',
        pb0q: 'What does Pro membership include?',
        pb0a: 'Pro unlocks four features: full Neighbourhood Intelligence (with the official Property Price Index), Priority Seller Inbox so your messages appear at the top, Smart Alerts the moment a matching property goes live, and Price Drop Alerts on properties you have saved.',
        pb1q: 'What is a Listing Boost?',
        pb1a: "A Boost is a one-time 14-day enhancement for a specific listing, priced at €10.99 per listing. Boosted listings rotate through the homepage Boosted carousel and receive a ranking advantage in search results within the buyer's active filters.",
        pb2q: 'How is the boost system kept fair?',
        pb2a: "Boosted listings only appear within the buyer's active search filters — they are never injected into unrelated results.",
        pb4q: 'How do I manage or cancel my Pro subscription?',
        pb4a: "Go to your Profile page and click Manage subscription. That opens the Stripe customer portal where you can switch plan, update your payment method, or cancel. If we can't find your subscription on file, email cosmoestatescyprus@gmail.com and we'll handle it.",
        ps1q: 'How do I report a suspicious listing?',
        ps1a: 'Tap the flag button on any property detail page to report a suspicious or fraudulent listing — available to everyone, no Pro membership required. Our team reviews all reports within 24 hours.',
        ps2q: 'How do I delete my account?',
        ps2a: 'You can request account deletion from the Settings page under Account Management. All your listings, messages, and personal data will be permanently removed within 30 days in line with GDPR requirements.',
      },
    },
    brokerListings: {
      backToProfile: 'Back to profile',
      brokerFallback: 'Broker',
      listingsByPrefix: 'Listings by ',
      activeListing: 'active listing',
      activeListings: 'active listings',
      empty: 'This broker has no active listings yet.',
    },
    legal: {
      breadcrumb: 'Legal',
      effectiveDate: 'Effective date:',
      lastUpdated: 'Last updated:',
      onThisPage: 'On this page',
      backToAll: '← Back to all legal documents',
      indexTitle: 'Legal documents',
      indexCrumb: 'Legal',
      indexSubtitle: 'The legal terms and policies that govern your use of CosmoEstates. These documents apply to everyone using the Platform — visitors, registered users, agents, and partners.',
      indexRead: 'Read →',
      indexMetaTitle: 'Legal Documents | CosmoEstates',
      indexMetaDesc: 'Index of CosmoEstates legal documents: Terms, Privacy, Cookies, Disclaimer, Acceptable Use, and Billing terms.',
      docs: {
        termsTitle: 'Terms & Conditions',
        termsDesc: 'The rules for using CosmoEstates: account, listings, tools, lead-generation, fees, liability, and dispute resolution.',
        privacyTitle: 'Privacy Policy',
        privacyDesc: 'What personal data we collect, why we use it, who we share it with, how long we keep it, and your GDPR rights.',
        cookiesTitle: 'Cookie Policy',
        cookiesDesc: 'How we use cookies, localStorage, and sessionStorage — including the "recently viewed" feature — and how to manage consent.',
        disclaimerTitle: 'Disclaimer',
        disclaimerDesc: 'How AI tools (Price Estimate, Renovation cost, Neighbourhood Intelligence, Mortgage Calculator) work and their limits.',
        acceptableUseTitle: 'Acceptable Use & Listing Policy',
        acceptableUseDesc: 'What you can and cannot post on CosmoEstates, moderation, and consequences for breach.',
        billingTitle: 'Subscription, Boost & Refund Terms',
        billingDesc: 'Pro plans, Boost packages, CPL fees, and your 14-day right to withdraw as a consumer.',
      },
    },
    policyEmbed: {
      privacyTitle: 'Privacy Policy',
      termsTitle: 'Terms of Service',
      cookiesTitle: 'Cookies Policy',
      subtitle: 'Maintained by Termageddon — see the embedded policy below for the current version.',
      loadingPre: 'Please wait while the policy is loaded. If it does not load, please ',
      clickHere: 'click here to view the policy',
      clickHereAria: 'click here to view the policy',
    },
    contactBroker: {
      titlePrefix: 'Contact ',
      fallbackName: 'this agent',
      subtitle: "Choose how you'd like to get in touch.",
      call: 'Call',
      email: 'Email',
      empty: 'No contact details available yet.',
      closeAria: 'Close',
    },
    rateLimit: {
      proOnlyTitle: 'Pro members only',
      proOnlyBody: 'This feature is available exclusively for CosmoEstates Pro members.',
      upgradeToPro: 'Upgrade to Pro',
      signInTitle: 'Please sign in',
      signInBody: 'You need to be logged in to use this feature.',
      signIn: 'Sign in',
      limitTitle: "You've reached your limit",
      limitBodyPrefix: "You've used all ",
      limitBodyMid: ' ',
      limitBodySuffix: ' requests for this 3-hour window.',
      resetsInPrefix: 'Resets in ',
      resetsInSuffix: '.',
      contactHelp: 'Contact us for help',
      upgradeHigher: 'Upgrade for higher limits',
      proHintPrefix: 'Pro members get ',
      proHintSuffix: ' requests per 3 hours.',
    },
    neighbourhoodCompare: {
      title: 'How this neighbourhood compares',
      thisNeighbourhood: 'This neighbourhood',
      cityAverageSuffix: ' average',
      cityAverageTitlePrefix: '',
      cityAverageTitleMid: ' average: ',
    },
    boostPage: {
      eyebrow: 'LISTING BOOST',
      heroTitle: 'Get your listing in front of more buyers.',
      heroSub: 'Promote your property to top placement, featured sections, and visual prominence — so serious buyers see it first.',
      cancelled: 'Checkout cancelled — no charges were made. Pick a package and try again.',
      benefitsTitle: 'Benefits of boosting',
      benefits: [
        {
          title: 'Better placement',
          desc: 'Boosted listings rotate above the standard pool in search results, giving your property a clear visibility advantage.',
        },
        {
          title: 'Featured carousel rotation',
          desc: 'Your listing rotates through the homepage Featured Listings carousel — boosted listings get priority placement when buyers visit.',
        },
      ],
      packagesTitle: 'Boost your Listing',
      packageDuration: '14 days',
      packageListingLabel: '1 Listing',
      cta: 'Boost now →',
    },
    boostConfirm: {
      back: '← Back to listings',
      title: 'Confirm your boost',
      sub: 'Review your package and selected listings before paying.',
      sectionPackage: 'PACKAGE',
      sectionListings: 'YOUR LISTINGS',
      loading: 'Loading listings…',
      untitled: 'Untitled listing',
      total: 'Total',
      redirecting: 'Redirecting to Stripe…',
      proceed: 'Proceed to checkout →',
      note: "You'll be redirected to Stripe to complete payment securely.",
    },
    boostSelectPage: {
      back: '← Back to package',
      title: 'Pick your listings',
      packageLabel: 'Package',
      selectedOfCap: '{n}/{cap} selected',
      next: 'Next →',
      pickUpTo: 'Pick up to {cap} listings to boost for 14 days.',
    },
    proPage: {
      choosePlan: 'Choose your plan',
      features: {
        priorityInbox: {
          title: 'Priority Inbox',
          desc: 'Pro members are more likely to be responded to first due to inbox placement.',
        },
        neighbourhood: {
          title: 'Neighbourhood Intelligence',
          bullets: [
            'Transport & connectivity ratings',
            'Nearby schools & education quality',
            'Property Price Index (CBC data)',
            'Expat density & community presence',
          ],
        },
        smartAlerts: {
          title: 'Smart Alerts',
          desc: 'Get instant notifications the moment a property matching your saved search goes live — stay ahead of the market.',
        },
        priceDropAlerts: {
          title: 'Price Drop Alerts',
          desc: 'Get notified the moment a property you have saved drops in price. Be the first to act when sellers move.',
        },
      },
    },
    checkoutHero: {
      eyebrow: 'YOUR PRO MEMBERSHIP',
      title: "You're moments away from unlocking Pro",
      sub: 'Secure checkout powered by Stripe · Cancel anytime',
    },
  },

  el: {
    nav: {
      listProperty: 'Καταχώριση Ακινήτου',
      findBroker: 'Εύρεση Μεσίτη / Agent',
      valuation: 'Εκτίμηση',
      findAgent: 'Εύρεση Agent',
      notifications: 'Ειδοποιήσεις',
      watchlist: 'Λίστα παρακολούθησης',
      profile: 'Προφίλ',
      markAllRead: 'Σήμανση όλων ως αναγνωσμένα',
      noNotifications: 'Δεν υπάρχουν ειδοποιήσεις ακόμα.',
      viewAllNotifications: 'Όλες οι ειδοποιήσεις και τα μηνύματα',
      myWatchlist: 'Η λίστα μου',
      savedSuffix: 'αποθηκευμένα',
      noSavedProperties: 'Δεν έχετε αποθηκευμένα ακίνητα ακόμα.',
      browseListings: 'Δείτε αγγελίες',
      viewAllSaved: 'Όλα τα αποθηκευμένα ακίνητα',
      perMonthShort: 'μήν',
      myProfile: 'Το προφίλ μου',
      myListings: 'Οι αγγελίες μου',
      inbox: 'Εισερχόμενα',
      settings: 'Ρυθμίσεις',
      logout: 'Αποσύνδεση',
      signIn: 'Σύνδεση',
      signInSub: 'Πρόσβαση στον λογαριασμό σας',
      register: 'Εγγραφή',
      registerSub: 'Δημιουργήστε δωρεάν λογαριασμό',
      menu: 'Μενού',
      alerts: 'Ειδοποιήσεις',
      messages: 'Μηνύματα',
      noAlerts: 'Δεν υπάρχουν ειδοποιήσεις ακόμα.',
      noMessages: 'Δεν υπάρχουν μηνύματα ακόμα.',
      noMessagesYet: 'Κανένα μήνυμα ακόμα',
      openFullInbox: 'Άνοιγμα πλήρους inbox',
      goToMessages: 'Μετάβαση στα μηνύματα',
      justNow: 'Τώρα',
      minutesShort: 'λ πριν',
      hoursShort: 'ώ πριν',
      agent: 'Μεσίτης',
      buyer: 'Αγοραστής',
      buy: 'Αγορά',
      rent: 'Ενοικίαση',
      sell: 'Πώληση',
      tools: 'Εργαλεία',
      pro: 'Pro',
    },
    home: {
      hero: {
        titleA: 'Βρες το',
        titleB: 'Ιδανικό',
        titleC: 'Ακίνητο σήμερα',
        subtitle: 'Περιήγηση Αγγελιών · Εύρεση Μεσιτών · Σύνδεση με Τεχνικούς',
      },
      search: {
        buy: 'Αγορά',
        rent: 'Ενοικίαση',
        location: 'Πόλη ή διεύθυνση…',
        propertyType: 'Τύπος ακινήτου',
        priceRange: 'Εύρος τιμής',
        upTo: 'Έως',
        from: 'Από',
        to: 'έως',
        button: 'Αναζήτηση',
        types: {
          house: 'Σπίτι',
          apartment: 'Διαμέρισμα',
          villa: 'Βίλα',
          commercial: 'Επαγγελματικό',
          underConstruction: 'Υπό κατασκευή',
        },
      },
      sectionLabel: 'ΤΙ ΜΑΣ ΞΕΧΩΡΙΖΕΙ',
      services: {
        title: 'Οι Υπηρεσίες μας',
        subtitle:
          'Το CosmoEstates δεν είναι απλώς μια πλατφόρμα ακινήτων. Σας δίνουμε τα εργαλεία για πιο έξυπνες, πιο σίγουρες αποφάσεις στην κυπριακή αγορά.',
        valuation: {
          title: 'Ενδεικτική Εκτίμηση Ακινήτου',
          description:
            'Λάβετε δωρεάν ενδεικτική εκτίμηση τιμής για οποιοδήποτε ακίνητο στην Κύπρο, βασισμένη στα δεδομένα της αγοράς. Συμπληρώστε λίγα στοιχεία και θα τη στείλουμε στο email σας — ως αφετηρία, όχι ως επίσημη εκτίμηση.',
          bullets: [
            'Άμεση ενδεικτική εκτίμηση',
            'Η εκτίμηση αποστέλλεται απευθείας στο email σας',
            '100% δωρεάν — χωρίς λογαριασμό',
          ],
          cta: 'Δωρεάν Εκτίμηση',
        },
        renovation: {
          title: 'Εργαλείο Ανακαίνισης',
          description:
            'Σχεδιάστε την ανακαίνισή σας με σιγουριά. Παίρνετε ένα ενδεικτικό συνολικό εύρος κόστους ανακαίνισης βάσει των επιλογών σας και άμεση σύνδεση με επαληθευμένους τεχνίτες για κάθε βήμα του έργου.',
          bullets: ['Σύνδεση με επαληθευμένους τεχνίτες', 'Ενδεικτικό συνολικό εύρος κόστους ανακαίνισης', 'Ανάλυση κόστους ανά δωμάτιο'],
          cta: 'Δοκιμάστε το Εργαλείο',
        },
        neighbourhood: {
          title: 'Πληροφορίες Γειτονιάς',
          description:
            'Πριν αγοράσετε, μάθετε τα πάντα για την περιοχή. Βαθμολογούμε κάθε γειτονιά της Κύπρου σε μεταφορές, σχολεία, παροχές και πυκνότητα ομογενών, μαζί με τον επίσημο Δείκτη Τιμών Ακινήτων.',
          bullets: [
            'Βαθμολογίες μεταφορών και συνδεσιμότητας',
            'Χάρτες σχολικών περιοχών',
            'Δείκτης Τιμών Ακινήτων (δεδομένα ΚΤΚ)',
            'Πυκνότητα ξένης κοινότητας',
          ],
          cta: 'Εξερεύνηση Γειτονιών',
        },
        mortgage: {
          title: 'Οικονομική Πυξίδα',
          description:
            'Υπολογίστε τις μηνιαίες δόσεις άμεσα. Προσαρμόστε τιμή, προκαταβολή, επιτόκιο και διάρκεια — χωρίς σύνδεση.',
          bullets: [
            'Εκτίμηση μηνιαίας δόσης',
            'Ανάλυση τόκου και κεφαλαίου',
            'Διάγραμμα ανά έτος',
            'Χωρίς λογαριασμό',
          ],
          cta: 'Άνοιγμα Υπολογιστή',
        },
      },
      boostedListings: {
        title: 'Προβεβλημένες Αγγελίες',
        viewAll: 'Δείτε όλες τις αγγελίες',
        viewDetails: 'Λεπτομέρειες',
      },
      style: {
        title: 'Βρες το στιλ σου',
        mediterranean: { tag: 'ΖΩΗ ΣΤΟ ΦΩΣ ΤΗΣ ΘΑΛΑΣΣΑΣ', name: 'Μεσογειακά Σπίτια' },
        modern: { tag: 'ΑΡΧΙΤΕΚΤΟΝΙΚΗ ΚΑΙ ΠΟΛΥΤΕΛΕΙΑ', name: 'Σύγχρονες Βίλες' },
        luxury: { tag: 'ΑΝΑΒΑΘΜΙΣΜΕΝΗ ΑΣΤΙΚΗ ΚΑΙ ΠΑΡΑΛΙΑΚΗ ΖΩΗ', name: 'Διαμερίσματα Πολυτελείας' },
      },
      pro: {
        eyebrow: 'COSMOESTATES PRO',
        title: 'Ο εξυπνότερος τρόπος αγοράς στην Κύπρο',
        subtitle:
          'Αναβαθμιστείτε σε Pro και ξεκλειδώστε όλα τα εργαλεία που χρειάζεστε για σίγουρες αποφάσεις βασισμένες σε δεδομένα.',
        getStarted: 'Ξεκινήστε με Pro',
        seePlans: 'Δείτε όλα τα πακέτα',
        hidePlans: 'Απόκρυψη πακέτων',
        getStartedWith: 'Ξεκινήστε με το {label} Πακέτο',
        whatsIncluded: 'Τι περιλαμβάνεται',
        features: {
          renovation: {
            title: 'Αναλυτικές Εκθέσεις Ανακαίνισης',
            description:
              'Ο βασικός υπολογιστής κόστους είναι δωρεάν για όλους. Το Pro ξεκλειδώνει την πλήρη έκθεση: εκτιμώμενη προστιθέμενη αξία ακινήτου, PDF, χρονοδιάγραμμα έργου και άμεση πρόσβαση σε επαληθευμένους τεχνίτες για κάθε βήμα του έργου.',
          },
          neighbourhood: {
            title: 'Πληροφορίες Γειτονιάς',
            description:
              'Πλήρεις βαθμολογίες μεταφορών, σχολείων, παροχών και πυκνότητας ομογενών. Επιπλέον ο επίσημος Δείκτης Τιμών Ακινήτων (δεδομένα ΚΤΚ), ιστορικό πωλήσεων και εκτιμήσεις επενδυτικής απόδοσης ανά περιοχή.',
          },
          alerts: {
            title: 'Προτεραιότητα στις Ειδοποιήσεις',
            description:
              'Λάβετε ειδοποίηση μόλις ένα ακίνητο που σας ταιριάζει βγει στην αγορά — πριν εμφανιστεί στους δωρεάν χρήστες.',
          },
          inbox: {
            title: 'Προτεραιότητα στο Inbox',
            description:
              'Το μήνυμά σας εμφανίζεται στην κορυφή του inbox κάθε πωλητή. Τα μέλη Pro φαίνονται πρώτα.',
          },
          reports: {
            title: 'Αναφορές Λεπτομερειών Αγγελίας',
            description:
              'Δημιουργήστε πλήρες PDF για οποιαδήποτε αγγελία ακινήτου — ιστορικό τιμών, βαθμολογίες γειτονιάς, εκτίμηση απόδοσης και βασικά στοιχεία σε ένα κοινόχρηστο έγγραφο.',
          },
        },
      },
      sponsored: {
        label: 'ΧΟΡΗΓΟΥΜΕΝΟ',
        title: 'Η διαφήμισή σας θα μπορούσε να είναι εδώ',
        description:
          'Φτάστε σε χιλιάδες αγοραστές και ενοικιαστές ακινήτων σε όλη την Κύπρο κάθε μέρα.',
        cta: 'Διαφημιστείτε μαζί μας',
      },
      recent: {
        eyebrow: 'Πρόσφατες Αγγελίες',
        subtitle: 'Τα τελευταία ακίνητα που προστέθηκαν στο CosmoEstates — μόλις βγήκαν στην αγορά.',
      },
      recentlyViewed: {
        eyebrow: 'Παρόμοια με αυτά που είδατε',
        subtitle: 'Επιλεγμένα ακίνητα που ταιριάζουν με αυτά που είδατε πρόσφατα.',
      },
      adSponsored: 'Χορηγούμενο',
      adTitle: 'Η διαφήμισή σας εδώ',
      adSub: 'Φτάστε σε χιλιάδες αγοραστές και ενοικιαστές ακινήτων στην Κύπρο κάθε μέρα.',
      adCta: 'Διαφημιστείτε μαζί μας',
    },
    maskedPhone: {
      loginToView: 'Συνδεθείτε για να δείτε τα στοιχεία',
    },
    listings: {
      pageTitle: 'Όλα τα Ακίνητα',
      propertiesFound: 'ακίνητα βρέθηκαν',
      propertiesIn: 'Ακίνητα στο "{q}"',
      listingType: 'ΤΥΠΟΣ ΑΓΓΕΛΙΑΣ',
      propertyType: 'ΤΥΠΟΣ ΑΚΙΝΗΤΟΥ',
      sortBy: 'ΤΑΞΙΝΟΜΗΣΗ',
      filtersBtn: 'Φίλτρα',
      filters: {
        all: 'Όλα',
        forSale: 'Προς Πώληση',
        forRent: 'Προς Ενοικίαση',
        house: 'Κατοικία',
        apartment: 'Διαμέρισμα',
        villa: 'Βίλα',
        newestFirst: 'Νεότερα Πρώτα',
        priceLow: 'Τιμή: Χαμηλή προς Υψηλή',
        priceHigh: 'Τιμή: Υψηλή προς Χαμηλή',
      },
      advertisement: 'Διαφήμιση',
      placeAd: 'Τοποθετήστε τη Διαφήμισή σας Εδώ',
      adSub: 'Φτάστε σε χιλιάδες αναζητητές ακινήτων στην Κύπρο κάθε μέρα',
      advertise: 'Διαφημιστείτε μαζί μας',
      noProperties: 'Δεν βρέθηκαν ακίνητα',
      adjustFilters: 'Δοκιμάστε να προσαρμόσετε τα φίλτρα ή αναζητήστε σε άλλη περιοχή.',
      viewProperty: 'Δείτε ακίνητο',
    },
    property: {
      back: 'Πίσω',
      save: 'Αποθήκευση',
      saved: 'Αποθηκεύτηκε',
      askingPrice: 'ΤΙΜΗ ΠΩΛΗΣΗΣ',
      defaultMessage: 'Γεια σας, ενδιαφέρομαι για αυτό το ακίνητο. Μπορούμε να κανονίσουμε επίσκεψη;',
      sendMessage: 'Αποστολή Μηνύματος',
      bedrooms: 'ΥΠΝΟΔΩΜΑΤΙΑ',
      bathrooms: 'ΜΠΑΝΙΑ',
      floorArea: 'ΕΜΒΑΔΟΝ',
      type: 'ΤΥΠΟΣ',
      featuresAndAmenities: 'Χαρακτηριστικά και Ανέσεις',
      similarProperties: 'Παρόμοια Ακίνητα',
    },
    footer: {
      tagline: 'Βρες το μέρος που θα αποκαλείς σπίτι.',
      findYourHome: 'ΒΡΕΣ ΤΟ ΣΠΙΤΙ ΣΟΥ',
      services: 'ΥΠΗΡΕΣΙΕΣ',
      company: 'ΕΤΑΙΡΕΙΑ',
      housesForSale: 'Σπίτια προς Πώληση',
      apartments: 'Διαμερίσματα',
      villas: 'Βίλες',
      forRent: 'Προς Ενοικίαση',
      commercial: 'Επαγγελματικά',
      propertyValuation: 'Εκτίμηση Ακινήτου',
      renovationSimulator: 'Εργαλείο Ανακαίνισης',
      neighbourhoodIntel: 'Πληροφορίες Γειτονιάς',
      findABroker: 'Εύρεση Agent',
      listAProperty: 'Καταχώρηση Ακινήτου',
      whoWeAre: 'Ποιοι είμαστε',
      contactUs: 'Επικοινωνία',
      privacyPolicy: 'Πολιτική Απορρήτου',
      termsOfUse: 'Όροι Χρήσης',
      cookiesPolicy: 'Πολιτική Cookies',
      joinAsAgent: 'Γίνετε Agent',
      faq: 'Συχνές Ερωτήσεις',
      legal: 'Νομικά',
      terms: 'Όροι',
      privacy: 'Απόρρητο',
      cookies: 'Cookies',
      disclaimer: 'Αποποίηση Ευθύνης',
      acceptableUse: 'Αποδεκτή Χρήση',
      billingRefunds: 'Χρεώσεις & Επιστροφές',
      manageCookies: 'Διαχείριση cookies',
      copyright: '© 2026 CosmoEstates. Με επιφύλαξη παντός δικαιώματος. — Πύλη Ακινήτων Κύπρου',
    },
    common: {
      loading: 'Φόρτωση…',
      error: 'Κάτι πήγε στραβά',
      retry: 'Δοκιμάστε ξανά',
      cancel: 'Ακύρωση',
      save: 'Αποθήκευση',
      close: 'Κλείσιμο',
      next: 'Επόμενο',
      back: 'Πίσω',
    },
    cta: {
      title: 'Είστε μεσίτης ή ιδιοκτήτης ακινήτου;',
      sub: 'Καταχωρίστε το ακίνητό σας δωρεάν και φτάστε σε πάνω από 2,4 εκατ. ενεργούς αγοραστές και ενοικιαστές κάθε μήνα. Ανεβάστε το listing σας σε λίγα λεπτά.',
      freeListings: 'Δωρεάν αγγελίες',
      verifiedBadge: 'Σήμα επαλήθευσης',
      analytics: 'Dashboard αναλυτικών',
      directEnquiries: 'Άμεσα αιτήματα',
      postListing: 'Δωρεάν Αγγελία',
      learnMore: 'Μάθετε Περισσότερα',
    },
    categories: {
      title: 'Αναζήτηση ανά Κατηγορία',
      sub: 'Εξερευνήστε χιλιάδες αγγελίες σε κάθε τύπο ακινήτου',
      viewAll: 'Δείτε όλες τις κατηγορίες →',
      listingsSuffix: 'αγγελίες',
      names: {
        houses: 'Κατοικίες',
        apartments: 'Διαμερίσματα',
        villas: 'Βίλες',
        commercial: 'Επαγγελματικά',
        newBuilds: 'Νέες Κατασκευές',
        landPlots: 'Γη & Οικόπεδα',
        holiday: 'Εξοχικά',
        offices: 'Γραφεία',
        parking: 'Πάρκινγκ',
        foreclosure: 'Κατασχεμένα',
      },
    },
    partners: {
      discover: 'Ανακαλύψτε τους Συνεργάτες μας',
      viewAll: 'Όλοι οι Συνεργάτες',
      eyebrow: 'Έμπιστοι Συνεργάτες',
      title: 'Σε συνεργασία με τα καλύτερα της Κύπρου',
      subtitle: 'Πιστοποιημένα γραφεία σε όλο το νησί, επιλεγμένα με κριτήρια ποιότητας και εξυπηρέτησης.',
      discoverAll: 'Όλοι οι συνεργάτες →',
    },
    allPartners: {
      badge: 'Οι Συνεργάτες μας',
      title: 'Γνωρίστε τους Συνεργάτες του CosmoEstates',
      subtitle: 'Περιηγηθείτε στα γραφεία και τους επαγγελματίες που συνεργάζονται με το CosmoEstates για να συνδέουν αγοραστές, ενοικιαστές και πωλητές σε όλη την Κύπρο.',
      loading: 'Φόρτωση συνεργατών…',
      viewListings: 'Δείτε Αγγελίες',
      fairnessNote: 'Η σειρά των συνεργατών τυχαιοποιείται σε κάθε φόρτωση σελίδας για να διασφαλιστεί ίση ορατότητα σε όλα τα γραφεία.',
      noPartnersFound: 'Δεν βρέθηκαν συνεργάτες',
    },
    card: {
      beds: 'υπν.',
      baths: 'μπάνια',
      bedrooms: 'υπνοδωμάτια',
      bathrooms: 'μπάνια',
      forSale: 'Προς Πώληση',
      forRent: 'Προς Ενοικίαση',
      view: 'Δείτε →',
      viewDetails: 'Λεπτομέρειες →',
      perMonth: '/μήν',
      boosted: 'Προβεβλημένο',
      featured: 'Επιλεγμένο',
      soldBadge: 'ΠΩΛΗΘΗΚΕ',
      partnerBadge: 'Συνεργάτης',
      prevPhoto: 'Προηγούμενη φωτογραφία',
      nextPhoto: 'Επόμενη φωτογραφία',
      savePropertyAria: 'Αποθήκευση ακινήτου',
    },
    progate: {
      badge: 'CosmoEstates Pro',
      signInRequired: 'Απαιτείται σύνδεση',
      proRequired: 'Απαιτείται πρόσβαση Pro',
      signInMsg: 'Συνδεθείτε και αναβαθμιστείτε για πρόσβαση στο {feature}.',
      proMsg: 'Το {feature} είναι αποκλειστικό χαρακτηριστικό Pro. Αναβαθμιστείτε για πρόσβαση.',
      signIn: 'Σύνδεση',
      createAccount: 'Δημιουργία Λογαριασμού',
      viewPlans: 'Δείτε τα πακέτα Pro',
      noteAuth: 'Αναβαθμιστείτε σε Pro μετά τη σύνδεση',
      notePro: 'Ακύρωση ανά πάσα στιγμή · Ασφαλής πληρωμή μέσω Stripe',
      features: {
        renovation: 'Αναλυτικές Εκθέσεις Ανακαίνισης',
        neighbourhood: 'Πληροφορίες Γειτονιάς',
        alerts: 'Προτεραιότητα στις Ειδοποιήσεις',
        inbox: 'Προτεραιότητα στο Inbox',
        reports: 'Αναφορές Λεπτομερειών Αγγελίας',
      },
    },
    login: {
      welcomeBack: 'Καλώς ήρθατε ξανά',
      sub: 'Συνδεθείτε για πρόσβαση στη λίστα παρακολούθησης και τις ειδοποιήσεις σας.',
      emailAddress: 'Διεύθυνση Email',
      emailRequired: 'Το email είναι υποχρεωτικό',
      validEmail: 'Εισάγετε έγκυρο email',
      password: 'Κωδικός',
      passwordRequired: 'Ο κωδικός είναι υποχρεωτικός',
      forgotPassword: 'Ξεχάσατε τον κωδικό;',
      signIn: 'Σύνδεση',
      noAccount: 'Δεν έχετε λογαριασμό;',
      registerNow: 'Εγγραφή τώρα',
      continueWithGoogle: 'Συνέχεια με Google',
      orSignInEmail: 'ή σύνδεση με email',
      heroTitleA: 'Ξεκινήστε το ταξίδι σας',
      heroTitleB: 'στα ακίνητα της Κύπρου',
      heroTitleC: 'εδώ',
      heroSubtitle: 'Ανακαλύψτε σπίτια, συνδεθείτε με μεσίτες και βρείτε τη θέση σας κάτω από τον ήλιο της Κύπρου.',
    },
    register: {
      createAccount: 'Δημιουργήστε τον λογαριασμό σας',
      startTrialTitle: 'Ξεκινήστε τη δωρεάν δοκιμή σας',
      sub: 'Γίνετε μέλος στην κοινότητα αγοραστών ακινήτων στην Κύπρο.',
      trialSub: '5 ημέρες πλήρους πρόσβασης Pro. Χωρίς πιστωτική κάρτα.',
      trialBanner: 'Εγγράφεστε για δωρεάν δοκιμή 5 ημερών Pro',
      trialDesc: 'Αποκτήστε άμεση πρόσβαση σε όλα τα χαρακτηριστικά Pro — αναφορές ανακαίνισης, πληροφορίες γειτονιάς, ειδοποιήσεις ακινήτων και άλλα. Χωρίς κάρτα.',
      firstName: 'Όνομα',
      lastName: 'Επώνυμο',
      emailAddress: 'Διεύθυνση Email',
      phoneNumber: 'Αριθμός Τηλεφώνου',
      phoneOptional: '(προαιρετικό)',
      password: 'Κωδικός',
      passwordPlaceholder: 'Τουλάχιστον 6 χαρακτήρες',
      firstNameRequired: 'Το όνομα είναι υποχρεωτικό',
      lastNameRequired: 'Το επώνυμο είναι υποχρεωτικό',
      validEmail: 'Εισάγετε έγκυρο email',
      passwordMin: 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες',
      startFreeTrial: 'Έναρξη Δωρεάν Δοκιμής',
      createAccountBtn: 'Δημιουργία Λογαριασμού',
      trialNote: 'Η δοκιμή διαρκεί 14 ημέρες από την εγγραφή. Μετά την ολοκλήρωσή της, θα επιστρέψετε αυτόματα στο δωρεάν πλάνο. Αναβαθμιστείτε σε Pro οποτεδήποτε.',
      alreadyHaveAccount: 'Έχετε ήδη λογαριασμό;',
      signIn: 'Σύνδεση',
      continueWithGoogle: 'Συνέχεια με Google',
      orSignUpEmail: 'ή εγγραφή με email',
      heroTitleA: 'Βρείτε το σπίτι σας',
      heroTitleB: 'κάτω από τον',
      heroTitleC: 'κυπριακό ήλιο',
      heroSubtitle: 'Γίνετε μέλος μιας κοινότητας που εξερευνά ακίνητα σε Λεμεσό, Πάφο, Λευκωσία και πέρα από αυτά.',
    },
    forgot: {
      title: 'Επαναφορά κωδικού',
      sub: 'Εισάγετε το email σας και θα σας στείλουμε σύνδεσμο επαναφοράς.',
      emailAddress: 'Διεύθυνση Email',
      sendLink: 'Αποστολή Συνδέσμου',
      rememberIt: 'Θυμάστε τον;',
      signIn: 'Σύνδεση',
      checkEmail: 'Ελέγξτε το email σας',
      sentMsg: 'Στείλαμε σύνδεσμο επαναφοράς στο {email}. Κάντε κλικ για να ορίσετε νέο κωδικό.',
      notReceived: 'Δεν το λάβατε; Ελέγξτε τα spam ή',
      tryAgain: 'δοκιμάστε ξανά',
      validEmail: 'Εισάγετε έγκυρη διεύθυνση email',
    },
    profile: {
      title: 'Το Προφίλ μου',
      sub: 'Διαχειριστείτε τα προσωπικά σας στοιχεία',
      personalDetails: 'Προσωπικά Στοιχεία',
      firstName: 'Όνομα',
      lastName: 'Επώνυμο',
      emailAddress: 'Διεύθυνση Email',
      emailCannotChange: 'Το email δεν μπορεί να αλλαχθεί εδώ',
      phoneNumber: 'Αριθμός Τηλεφώνου',
      changesSaved: 'Οι αλλαγές αποθηκεύτηκαν',
      saveChanges: 'Αποθήκευση Αλλαγών',
      saving: 'Αποθήκευση...',
      proMembership: 'Συνδρομή Pro',
      proActive: 'Pro Ενεργό',
      freePlan: 'Δωρεάν Πλάνο',
      proActiveMsg: 'Έχετε πλήρη πρόσβαση σε όλες τις δυνατότητες του CosmoEstates Pro.',
      freeMsg: 'Αναβαθμιστείτε σε CosmoEstates Pro για ξεκλείδωμα αναφορών στεγαστικού, ειδοποιήσεων πτώσης τιμής, εργαλείων επένδυσης και πολλών άλλων.',
      upgradePro: 'Αναβάθμιση σε Pro',
      manageSubscription: 'Διαχείριση συνδρομής',
      portalLoading: 'Άνοιγμα…',
      portalNotSignedIn: 'Συνδεθείτε ξανά για να διαχειριστείτε τη συνδρομή σας.',
      portalNoSubscription: 'Δεν υπάρχει συνδεδεμένη συνδρομή στον λογαριασμό σας. Επικοινωνήστε με το support@cosmoestates.cy και θα το διορθώσουμε.',
      portalFailed: 'Δεν ήταν δυνατό το άνοιγμα της διαχείρισης συνδρομής. Δοκιμάστε ξανά ή επικοινωνήστε με την υποστήριξη.',
      portalMissingBefore: 'Δεν βρίσκουμε τα στοιχεία της συνδρομής σας στο σύστημά μας. Για ακύρωση ή διαχείριση της συνδρομής Pro, στείλτε email στο ',
      portalMissingAfter: ' και θα το φροντίσουμε εντός 24 ωρών.',
      failedSave: 'Αποτυχία αποθήκευσης. Προσπαθήστε ξανά.',
    },
    checkout: {
      back: 'Πίσω',
      choosePlan: 'Επιλέξτε το πακέτο σας',
      unlockPro: 'Ξεκλειδώστε την πλήρη εμπειρία CosmoEstates Pro',
      emailAddress: 'Διεύθυνση email',
      everythingIncluded: 'Τι περιλαμβάνεται:',
      paySecure: 'Πληρωμή €{amount} — Ασφαλής Ολοκλήρωση',
      ssl: '256-bit SSL',
      pci: 'PCI DSS',
      poweredByStripe: 'Με τεχνολογία Stripe',
      terms: 'Θα ανακατευθυνθείτε στη σελίδα ασφαλούς πληρωμής της Stripe. Ολοκληρώνοντας την αγορά, αποδέχεστε τους Όρους Χρήσης. Ακύρωση ανά πάσα στιγμή από το προφίλ σας.',
      termsLink: 'Όρους Χρήσης',
      orderSummary: 'Σύνοψη Παραγγελίας',
      productName: 'CosmoEstates Pro',
      priceVat: 'Τιμή (συμπ. ΦΠΑ)',
      total: 'Σύνολο',
      youSave: 'Εξοικονομείτε {discount}',
      moreFeatures: '+ {n} επιπλέον χαρακτηριστικά',
      planSuffix: 'πακέτο',
      freeTrial: 'Δωρεάν Δοκιμή',
      trialDays: '5 ημέρες εντελώς δωρεάν',
      trialSub: 'Πλήρης πρόσβαση Pro. Χωρίς πιστωτική κάρτα. Ακύρωση ανά πάσα στιγμή.',
      yourName: 'Το όνομά σας',
      startTrialBtn: 'Έναρξη Δωρεάν Δοκιμής 5 Ημερών',
      noPayment: 'Δεν απαιτούνται στοιχεία πληρωμής. Μετά τις 5 ημέρες θα μεταφερθείτε στο δωρεάν πλάνο εκτός αν αναβαθμιστείτε.',
      whatYouGetFree: 'Τι λαμβάνετε δωρεάν',
      daysProAccess: '5 Ημέρες Πρόσβασης Pro',
      noCreditCard: 'Δεν απαιτείται πιστωτική κάρτα.',
      viewPaidPlans: 'Δείτε πληρωμένα πακέτα',
      trialLive: 'Η δοκιμή σας είναι ενεργή!',
      trialMsg: 'Έχετε 5 ημέρες δωρεάν πρόσβαση Pro. Δεν απαιτείται πιστωτική κάρτα. Εξερευνήστε ό,τι έχει να προσφέρει το CosmoEstates Pro.',
      account: 'Λογαριασμός',
      trialEnds: 'Λήξη δοκιμής',
      cardNeeded: 'Χρειάζεται κάρτα;',
      no: 'Όχι',
      trialNote: 'Θα σας αποστείλουμε υπενθύμιση πριν τη λήξη της δοκιμής. Μπορείτε να αναβαθμιστείτε ή να ακυρώσετε ανά πάσα στιγμή από το προφίλ σας.',
      startExploring: 'Αρχίστε να Εξερευνάτε',
      validEmail: 'Απαιτείται έγκυρο email',
      nameRequired: 'Το όνομα είναι υποχρεωτικό',
      features: {
        priorityInbox: 'Εισερχόμενα Προτεραιότητας',
        neighbourhoodIntel: 'Πληροφορίες Γειτονιάς',
        smartAlerts: 'Έξυπνες Ειδοποιήσεις',
        priceDropAlerts: 'Ειδοποιήσεις Μείωσης Τιμής',
        pdfReports: 'PDF Αναφορές',
      },
      mostPopular: 'Πιο Δημοφιλές',
      plans: {
        monthly: { label: 'Μηνιαία', note: 'Χρέωση μηνιαίως' },
        quarterly: { label: 'Τριμηνιαία', note: 'EUR 10,19 / μήνα', badge: 'Πιο Δημοφιλές', discount: 'Εξοικ. 21%' },
        yearly: { label: 'Ετήσια', note: 'EUR 9,54 / μήνα', discount: 'Εξοικ. 26%' },
      },
    },
    settings: {
      title: 'Ρυθμίσεις',
      sub: 'Διαχειριστείτε τις προτιμήσεις του λογαριασμού σας',
      notifications: 'Ειδοποιήσεις',
      notif: {
        newListings: 'Νέες ταιριαστές αγγελίες',
        newListingsDesc: 'Λάβετε ειδοποίηση όταν μια αγγελία που ταιριάζει στην αναζήτησή σας δημοσιεύεται',
        priceDrops: 'Μειώσεις τιμής',
        priceDropsDesc: 'Ειδοποιηθείτε όταν ένα αποθηκευμένο ακίνητο μειώσει την τιμή του',
        priceDropsLocked: 'Αναβαθμιστείτε σε Pro για ειδοποιήσεις πτώσης τιμής',
        messages: 'Μηνύματα',
        messagesDesc: 'Λάβετε ειδοποιήσεις για νέα μηνύματα από πωλητές',
        newsletter: 'Εβδομαδιαίο newsletter',
        newsletterDesc: 'Ενημερώσεις αγοράς και αγγελίες κάθε εβδομάδα',
      },
      security: 'Ασφάλεια',
      dangerZone: 'Διαγραφή λογαριασμού',
      dangerDesc: 'Οριστική διαγραφή του λογαριασμού σας και όλων των σχετικών δεδομένων, συμπεριλαμβανομένων των αγγελιών, λίστας παρακολούθησης και ειδοποιήσεών σας. Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
      confirmDelete: 'Είστε απολύτως σίγουροι; Πληκτρολογήστε DELETE για επιβεβαίωση:',
      deletePlaceholder: 'Πληκτρολογήστε DELETE',
      deleting: 'Διαγραφή...',
      confirmDeleteBtn: 'Ναι, Διαγραφή Λογαριασμού',
      deleteBtn: 'Διαγραφή Λογαριασμού',
      cancel: 'Ακύρωση',
      settingsSaved: 'Οι ρυθμίσεις αποθηκεύτηκαν',
      saveSettings: 'Αποθήκευση Ρυθμίσεων',
      saving: 'Αποθήκευση…',
      password: {
        signedInAs: 'Συνδεδεμένοι ως {email}. Για ασφάλεια, θα σας στείλουμε έναν κωδικό επιβεβαίωσης.',
        updated: 'Ο κωδικός ενημερώθηκε επιτυχώς.',
        newPassword: 'Νέος Κωδικός',
        confirmPassword: 'Επιβεβαίωση Νέου Κωδικού',
        placeholder: 'Τουλάχιστον 6 χαρακτήρες',
        confirmPlaceholder: 'Επαναλάβετε τον νέο κωδικό',
        updating: 'Ενημέρωση...',
        updateBtn: 'Ενημέρωση Κωδικού',
        minLength: 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες',
        noMatch: 'Οι κωδικοί δεν ταιριάζουν',
        sessionExpired: 'Η συνεδρία έληξε — αποσυνδεθείτε και επανασυνδεθείτε.',
        error: 'Κάτι πήγε στραβά. Προσπαθήστε ξανά.',
        sendCode: 'Αποστολή κωδικού επιβεβαίωσης',
        sending: 'Αποστολή κωδικού...',
        codeSent: 'Στείλαμε έναν κωδικό επιβεβαίωσης στο {email}. Εισάγετέ τον παρακάτω μαζί με τον νέο σας κωδικό.',
        code: 'Κωδικός επιβεβαίωσης',
        codePlaceholder: 'Κωδικός επιβεβαίωσης',
        codeRequired: 'Εισάγετε τον κωδικό επιβεβαίωσης που σας στείλαμε.',
        invalidCode: 'Μη έγκυρος ή ληγμένος κωδικός. Ζητήστε νέο.',
        resendCode: 'Αποστολή νέου κωδικού',
        reauthFailed: 'Αδυναμία αποστολής κωδικού. Προσπαθήστε ξανά.',
        cancelChange: 'Ακύρωση',
        resetTitle: 'Επαναφορά κωδικού',
        resetIntro: 'Θα σας στείλουμε έναν 8-ψήφιο κωδικό επιβεβαίωσης στο {email} για να επιβεβαιώσουμε ότι είστε εσείς. Στη συνέχεια θα εισάγετε τον τρέχοντα κωδικό σας και θα επιλέξετε νέο.',
        getCodeBtn: 'Λήψη κωδικού επιβεβαίωσης',
        currentPassword: 'Τρέχων Κωδικός',
        currentPasswordPlaceholder: 'Εισάγετε τον τρέχοντα κωδικό σας',
        currentPasswordRequired: 'Εισάγετε τον τρέχοντα κωδικό σας.',
        wrongCurrentPassword: 'Ο τρέχων κωδικός είναι λανθασμένος.',
        mustBeDifferent: 'Ο νέος κωδικός πρέπει να διαφέρει από τον τρέχοντα.',
        tooWeak: 'Ο κωδικός είναι πολύ αδύναμος. Χρησιμοποιήστε έναν μεγαλύτερο, πιο δύσκολο κωδικό.',
      },
    },
    saved: {
      title: 'Αποθηκευμένα Ακίνητα',
      propertySaved: '{n} ακίνητο αποθηκευμένο',
      propertiesSaved: '{n} ακίνητα αποθηκευμένα',
      sortBy: 'Ταξινόμηση',
      savedOrder: 'Σειρά Αποθήκευσης',
      advertisement: 'Διαφήμιση',
      placeAd: 'Τοποθετήστε τη Διαφήμισή σας Εδώ',
      adSub: 'Φτάστε σε χιλιάδες αναζητητές ακινήτων στην Κύπρο κάθε μέρα',
      getInTouch: 'Επικοινωνήστε μαζί μας →',
      noSaved: 'Δεν έχετε αποθηκευμένα ακίνητα ακόμα',
      heartProperty: 'Αποθηκεύστε ένα ακίνητο για να εμφανιστεί εδώ.',
      browseListings: 'Δείτε Αγγελίες',
    },
    agentDashboard: {
      loading: 'Φόρτωση του πίνακα σας…',
      pageTitle: 'Οι Αγγελίες μου',
      pageSubViews: '{views} συνολικές προβολές · {active} ενεργές',
      tabs: {
        listings: 'Αγγελίες',
        agentProfile: 'Προφίλ Agent',
      },
      markSold: {
        title: 'Σήμανση ως Πωλήθηκε;',
        message: 'Η αγγελία θα εμφανίζει ένα banner "ΠΩΛΗΘΗΚΕ" για 4 ώρες και έπειτα θα αρχειοθετηθεί αυτόματα.',
        confirm: 'Ναι, σήμανση ως πωλήθηκε',
      },
      restore: {
        title: 'Επαναφορά αγγελίας;',
        message: 'Η αγγελία θα επαναφερθεί σε ενεργή κατάσταση και θα είναι ξανά ορατή στα δημόσια αποτελέσματα αναζήτησης.',
        confirm: 'Ναι, επαναφορά',
      },
      delete: {
        title: 'Διαγραφή αυτής της αγγελίας;',
        message: 'Αυτή η ενέργεια θα διαγράψει οριστικά την αγγελία. Δεν μπορεί να αναιρεθεί.',
        confirm: 'Ναι, διαγραφή',
      },
    },
    featured: {
      boostedListings: 'Προωθημένες Αγγελίες',
      viewAll: 'Δείτε όλες',
      viewAllListings: 'Δείτε όλες τις αγγελίες',
    },
    authModal: {
      loginTitle: 'Καλώς ορίσατε',
      registerTitle: 'Δημιουργήστε λογαριασμό',
      loginSub: 'Συνδεθείτε για πρόσβαση στη λίστα σας και τις ειδοποιήσεις.',
      registerSub: 'Εγγραφείτε στο CosmoEstates για να αποθηκεύετε ακίνητα.',
      firstName: 'Όνομα',
      lastName: 'Επώνυμο',
      email: 'Διεύθυνση Email',
      phone: 'Αριθμός Τηλεφώνου',
      password: 'Κωδικός',
      confirmPassword: 'Επιβεβαίωση Κωδικού',
      repeatPassword: 'Επαναλάβετε τον κωδικό',
      forgotPassword: 'Ξεχάσατε τον κωδικό;',
      signIn: 'Σύνδεση',
      createAccount: 'Δημιουργία Λογαριασμού',
      noAccount: 'Δεν έχετε λογαριασμό;',
      registerNow: 'Εγγραφείτε τώρα',
      haveAccount: 'Έχετε ήδη λογαριασμό;',
      signInLink: 'Συνδεθείτε',
      atLeast6: 'Τουλάχιστον 6 χαρακτήρες',
      firstNameRequired: 'Το όνομα είναι υποχρεωτικό',
      lastNameRequired: 'Το επώνυμο είναι υποχρεωτικό',
      phoneRequired: 'Το τηλέφωνο είναι υποχρεωτικό',
      passwordsNoMatch: 'Οι κωδικοί δεν ταιριάζουν',
      passwordMin: 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες',
      emailInvalid: 'Εισάγετε έγκυρη διεύθυνση email',
      passwordRequired: 'Ο κωδικός είναι υποχρεωτικός',
    },
    statsBar: {
      activeListings: 'Ενεργές Αγγελίες',
      propertyTypes: 'Τύποι Ακινήτων',
      citiesCovered: 'Πόλεις',
      registeredUsers: 'Εγγεγραμμένοι Χρήστες',
    },
    boostSuccess: {
      activating: 'Ενεργοποίηση προώθησης…',
      activatingSub: 'Παρακαλώ περιμένετε ενώ επιβεβαιώνουμε την πληρωμή σας.',
      success: 'Η αγγελία σας προωθείται!',
      successSub: 'Η αγγελία σας λαμβάνει προτεραιότητα στα αποτελέσματα αναζήτησης και εναλλάσσεται στο προβεβλημένο carousel για 14 μέρες.',
      viewMyListings: 'Οι Αγγελίες μου',
      backHome: 'Πίσω στην Αρχική',
    },
    partner: {
      notFound: 'Ο συνεργάτης δεν βρέθηκε',
      back: 'Πίσω',
      backHome: 'Πίσω στην Αρχική',
      founded: 'Ίδρυση',
      contactUs: 'Επικοινωνία',
      listings: 'Αγγελίες',
      citiesServed: 'Πόλεις Εξυπηρέτησης',
      loading: 'Φόρτωση...',
      noListings: 'Δεν υπάρχουν αγγελίες ακόμα.',
    },
    notifications: {
      title: 'Ειδοποιήσεις',
      pageTitle: 'Εισερχόμενα',
      inbox: 'Εισερχόμενα',
      alerts: 'Ειδοποιήσεις',
      loading: 'Φόρτωση...',
      noMessagesYet: 'Κανένα μήνυμα ακόμα',
      markAllRead: 'Σήμανση όλων ως αναγνωσμένα',
      noAlerts: 'Δεν υπάρχουν ειδοποιήσεις ακόμα.',
      noAlertsSub: 'Πτώσεις τιμών, νέες αγγελίες και δραστηριότητα λογαριασμού θα εμφανίζονται εδώ.',
      selectConvo: 'Επιλέξτε συνομιλία',
      selectConvoSub: 'Επιλέξτε ένα νήμα από τα αριστερά για να το διαβάσετε και να απαντήσετε',
      preview: 'Προεπισκόπηση',
      demoPlaceholder: 'Αυτή είναι μια δοκιμαστική συνομιλία…',
      typeMessage: 'Πληκτρολογήστε μήνυμα…',
      back: 'Πίσω',
      loadingConvos: 'Φόρτωση συνομιλιών…',
      noConvos: 'Δεν υπάρχουν συνομιλίες ακόμα',
      noConvosSub: 'Επικοινωνήστε με έναν πράκτορα σε οποιαδήποτε αγγελία για να ξεκινήσετε',
      browseListings: 'Αναζήτηση αγγελιών',
      sayHello: 'Κανένα μήνυμα ακόμα. Πείτε γεια!',
      justNow: 'Μόλις τώρα',
    },
    property: {
      notFound: 'Το ακίνητο δεν βρέθηκε',
      backToListings: 'Πίσω στις Αγγελίες',
      noPhoto: 'Δεν έχει ανέβει φωτογραφία για αυτό το δωμάτιο',
      photoLabel: 'Φωτογραφία',
      back: 'Πίσω',
      saved: 'Αποθηκευμένο',
      save: 'Αποθήκευση',
      report: 'Αναφορά',
      perMonth: 'Ανά Μήνα',
      perMonthShort: '/μήνα',
      askingPrice: 'Ζητούμενη Τιμή',
      messageSent: 'Το μήνυμα εστάλη! Μεταφορά στα εισερχόμενα...',
      defaultMessage: 'Γεια, ενδιαφέρομαι για αυτό το ακίνητο. Μπορούμε να κανονίσουμε μια επίσκεψη;',
      sending: 'Αποστολή...',
      messageLandlord: 'Μήνυμα στον Ιδιοκτήτη',
      sendMessage: 'Αποστολή Μηνύματος',
      bedrooms: 'Υπνοδωμάτια',
      bathrooms: 'Μπάνια',
      floorArea: 'Εμβαδόν',
      type: 'Τύπος',
      description: 'Περιγραφή',
      features: 'Χαρακτηριστικά και Ανέσεις',
      similarProperties: 'Παρόμοια Ακίνητα',
      rooms: {
        livingRoom: 'Σαλόνι',
        masterBedroom: 'Κύριο Υπνοδωμάτιο',
        kitchen: 'Κουζίνα',
        bathroom: 'Μπάνιο',
        gardenTerrace: 'Κήπος / Βεράντα',
        exterior: 'Εξωτερικό',
      },
    },
    inbox: {
      title: 'Εισερχόμενα',
      sub: 'Μηνύματα από αγοραστές και μεσίτες σχετικά με τις αγγελίες σας.',
      loading: 'Φόρτωση μηνυμάτων…',
      empty: 'Δεν υπάρχουν μηνύματα ακόμα',
      emptySub: 'Όταν αγοραστές ή μεσίτες σας στείλουν μήνυμα για μια αγγελία, θα εμφανιστεί εδώ.',
      browseListings: 'Δείτε αγγελίες',
      unreadOne: '1 μη αναγνωσμένο',
      unreadMany: '{n} μη αναγνωσμένα',
    },
    myListings: {
      title: 'Οι Αγγελίες μου',
      sub: 'Ακίνητα που έχετε καταχωρήσει στο CosmoEstates',
      loading: 'Φόρτωση αγγελιών…',
      noListings: 'Δεν υπάρχουν αγγελίες ακόμα',
      noListingsSub: 'Δεν έχετε αναρτήσει ακίνητα. Ξεκινήστε και φτάστε σε χιλιάδες αγοραστές.',
      postListing: '+ Δημοσίευση Αγγελίας',
      addListing: '+ Προσθήκη Αγγελίας',
      boostListings: 'Προώθηση Αγγελιών',
      boost: 'Προώθηση',
      boosted: 'Προωθημένο',
      daysLeft: '{n} μέρες',
      edit: 'Επεξεργασία',
      view: 'Προβολή',
      delete: 'Διαγραφή',
      markSold: 'Πωλήθηκε',
      restore: 'Επαναφορά',
      filterActive: 'Ενεργές',
      filterSold: 'Πωλήθηκαν',
      filterArchived: 'Αρχειοθετημένες',
      filterAll: 'Όλες',
      filterEmpty: 'Καμία αγγελία σε αυτή την προβολή.',
      statusSold: 'ΠΩΛΗΘΗΚΕ',
      statusArchived: 'ΑΡΧΕΙΟΘΕΤΗΜΕΝΗ',
      markSoldTitle: 'Σήμανση ως Πωλήθηκε;',
      markSoldMessage: 'Η αγγελία θα εμφανίζεται ως ΠΩΛΗΘΗΚΕ για 4 ώρες και θα αρχειοθετηθεί αυτόματα. Μπορείτε να την επαναφέρετε ανά πάσα στιγμή από το φίλτρο Αρχειοθετημένες.',
      markSoldConfirm: 'Ναι, σήμανση πωλήθηκε',
      restoreTitle: 'Επαναφορά αγγελίας;',
      restoreMessage: 'Η αγγελία θα επιστρέψει σε κατάσταση Ενεργή και θα είναι ξανά ορατή στο κοινό.',
      restoreConfirm: 'Ναι, επαναφορά',
      untitled: 'Χωρίς τίτλο',
      listingCount: '{n} αγγελία',
      listingsCount: '{n} αγγελίες',
      tipsTitle: 'Συμβουλές Αγγελίας',
      tips: [
        { title: 'Προσθέστε επαγγελματικές φωτογραφίες', desc: 'Οι αγγελίες με φωτογραφίες λαμβάνουν περισσότερα ερωτήματα.' },
        { title: 'Γράψτε λεπτομερή περιγραφή', desc: 'Αναδείξτε μοναδικά χαρακτηριστικά, κοντινές υπηρεσίες και κατάσταση.' },
        { title: 'Τιμολογήστε σωστά', desc: 'Χρησιμοποιήστε το δωρεάν εργαλείο εκτίμησής μας.' },
      ],
      deleteConfirm: 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την αγγελία; Δεν μπορεί να αναιρεθεί.',
      boostModal: {
        title: 'Προώθηση Αγγελιών σας',
        sub: 'Λάβετε προτεραιότητα στα αποτελέσματα αναζήτησης και εναλλαγή στο προβεβλημένο carousel για 14 μέρες.',
        choosePackage: 'Επιλογή Πακέτου',
        thirtyDays: '14 μέρες · έως {n} αγγελία',
        thirtyDaysPlural: '14 μέρες · έως {n} αγγελίες',
        selectListings: 'Επιλογή Αγγελιών ({selected}/{max})',
        active: '✓ Ενεργό',
        selected: '✓ Επιλεγμένο',
        select: 'Επιλογή',
        continue: 'Συνέχεια στο Checkout →',
        selectOne: 'Επιλέξτε τουλάχιστον μία αγγελία.',
      },
    },
    boostSelect: {
      title: 'Προώθηση Αγγελιών',
      sub: 'Επιλέξτε ακριβώς 1 αγγελία για προώθηση 14 ημερών.',
      loading: 'Φόρτωση αγγελιών…',
      noListings: 'Δεν υπάρχουν αγγελίες προς προώθηση',
      noListingsSub: 'Καταχωρίστε πρώτα μια αγγελία και επιστρέψτε για να την προωθήσετε.',
      noListingsCta: '+ Καταχώριση Αγγελίας',
      signInTitle: 'Συνδεθείτε για να προωθήσετε αγγελίες',
      signInSub: 'Χρειάζεστε λογαριασμό για να προωθήσετε αγγελίες στο CosmoEstates.',
      signIn: 'Σύνδεση',
      maxNotice: 'Μπορείτε να προωθήσετε 1 αγγελία κάθε φορά.',
      alreadyBoosted: 'Ήδη προωθημένο',
      selectedCount: '{n} επιλεγμένες',
      perListing: '€10.99 ανά αγγελία · 14 ημέρες',
      total: 'Σύνολο',
      boostSelected: 'Προώθηση επιλεγμένων →',
      redirecting: 'Μεταφορά στο checkout…',
      back: 'Πίσω στις Αγγελίες μου',
    },
    reset: {
      invalidLink: 'Αυτός ο σύνδεσμος επαναφοράς δεν είναι έγκυρος ή έχει λήξει.',
      minLength: 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες',
      noMatch: 'Οι κωδικοί δεν ταιριάζουν',
      success: 'Ο κωδικός ενημερώθηκε!',
      successSub: 'Μεταφορά στη σύνδεση...',
      title: 'Ορισμός νέου κωδικού',
      sub: 'Επιλέξτε έναν ισχυρό κωδικό για τον λογαριασμό σας.',
      newPassword: 'Νέος Κωδικός',
      confirmPassword: 'Επιβεβαίωση Νέου Κωδικού',
      placeholder: 'Τουλάχιστον 6 χαρακτήρες',
      confirmPlaceholder: 'Επαναλάβετε τον νέο κωδικό',
      submit: 'Ενημέρωση Κωδικού',
      requestNew: 'Αίτημα νέου συνδέσμου επαναφοράς',
    },
    verifyEmail: {
      noEmailFound: 'Δεν βρέθηκε email. Παρακαλώ εγγραφείτε ξανά.',
    },
    privacy: {
      title: 'Πολιτική Απορρήτου',
      lastUpdated: 'Τελευταία ενημέρωση: Απρίλιος 2026',
      comingSoon: 'Σύντομα διαθέσιμο.',
    },
    terms: {
      title: 'Όροι Χρήσης',
      lastUpdated: 'Τελευταία ενημέρωση: Απρίλιος 2026',
      comingSoon: 'Σύντομα διαθέσιμο.',
    },
    valuation: {
      title: 'Δωρεάν Ενδεικτική Εκτίμηση Ακινήτου',
      eyebrow: 'ΔΩΡΕΑΝ ΕΝΔΕΙΚΤΙΚΗ ΕΚΤΙΜΗΣΗ',
      subtitle: 'Δωρεάν ενδεικτική εκτίμηση τιμής για οποιοδήποτε ακίνητο στην Κύπρο. Ως αφετηρία, όχι ως επίσημη εκτίμηση.',
      trust1: '100% Δωρεάν',
      trust2: 'Βάσει Αγοράς',
      trust3: 'Άμεσα Αποτελέσματα',
      trust4: 'Χωρίς Εγγραφή',
      back: 'Πίσω',
      backHome: 'Πίσω στην Αρχική',
      continue: 'Συνέχεια',
      sending: 'Αποστολή...',
      getValuation: 'Λήψη Εκτίμησής μου',
      cityAvg: 'μέσος όρος πόλης',
      steps: ['Τύπος', 'Τοποθεσία', 'Στοιχεία', 'Κατάσταση', 'Email'],
      step1Title: 'Τι τύπο ακινήτου είναι;',
      step2Title: 'Πού βρίσκεται το ακίνητο;',
      step3Title: 'Μέγεθος και προδιαγραφές',
      step4Title: 'Ποια είναι η τρέχουσα κατάσταση;',
      step5Title: 'Πού να στείλουμε την έκθεσή σας;',
      step5Sub: 'Θα σας στείλουμε εύρος τιμής για το ακίνητό σας, την τρέχουσα τιμή ανά m² στην περιοχή σας και ένα ενδεικτικό εύρος μηνιαίου ενοικίου — ως αφετηρία, όχι ως επίσημη εκτίμηση.',
      gdpr: 'Με την υποβολή αποδέχεστε να λάβετε την εκτίμησή σας μέσω email. Σεβόμαστε το απόρρητό σας.',
      consent: {
        label: 'Επιτρέπω να προωθηθούν αυτές οι πληροφορίες σε κτηματομεσίτες',
        detail: 'Επαληθευμένοι Κύπριοι κτηματομεσίτες που εξειδικεύονται στην περιοχή σας θα επικοινωνήσουν μαζί σας εντός 48 ωρών. Μπορείτε να αλλάξετε αυτή τη ρύθμιση οποτεδήποτε στις ρυθμίσεις του προφίλ σας.',
      },
      cityLabel: 'Πόλη',
      selectCity: 'Επιλέξτε πόλη...',
      areaLabel: 'Περιοχή',
      optional: '(προαιρετικό)',
      usingCityAvg: 'Χρήση μέσου όρου {city}',
      plotSize: 'Εμβαδόν Οικοπέδου (m²)',
      coveredArea: 'Καλυπτόμενο Εμβαδόν (m²)',
      floorLabel: 'Όροφος (προαιρετικό)',
      bedroomsLabel: 'Υπνοδωμάτια',
      bathroomsLabel: 'Μπάνια',
      featuresLabel: 'Χαρακτηριστικά Ακινήτου',
      firstNameLabel: 'Όνομα',
      emailLabel: 'Διεύθυνση Email',
      resultTitle: 'Η Ενδεικτική Εκτίμησή σας Είναι Έτοιμη',
      resultBasedOn: 'Βάσει',
      salePriceEstimate: 'Εκτίμηση Τιμής Πώλησης',
      monthlyRentEstimate: 'Εκτίμηση Μηνιαίου Ενοικίου',
      perMonth: 'ανά μήνα',
      summaryProperty: 'Ακίνητο',
      summaryCity: 'Πόλη',
      summaryPlotSize: 'Εμβαδόν Οικοπέδου',
      summaryCoveredArea: 'Καλυπτόμενο Εμβαδόν',
      summaryBedrooms: 'Υπνοδωμάτια',
      summaryCondition: 'Κατάσταση',
      summaryFeatures: 'Χαρακτηριστικά',
      reportSent: 'Μια ενδεικτική εκτίμηση στάλθηκε στο',
      errors: {
        propertyType:  'Παρακαλώ επιλέξτε τύπο ακινήτου',
        city:          'Η πόλη είναι υποχρεωτική',
        plotSize:      'Το εμβαδόν οικοπέδου είναι υποχρεωτικό',
        coveredArea:   'Το καλυπτόμενο εμβαδόν είναι υποχρεωτικό',
        condition:     'Παρακαλώ επιλέξτε κατάσταση',
        firstName:     'Το όνομα είναι υποχρεωτικό',
        email:         'Το email είναι υποχρεωτικό',
        emailInvalid:  'Εισάγετε έγκυρο email',
        yearBuilt:     'Παρακαλώ επιλέξτε το έτος κατασκευής',
        titleDeed:     'Παρακαλώ επιλέξτε την κατάσταση του τίτλου ιδιοκτησίας',
        parkingType:   'Παρακαλώ επιλέξτε τον τύπο χώρου στάθμευσης',
        furnished:     'Παρακαλώ επιλέξτε την κατάσταση επίπλωσης',
        distanceToSea: 'Παρακαλώ επιλέξτε την απόσταση από τη θάλασσα',
      },
      propertyTypes: {
        Apartment: 'Διαμέρισμα',
        House: 'Κατοικία',
        Villa: 'Βίλα',
        Commercial: 'Επαγγελματικό',
        Land: 'Οικόπεδο',
      },
      conditions: {
        brandNew: 'Νεόδμητο',
        brandNewDesc: 'Νεόδμητο ή αχρησιμοποίητο',
        excellent: 'Άριστη',
        excellentDesc: 'Πρόσφατα ανακαινισμένο, χωρίς ανάγκη εργασιών',
        good: 'Καλή',
        goodDesc: 'Καλά συντηρημένο, μικρές ανάγκες',
        needsRenovation: 'Χρειάζεται Ανακαίνιση',
        needsRenovationDesc: 'Απαιτεί σημαντικές εργασίες',
      },
      features: {
        seaView: 'Θέα Θάλασσα',
        privatePool: 'Ιδιωτική Πισίνα',
        coveredParking: 'Καλυμμένος Χώρος Στάθμευσης',
        communalPool: 'Κοινόχρηστη Πισίνα',
        balconyTerrace: 'Μπαλκόνι / Βεράντα',
        airConditioning: 'Κλιματισμός',
        solarPanels: 'Ηλιακά Πάνελ',
        fireplace: 'Τζάκι',
      },
      heroEyebrow: 'ΕΡΓΑΛΕΙΟ ΑΚΙΝΗΤΩΝ ΚΥΠΡΟΥ',
      heroTitle: 'Ενδεικτική Εκτίμηση Ακινήτου',
      heroSubtitle: 'Λάβετε δωρεάν προσανατολισμό για το πιθανό εύρος τιμής του ακινήτου σας. Η εκτίμησή μας βασίζεται σε δεδομένα αλλά δεν αποτελεί επίσημη εκτίμηση. Για πιστοποιημένη εκτίμηση, ',
      requestPro: 'επικοινωνήστε με πιστοποιημένο εκτιμητή',
      reasonLabel: 'Ποιος είναι ο λόγος για την εκτίμηση;',
      reasonOptional: 'προαιρετικό',
      reasons: {
        just_exploring: 'Απλώς διερευνώ',
        thinking_about: 'Σκέφτομαι να πουλήσω',
        within_6_months: 'Εντός 6 μηνών',
        within_1_year: 'Πώληση εντός 1 έτους',
      },
      cities: {
        Limassol: 'Λεμεσός',
        Paphos: 'Πάφος',
        Nicosia: 'Λευκωσία',
        Larnaca: 'Λάρνακα',
        'Ayia Napa': 'Αγία Νάπα',
        Protaras: 'Πρωταράς',
        Paralimni: 'Παραλίμνι',
      },
      areaPlaceholder: 'π.χ. Γερμασόγεια, Mackenzie...',
      plotPlaceholder: 'π.χ. 500',
      coveredPlaceholder: 'π.χ. 120',
      landPlotPlaceholder: 'π.χ. 1000',
      firstNamePlaceholder: 'π.χ. Νικόλας',
      emailPlaceholder: 'you@example.com',
      disclaimerNote: 'Σημείωση:',
      disclaimerText: 'Αυτή είναι μια αυτοματοποιημένη εκτίμηση βάσει δεδομένων της κυπριακής αγοράς και πρόσφατων συγκρίσιμων συναλλαγών. Δεν είναι',
      disclaimerNotProfessional: 'επαγγελματική εκτίμηση',
      disclaimerTextCont: 'και δεν πρέπει να χρησιμοποιείται για νομικούς, οικονομικούς ή συναλλακτικούς σκοπούς. Για επίσημη εκτίμηση, συμβουλευτείτε πιστοποιημένο εκτιμητή RICS.',
      privacyPrefix: 'Συμφωνώ με την',
      privacyPolicy: 'Πολιτική Απορρήτου',
      privacyAnd: 'και τους',
      termsOfService: 'Όρους Χρήσης',
      privacySuffix: ' του CosmoEstates, και συναινώ στην επεξεργασία των δεδομένων μου για τη δημιουργία αυτής της ενδεικτικής εκτίμησης.',
      privacyAlert: 'Παρακαλώ συμφωνήστε με την Πολιτική Απορρήτου και τους Όρους Χρήσης για να συνεχίσετε.',
      successTitle: 'Η ενδεικτική εκτίμησή σας είναι καθ\' οδόν',
      successMsg: 'Ετοιμάζουμε την ενδεικτική εκτίμηση του ακινήτου σας και θα την αποστείλουμε στο',
      successMsgSuffix: 'εντός των επόμενων λεπτών.',
      successHint: 'Ελέγξτε τα εισερχόμενά σας (και τον φάκελο ανεπιθύμητης αλληλογραφίας). Θα λάβετε ένα εύρος τιμής πώλησης, ένα ενδεικτικό εύρος μηνιαίου ενοικίου και την τρέχουσα τιμή ανά m² στην περιοχή σας. Χρησιμοποιήστε το ως αφετηρία — ',
      successBackHome: 'Πίσω στην αρχική →',
      selectOption: 'Επιλέξτε…',
      dontKnow: 'Δεν γνωρίζω',
      improvesAccuracy: 'βελτιώνει την ακρίβεια',
      yearBuiltLabel: 'Έτος κατασκευής',
      yearBuilt: {
        opt2020newer:  '2020 ή νεότερο',
        opt2010_2019:  '2010–2019',
        opt2000_2009:  '2000–2009',
        opt1990_1999:  '1990–1999',
        optBefore1990: 'Πριν το 1990',
        optUnknown:    'Δεν γνωρίζω',
      },
      energyRatingLabel: 'Ενεργειακή κατάταξη',
      titleDeedLabel: 'Τίτλος ιδιοκτησίας',
      titleDeedTooltip: 'Ο τίτλος ιδιοκτησίας επηρεάζει σημαντικά την αξία των ακινήτων στην Κύπρο.',
      titleDeed: {
        optAvailable: 'Διαθέσιμος',
        optPending:   'Σε εκκρεμότητα',
        optNone:      'Χωρίς τίτλο',
      },
      parkingLabel: 'Χώρος στάθμευσης',
      parking: {
        optCovered: 'Καλυμμένος / Ιδιωτικός',
        optOpen:    'Ανοιχτός / Εκχωρημένος',
        optStreet:  'Μόνο επί οδού',
        optNone:    'Καθόλου',
      },
      furnishedLabel: 'Επιπλωμένο',
      furnished: {
        optFully:       'Πλήρως επιπλωμένο',
        optPartially:   'Μερικώς επιπλωμένο',
        optUnfurnished: 'Χωρίς έπιπλα',
      },
      distanceToSeaLabel: 'Απόσταση από τη θάλασσα',
      seaDistance: {
        optSeafront: 'Παραθαλάσσιο (0–100μ)',
        optWalking:  'Πεζή απόσταση (100–500μ)',
        optDrive:    'Κοντινή διαδρομή (500μ–2χλμ)',
        optInland:   'Ενδοχώρα (2χλμ+)',
      },
      step5TitleV2: 'Στοιχεία που βελτιώνουν την εκτίμηση',
      step5SubV2: 'Παραλείψτε αν δεν είστε βέβαιοι — αλλά η συμπλήρωση βελτιώνει την ακρίβεια της εκτίμησής σας.',
      step5Empty: 'Δεν ισχύουν πρόσθετα στοιχεία για αυτό το ακίνητο. Συνεχίστε για να λάβετε την εκτίμηση.',
      renovationYearLabel: 'Τελευταία ανακαίνιση',
      renovation: {
        opt2020plus:   '2020+',
        opt2015_2019:  '2015–2019',
        opt2010_2014:  '2010–2014',
        optOriginal:   'Αρχικό / χωρίς ανακαίνιση',
        optNotSure:    'Δεν είμαι σίγουρος/η',
      },
      orientationLabel: 'Κύριος προσανατολισμός',
      orientation: {
        optSouth:   'Νότος',
        optNorth:   'Βορράς',
        optEast:    'Ανατολή',
        optWest:    'Δύση',
        optCorner:  'Γωνιακό (πολλαπλός)',
        optNotSure: 'Δεν είμαι σίγουρος/η',
      },
      buildingTypeLabel: 'Τύπος κτιρίου',
      buildingType: {
        optLow:      'Χαμηλό (1–3 όροφοι)',
        optMid:      'Μεσαίο (4–8 όροφοι)',
        optHigh:     'Ψηλό (9+)',
        optBoutique: 'Boutique κτίριο (<10 μονάδες)',
      },
      confidence: {
        high:         'Υψηλή αξιοπιστία',
        medium:       'Μέτρια αξιοπιστία',
        low:          'Χαμηλή αξιοπιστία',
        estimateOnly: 'Μόνο ενδεικτική',
        subHigh:      'Βασισμένη σε ισχυρό σύνολο συγκρίσιμων ακινήτων και λεπτομερή δεδομένα.',
        subMedium:    'Βασισμένη σε αξιόπιστα δεδομένα αγοράς. Για μεγαλύτερη ακρίβεια, συμπληρώστε τα προαιρετικά πεδία του Βήματος 5.',
        subLow:       'Περιορισμένα συγκρίσιμα στην περιοχή — θεωρήστε την ενδεικτική.',
        subEstimate:  'Δεν υπάρχουν αρκετά δεδομένα στην περιοχή για μια εκτίμηση βασισμένη σε δεδομένα.',
      },
      result: {
        eyebrow:            'Η ΕΚΤΙΜΗΣΗ ΣΑΣ',
        title:              'Ενδεικτική τιμή ακινήτου',
        saleRange:          'Ενδεικτικό εύρος πώλησης',
        rentRange:          'Ενδεικτικό μηνιαίο ενοίκιο',
        perMonth:           ' /μήνα',
        pricePerSqm:        'Τρέχουσα τιμή ανά m²',
        howCalculated:      'Πώς υπολογίσαμε αυτή την εκτίμηση',
        baseLabel:          'Βάση ανά m² ({source})',
        basedOnN:           'Βασισμένη σε {n} πρόσφατες αγγελίες',
        totalAdjustment:    'Συνολική προσαρμογή',
        cappedNote:         'Η συνολική προσαρμογή περιορίστηκε από {raw} για να παραμείνει εντός ασφαλών ορίων.',
        comparablesTitle:   'Παρόμοιες αγγελίες που χρησιμοποιήσαμε ως αναφορά',
        comparablesLoading: 'Φόρτωση παρόμοιων αγγελιών…',
        comparablesEmpty:   'Δεν έχουμε ακόμη αρκετές αγγελίες στην περιοχή σας για να δείξουμε συγκρίσιμα.',
        findAgentCTA:       'Επικοινωνήστε με πιστοποιημένο agent',
        newEstimate:        'Νέα εκτίμηση',
        legal:              'Η εκτίμηση βασίζεται σε δεδομένα αλλά δεν αποτελεί επίσημη εκτίμηση. Για πιστοποιημένη εκτίμηση, επικοινωνήστε με πιστοποιημένο εκτιμητή.',
      },
    },
    findAgent: {
      stepIndicator: 'Βήμα {step} από {total}',
      continue: 'Συνέχεια →',
      back: '← Πίσω',
      finding: 'Αναζήτηση agents...',
      findMyAgents: 'Βρες τους Agents μου',
      errorGeneric: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.',
      step1Title: 'Τι ψάχνεις;',
      step1Sub: 'Ας βρούμε τον κατάλληλο agent για την περίπτωσή σου.',
      lookingFor: {
        sell: 'Πώληση του ακινήτου μου',
        sellSub: 'Καταχώρηση και εύρεση κατάλληλου αγοραστή',
        buy: 'Αγορά ακινήτου',
        buySub: 'Βρες το επόμενο σπίτι σου',
        rent: 'Ενοικίαση ακινήτου',
        rentSub: 'Βρες μέρος να μείνεις',
        rent_out: 'Ενοικίαση του ακινήτου μου',
        rent_outSub: 'Βρες αξιόπιστους ενοικιαστές',
      },
      step2Title: 'Πες μας για το ακίνητο',
      step2Sub: 'Μόνο τα βασικά — οι agents που θα ταιριάξεις θα διευκρινίσουν τις λεπτομέρειες.',
      propertyTypeLabel: 'Τύπος ακινήτου',
      propertyTypes: {
        apartment: 'Διαμέρισμα',
        house: 'Κατοικία',
        villa: 'Βίλα',
        land: 'Οικόπεδο',
        commercial: 'Επαγγελματικό',
      },
      cityLabel: 'Πόλη',
      selectCity: 'Επιλέξτε πόλη',
      districtLabel: 'Περιοχή',
      districtPlaceholder: 'π.χ. Γερμασόγεια',
      optional: '(προαιρετικό)',
      bedroomsLabel: 'Υπνοδωμάτια',
      monthlyBudget: 'Μηνιαίος προϋπολογισμός (EUR)',
      budgetRange: 'Εύρος προϋπολογισμού (EUR)',
      fromPlaceholder: 'Από',
      toPlaceholder: 'Έως',
      step3TitleSell: 'Πες μας για την κατάστασή σου',
      step3TitleBuy: 'Λίγα ακόμη για εσένα',
      step3SubSell: 'Αυτό βοηθάει τους agents να προετοιμάσουν τη σωστή προσέγγιση.',
      step3SubBuy: 'Αυτό μας βοηθάει να επιλέξουμε τους καλύτερους agents για σένα.',
      situationLabel: 'Ποιο σε περιγράφει καλύτερα;',
      timeframeSellLabel: 'Πότε θέλεις να βγεις στην αγορά;',
      timeframeBuyLabel: 'Πότε ψάχνεις να μετακομίσεις;',
      situations: {
        sell: {
          specific_timeline: 'Πρέπει να πουλήσω εντός συγκεκριμένου χρονοδιαγράμματος',
          exploring: 'Εξερευνώ τις επιλογές μου',
          inheritance: 'Έχω κληρονομήσει το ακίνητο',
          relocate: 'Θέλω να μετακομίσω',
        },
        buy: {
          first_time: 'Είμαι πρωτοαγοραστής',
          investor: 'Επενδύω για εισόδημα από ενοίκια',
          relocating_to_cyprus: 'Μετακομίζω στην Κύπρο',
          golden_visa: 'Αγορά για άδεια διαμονής / golden visa',
        },
        rent: {
          short_term: 'Βραχυπρόθεσμα (κάτω από 6 μήνες)',
          long_term: 'Μακροπρόθεσμα (1 έτος ή περισσότερο)',
          relocating_to_cyprus: 'Μετακομίζω στην Κύπρο',
          students_or_family: 'Ενοίκιο με οικογένεια ή φοιτητές',
        },
        rent_out: {
          short_term_rental: 'Βραχυχρόνια ενοικίαση (Airbnb)',
          long_term_rental: 'Εύρεση μακροχρόνιων ενοικιαστών',
          need_management: 'Χρειάζομαι πλήρη διαχείριση ακινήτου',
          just_tenant_matching: 'Μόνο εύρεση ενοικιαστή, εγώ διαχειρίζομαι',
        },
      },
      timeframesSell: {
        immediate: 'Το συντομότερο δυνατό',
        within_3_months: 'Εντός 3 μηνών',
        within_6_months: 'Εντός 6 μηνών',
        within_12_months: 'Εντός 12 μηνών',
        within_24_months: 'Εντός 24 μηνών',
        just_exploring: 'Είμαι ευέλικτος',
      },
      moveIn: {
        asap: 'Το συντομότερο δυνατό',
        within_6_months: 'Εντός 6 μηνών',
        within_12_months: 'Εντός 12 μηνών',
        within_24_months: 'Εντός 24 μηνών',
        flexible: 'Είμαι ευέλικτος',
      },
      step4Title: 'Τι είναι το πιο σημαντικό για σένα;',
      step4Sub: 'Επίλεξε αυτό που μετράει περισσότερο.',
      priorities: {
        sell: {
          best_price: 'Καλύτερη τιμή, ακόμα κι αν χρειαστεί χρόνος',
          fast_sale: 'Γρήγορη πώληση, ακόμα κι αν χάσω λίγα χρήματα',
          full_process: 'Ο agent αναλαμβάνει πλήρως τη διαδικασία',
          language: 'Επικοινωνία στη γλώσσα μου',
          privacy: 'Διακριτική και χαμηλών τόνων πώληση',
        },
        buy: {
          best_location: 'Εύρεση της ιδανικής τοποθεσίας',
          best_value: 'Καλύτερη σχέση ποιότητας/τιμής',
          exclusive: 'Πρόσβαση σε αποκλειστικές, εκτός αγοράς αγγελίες',
          language: 'Επικοινωνία στη γλώσσα μου',
          full_process: 'Ο agent αναλαμβάνει χαρτιά και νομικά',
        },
        rent: {
          best_location: 'Εύρεση της ιδανικής τοποθεσίας',
          best_value: 'Καλύτερη σχέση ποιότητας/τιμής για τον προϋπολογισμό',
          fast_response: 'Γρήγορες απαντήσεις και άμεσες επισκέψεις',
          language: 'Επικοινωνία στη γλώσσα μου',
          furnished_ready: 'Επιπλωμένο και έτοιμο για μετακόμιση',
        },
        rent_out: {
          vetted_tenants: 'Αξιολογημένοι, αξιόπιστοι ενοικιαστές',
          max_yield: 'Μεγιστοποίηση απόδοσης ενοικίου',
          full_management: 'Χωρίς ασχολία — πλήρης διαχείριση',
          language: 'Επικοινωνία στη γλώσσα μου',
          fast_fill: 'Όσο πιο γρήγορη πλήρωση του κενού γίνεται',
        },
      },
      step5Title: 'Πώς μπορούν να επικοινωνήσουν οι agents μαζί σου;',
      step5Sub: '3 agents που ταίριαξαν θα επικοινωνήσουν εντός 24-48 ωρών.',
      fullNameLabel: 'Ονοματεπώνυμο',
      fullNamePlaceholder: 'Το όνομά σου',
      emailLabel: 'Email',
      emailPlaceholder: 'you@email.com',
      phoneLabel: 'Τηλέφωνο',
      phonePlaceholder: '+357 99 123456',
      contactMethodLabel: 'Τρόπος επικοινωνίας',
      contactMethods: {
        email: 'Email',
        call: 'Κλήση',
        whatsapp: 'WhatsApp',
      },
      consentLabel: 'Επιτρέπω την κοινοποίηση των στοιχείων επικοινωνίας μου σε έως 3 πιστοποιημένους κτηματομεσίτες που ταιριάζουν στις απαιτήσεις μου',
      success: {
        headline: 'Έτοιμα όλα!',
        sublinePrefix: 'Σε αντιστοιχίσαμε με',
        sublineMid: 'κορυφαίους agents στην/στο',
        sublineSuffix: 'Θα επικοινωνήσουν μαζί σου εντός 24-48 ωρών με την προτεινόμενη προσέγγισή τους.',
        yourArea: 'περιοχή σου',
        step1Title: 'Οι agents αξιολογούν τα στοιχεία σου',
        step1Text: 'Κάθε agent προετοιμάζει την εξατομικευμένη πρότασή του.',
        step2Title: 'Λαμβάνεις τις προσφορές τους',
        step2Text: 'Εντός 24-48 ωρών μέσω της προτιμώμενης μεθόδου επικοινωνίας.',
        step3Title: 'Σύγκρινε και επίλεξε',
        step3Text: 'Διάλεξε τον agent που σου ταιριάζει — ή κανέναν, αν προτιμάς να περιμένεις.',
        matchedHeading: 'Οι agents που ταιριάζουν',
        loading: 'Αναζητούμε τους καλύτερους agents για σένα...',
        emptyPrefix: 'Σε αντιστοιχίζουμε προσωπικά με agents στην/στο',
        emptySuffix: 'Θα έχετε νέα μας εντός 48 ωρών.',
        backHome: 'Πίσω στην αρχική',
      },
    },
    contact: {
      tag: 'Επικοινωνία',
      title: 'Επικοινωνήστε μαζί μας',
      sub: 'Έχεις ερώτηση, βρήκες σφάλμα ή θέλεις να μοιραστείς μια ιδέα; Διαβάζουμε κάθε μήνυμα.',
      categoryLabel: 'Θέμα επικοινωνίας',
      category: {
        support: 'Υποστήριξη',
        tips: 'Προτάσεις',
        bug: 'Αναφορά Σφάλματος',
        improvement: 'Βελτίωση',
      },
      nameLabel: 'Ονοματεπώνυμο',
      namePlaceholder: 'π.χ. Μαρία Ιωάννου',
      emailLabel: 'Email σας',
      emailPlaceholder: 'π.χ. maria@example.com',
      messageLabel: 'Μήνυμα',
      messagePlaceholder: 'Γράψτε όσα θέλετε…',
      submit: 'Αποστολή',
      sending: 'Αποστολή…',
      errorMsg: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.',
      successTitle: 'Το μήνυμα στάλθηκε!',
      successSub: 'Ευχαριστούμε — θα επικοινωνήσουμε σύντομα.',
      sendAnother: 'Αποστολή άλλου μηνύματος',
    },
    about: {
      tag: 'Ποιοι Είμαστε',
      title: 'Η νέα αγορά',
      titleHighlight: 'ακινήτων της Κύπρου',
      sub: 'Φτιαγμένο για Αγοραστές, Πωλητές, Μεσίτες και Εταιρείες.',
      missionLabel: 'Η Αποστολή μας',
      missionTitle: 'Προσβάσιμη αγορά ακινήτων για όλους στην Κύπρο',
      missionText: 'Δημιουργήσαμε το CosmoEstates για να κάνουμε την αναζήτηση νέου σπιτιού στην Κύπρο εύκολη και αποτελεσματική. Πώς το εξασφαλίζουμε; Ενσωματώνοντας εργαλεία τεχνητής νοημοσύνης, έξυπνη αναζήτηση και περισσότερους τρόπους για να είστε καλά πληροφορημένοι για την αγορά ακινήτων.',
      statsListings: 'Ενεργές αγγελίες',
      statsUsers: 'Χρήστες',
      statsAI: 'Έξυπνες πληροφορίες',
      statsTools: 'Μοναδικά Pro Εργαλεία',
      valuesTitle: 'Τι μας κινεί',
      value1Title: 'Διαφάνεια',
      value1: 'Πραγματικές τιμές, πραγματικά δεδομένα, χωρίς κρυφές χρεώσεις. Ό,τι βλέπετε είναι αυτό που παίρνετε.',
      value2Title: 'Νοημοσύνη',
      value2: 'Πληροφορίες γειτονιάς με τεχνητή νοημοσύνη και δεδομένα αγοράς για σίγουρες αποφάσεις.',
      value3Title: 'Κοινότητα',
      value3: 'Συνδέουμε αγοραστές, πωλητές και μεσίτες σε όλη την Κύπρο σε μία αξιόπιστη πλατφόρμα.',
      ctaTitle: 'Έτοιμοι να βρείτε το σπίτι σας στην Κύπρο;',
      ctaSub: 'Εξερευνήστε εκατοντάδες ακίνητα σε όλη την Κύπρο.',
      ctaBtn: 'Δείτε Αγγελίες',
      diffSectionTitle: 'Τι μας ξεχωρίζει',
      diff1Title: 'Εργαλεία για πιο εύκολες αποφάσεις',
      diff1Body: 'Εκτιμήσεις κόστους ανακαίνισης, βαθμολογίες γειτονιάς από δημόσιες πηγές και συγκριτικά στοιχεία τιμών — φτιαγμένα για να δίνουν στους αγοραστές πλαίσιο, όχι μόνο αγγελίες.',
      diff2Title: 'Βρείτε επαληθευμένους μεσίτες',
      diff2Body: 'Οι μεσίτες ελέγχονται πριν μπουν στην πλατφόρμα. Λάβετε προσφορές από μεσίτες για να σας βοηθήσουν να αγοράσετε, να πουλήσετε, να βρείτε ενοικίαση ή να ενοικιάσετε το ακίνητό σας.',
      diff3Title: 'Αγγελίες υψηλής ποιότητας',
      diff3Body: 'Οι αγγελίες ελέγχονται για να αποφεύγονται spam και χαμηλής ποιότητας καταχωρίσεις.',
      diff4Title: 'Φτιαγμένο για την Κύπρο',
      diff4Body: 'Σχεδιασμένο ειδικά για την αγορά ακινήτων της Κύπρου.',
      coverageSectionTitle: 'Τι καλύπτουμε',
      coverageDistricts: 'Επαρχίες που καλύπτουμε',
      coverageListings: 'Αγγελίες στην πλατφόρμα',
      coverageFounded: 'Έτος ίδρυσης',
      getStartedTitle: 'Ξεκινήστε',
      ctaBuyersHeading: 'Αγοραστές',
      ctaBuyersBody: 'Εξερευνήστε αγγελίες σε όλη την Κύπρο με δεδομένα γειτονιάς από αξιόπιστες πηγές.',
      ctaBuyersBtn: 'Δείτε αγγελίες →',
      ctaSellersHeading: 'Πωλητές',
      ctaSellersBody: 'Καταχωρίστε το ακίνητό σας και φτάστε σε σοβαρούς αγοραστές.',
      ctaSellersBtn: 'Καταχωρίστε ακίνητο →',
      ctaAgentsHeading: 'Μεσίτες',
      ctaAgentsBody: 'Γίνετε μέλος της πλατφόρμας ως επαληθευμένος μεσίτης. Συνδεθείτε με ποιοτικούς αγοραστές.',
      ctaAgentsBtn: 'Κάντε αίτηση ως μεσίτης →',
    },
    listProperty: {
      title: 'Καταχώριση Ακινήτου',
      sub: 'Συμπληρώστε τα παρακάτω στοιχεία για να δημοσιεύσετε την αγγελία σας.',
      editTitle: 'Επεξεργασία Αγγελίας',
      editSub: 'Ενημερώστε τα στοιχεία του ακινήτου σας παρακάτω.',
      signInTitle: 'Συνδεθείτε για να καταχωρίσετε το ακίνητό σας',
      signInSub: 'Χρειάζεστε λογαριασμό για να δημοσιεύσετε αγγελία στο CosmoEstates.',
      editSignInTitle: 'Συνδεθείτε για επεξεργασία αγγελιών',
      loadingListing: 'Φόρτωση αγγελίας...',
      signIn: 'Σύνδεση',
      register: 'Εγγραφή',
      steps: ['Ακίνητο', 'Τοποθεσία', 'Λεπτομέρειες', 'Επαφή', 'Προεπισκόπηση'],
      step1Title: 'Στοιχεία Ακινήτου',
      step1Desc: 'Ξεκινήστε με τα βασικά — τι καταχωρίζετε και τι το ξεχωρίζει. Όσο πιο σαφείς είστε, τόσο πιο σχετικούς αγοραστές θα σας στείλουμε.',
      step1TipsLabel: 'Τι να γράψετε',
      step1Tip1: 'Δώστε συγκεκριμένο τίτλο — οι αγοραστές σαρώνουν τα αποτελέσματα σε δευτερόλεπτα',
      step1Tip2: 'Αναφέρετε την πόλη ή τη γειτονιά για άμεσο πλαίσιο',
      step1Tip3: 'Αποφύγετε κεφαλαία, emoji ή γλώσσα πωλήσεων',
      step2Title: 'Τοποθεσία',
      step2Desc: 'Η τοποθεσία είναι ο σημαντικότερος παράγοντας στην αναζήτηση ακινήτων. Οι αγοραστές φιλτράρουν πρώτα ανά πόλη και μετά εμβαθύνουν σε συγκεκριμένες γειτονιές. Όσο πιο συγκεκριμένα, τόσο καλύτερα στοχευμένα τα ενδιαφέροντα.',
      step2TipsLabel: 'Σχετικά με τη διεύθυνση',
      step2Tip1: 'Η πλήρης διεύθυνση παραμένει ιδιωτική μέχρι κάποιος αγοραστής να σας στείλει μήνυμα',
      step2Tip2: 'Η αγγελία σας εμφανίζεται στον χάρτη της Κύπρου με βάση την πόλη',
      step2Tip3: 'Οι συγκεκριμένες γειτονιές βοηθούν τους αγοραστές να σας βρουν στα φίλτρα',
      step3Title: 'Περιγραφή και Χαρακτηριστικά',
      step3Desc: 'Μια ζωντανή περιγραφή και εξαιρετικές φωτογραφίες κάνουν τους αγοραστές να σταματήσουν το scrolling. Ξεκινήστε με το πιο ξεχωριστό χαρακτηριστικό — τη θέα στη θάλασσα, την ανακαινισμένη κουζίνα, τη βεράντα στη ταράτσα.',
      step3TipsLabel: 'Φωτογραφίες και περιγραφή',
      step3Tip1: 'Φωτογραφίστε με φως ημέρας, με ανοιχτά παράθυρα και περσίδες',
      step3Tip2: 'Η πρώτη φωτογραφία γίνεται η εικόνα εξωφύλλου της αγγελίας',
      step3Tip3: 'Αναφέρετε κοντινές παραλίες, σχολεία ή συγκοινωνίες',
      step4Title: 'Στοιχεία Επικοινωνίας',
      step4Desc: 'Οι αγοραστές επικοινωνούν μαζί σας πρώτα μέσω των μηνυμάτων του CosmoEstates — στέλνετε και λαμβάνετε μηνύματα με τον δικό σας ρυθμό.',
      step4TipsLabel: 'Πώς λειτουργεί η επικοινωνία',
      step4Tip1: 'Οι αγοραστές επικοινωνούν μαζί σας πρώτα μέσω των μηνυμάτων του CosmoEstates',
      step4Tip2: 'Μπορείτε να απαντάτε με τον δικό σας ρυθμό από τα εισερχόμενά σας',
      step5Title: 'Προεπισκόπηση και Επιβεβαίωση',
      step5Sub: 'Ελέγξτε την αγγελία σας παρακάτω. Κάντε κλικ σε οποιαδήποτε ενότητα για επεξεργασία.',
      editStep5Sub: 'Ελέγξτε τις αλλαγές σας. Κάντε κλικ σε οποιαδήποτε ενότητα για επεξεργασία.',
      forSale: 'Προς Πώληση',
      forRent: 'Προς Ενοικίαση',
      propertyType: 'Τύπος Ακινήτου',
      selectType: 'Επιλέξτε τύπο...',
      listingTitle: 'Τίτλος Αγγελίας',
      monthlyRent: 'Μηνιαίο Ενοίκιο (EUR)',
      askingPrice: 'Ζητούμενη Τιμή (EUR)',
      city: 'Πόλη',
      regionDistrict: 'Περιοχή / Δήμος',
      fullAddress: 'Πλήρης Διεύθυνση',
      plotSize: 'Εμβαδόν Οικοπέδου (m²)',
      coveredArea: 'Καλυπτόμενο Εμβαδόν (m²)',
      bedrooms: 'Υπνοδωμάτια',
      bathrooms: 'Μπάνια',
      select: 'Επιλέξτε...',
      description: 'Περιγραφή',
      keyFeatures: 'Βασικά Χαρακτηριστικά',
      commaSeparated: '(χωρισμένα με κόμμα)',
      photos: 'Φωτογραφίες',
      photosUpTo: '(έως 10)',
      addPhotos: 'Προσθήκη φωτογραφιών',
      addMore: '+ Προσθήκη',
      mainPhoto: 'Κύρια',
      yourName: 'Το Όνομά σας',
      fullName: 'Πλήρες όνομα',
      phoneNumber: 'Αριθμός Τηλεφώνου',
      emailAddress: 'Διεύθυνση Email',
      agencyName: 'Όνομα Εταιρείας',
      optional: '(προαιρετικό)',
      edit: 'Επεξεργασία',
      back: 'Πίσω',
      continue: 'Συνέχεια',
      submitting: 'Υποβολή...',
      submitListing: 'Υποβολή Αγγελίας',
      saving: 'Αποθήκευση...',
      saveChanges: 'Αποθήκευση Αλλαγών',
      successTitle: 'Η Αγγελία Υποβλήθηκε!',
      successSub: 'Το ακίνητό σας είναι πλέον ζωντανό στο CosmoEstates.',
      editSuccessTitle: 'Η Αγγελία Ενημερώθηκε!',
      editSuccessSub: 'Οι αλλαγές σας αποθηκεύτηκαν επιτυχώς.',
      backToMyListings: 'Πίσω στις Αγγελίες μου',
      viewListing: 'Προβολή Αγγελίας',
      boostTitle: 'Θέλετε περισσότερη προβολή;',
      boostSub: 'Προωθήστε την αγγελία σας για προτεραιότητα στα αποτελέσματα και εναλλαγή στο προβεβλημένο carousel για 14 μέρες.',
      boostCta: 'Προώθηση Αγγελίας',
      skipForNow: 'Παράλειψη προς το παρόν',
      previewType: 'Τύπος',
      previewTitle: 'Τίτλος',
      previewPrice: 'Τιμή',
      previewAddress: 'Διεύθυνση',
      previewPlotSize: 'Εμβαδόν Οικοπέδου',
      previewCoveredArea: 'Καλυπτόμενο Εμβαδόν',
      previewBedsBaths: 'Υπνοδ. / Μπάνια',
      previewPhotos: 'Φωτογραφίες',
      previewName: 'Όνομα',
      previewPhone: 'Τηλέφωνο',
      previewEmail: 'Email',
      previewAgency: 'Εταιρεία',
      previewSectionProperty: 'Στοιχεία Ακινήτου',
      previewSectionLocation: 'Τοποθεσία',
      previewSectionDetails: 'Περιγραφή και Χαρακτηριστικά',
      previewSectionContact: 'Επικοινωνία',
      photoSingular: 'φωτογραφία',
      photoPlural: 'φωτογραφίες',
      bed: 'υπν.',
      bath: 'μπ.',
      errors: {
        propertyType: 'Επιλέξτε τύπο ακινήτου',
        title: 'Ο τίτλος είναι υποχρεωτικός',
        titleShort: 'Εισάγετε έναν κατάλληλο τίτλο (τουλάχιστον {min} χαρακτήρες).',
        titleGibberish: 'Εισάγετε έναν πραγματικό τίτλο — αυτό μοιάζει με κείμενο γεμίσματος.',
        price: 'Η τιμή είναι υποχρεωτική',
        city: 'Η πόλη είναι υποχρεωτική',
        address: 'Η διεύθυνση είναι υποχρεωτική',
        bedrooms: 'Ο αριθμός υπνοδωματίων είναι υποχρεωτικός',
        bathrooms: 'Ο αριθμός μπάνιων είναι υποχρεωτικός',
        size: 'Το εμβαδόν κτιρίου είναι υποχρεωτικό',
        plotSize: 'Το εμβαδόν οικοπέδου είναι υποχρεωτικό',
        description: 'Η περιγραφή είναι υποχρεωτική',
        descriptionShort: 'Η περιγραφή χρειάζεται τουλάχιστον {min} χαρακτήρες (τώρα {have}).',
        descriptionGibberish: 'Γράψτε μια πραγματική περιγραφή — αυτό μοιάζει με κείμενο γεμίσματος.',
        photos: 'Προσθέστε τουλάχιστον {min} φωτογραφίες για να δημοσιεύσετε την αγγελία σας.',
        photosNeed: 'Προσθέστε {missing} ακόμη φωτογραφία{plural} για δημοσίευση ({have} από {min}).',
        contactName: 'Το όνομα είναι υποχρεωτικό',
        contactPhone: 'Το τηλέφωνο είναι υποχρεωτικό',
        contactEmail: 'Απαιτείται έγκυρο email',
      },
    },
    categoryGrid: {
      title: 'Αναζήτηση κατά',
      titleHighlight: 'Κατηγορία',
      sub: 'Εξερεύνηση κάθε τύπου ακινήτου σε ένα μέρος',
      viewAll: 'Όλες οι κατηγορίες →',
      listingsSuffix: 'αγγελίες',
    },
    findBroker: {
      eyebrow: 'ΠΙΣΤΟΠΟΙΗΜΕΝΟΙ ΕΠΑΓΓΕΛΜΑΤΙΕΣ',
      trustVerified: 'Πιστοποιημένο CREAA',
      trustResponse: 'Γρήγορη Ανταπόκριση',
      trustFree: 'Δωρεάν Επικοινωνία',
      title: 'Εύρεση Agent',
      sub: 'Συνδεθείτε με πιστοποιημένους επαγγελματίες ακινήτων σε όλη την Κύπρο.',
      applyAsBroker: 'Αίτηση ως Agent',
      loading: 'Φόρτωση agents...',
      noVerified: 'Δεν υπάρχουν πιστοποιημένοι agents ακόμα',
      noVerifiedSub: 'Εντάσσουμε τους πρώτους agents μας. Ελέγξτε ξανά σύντομα.',
      contact: 'Επικοινωνία',
      modal: {
        eyebrow: 'COSMOESTATES',
        title: 'Αίτηση ως Agent',
        sub: 'Συμπληρώστε τα στοιχεία σας και θα αξιολογήσουμε την αίτησή σας.',
        fullName: 'Πλήρες Όνομα',
        agencyName: 'Όνομα Εταιρείας',
        phone: 'Αριθμός Τηλεφώνου',
        email: 'Διεύθυνση Email',
        etek: 'Αριθμός ΕΤΕΚ (προαιρετικό)',
        speciality: 'Ειδικότητα (προαιρετικό)',
        languages: 'Γλώσσες',
        city: 'Πόλη',
        selectCity: 'Επιλέξτε πόλη...',
        description: 'Σχετικά με εσάς',
        descriptionPlaceholder: 'Πείτε στους πελάτες για την εμπειρία σας, τα αποτελέσματά σας και τι σας κάνει να ξεχωρίζετε...',
        submit: 'Υποβολή Αίτησης',
        successTitle: 'Η Αίτηση Υποβλήθηκε!',
        successSub: 'Θα αξιολογήσουμε την αίτησή σας και θα επικοινωνήσουμε μαζί σας εντός 2 εργάσιμων ημερών.',
        done: 'Τέλος',
      },
      errors: {
        required: 'Υποχρεωτικό',
        validEmail: 'Απαιτείται έγκυρο email',
      },
      viewProfile: 'Δείτε Προφίλ',
    },
    brokerProfile: {
      back: 'Πίσω στους Agents',
      verified: 'Επαληθευμένος Agent',
      call: 'Κλήση',
      emailBtn: 'Email',
      about: 'Σχετικά',
      speciality: 'Ειδικότητα',
      languages: 'Γλώσσες',
      etek: 'ΕΤΕΚ',
      location: 'Βάση εργασίας',
      notFound: 'Ο agent δεν βρέθηκε.',
      noBio: 'Δεν υπάρχει βιογραφικό.',
      noSpeciality: 'Γενική Πρακτική',
      editProfile: 'Επεξεργασία Προφίλ',
      saveChanges: 'Αποθήκευση',
      saving: 'Αποθήκευση...',
      saved: 'Το προφίλ ενημερώθηκε',
      cancelEdit: 'Ακύρωση',
      editPhone: 'Αριθμός Τηλεφώνου',
      editDescription: 'Σχετικά με εσάς',
      editDescPlaceholder: 'Πείτε στους πελάτες για την εμπειρία σας και τι σας κάνει να ξεχωρίζετε...',
      editSpeciality: 'Ειδικότητα',
      editSpecPlaceholder: 'π.χ. Πολυτελείς Βίλες, Εμπορικά',
      editLanguages: 'Γλώσσες',
      editLangPlaceholder: 'π.χ. Αγγλικά, Ελληνικά, Ρωσικά',
      yourListings: 'Ενεργές Αγγελίες σας',
      noListings: 'Δεν υπάρχουν ενεργές αγγελίες ακόμα.',
    },
    supportChat: {
      initialMessage: 'Γεια! Είμαι ο βοηθός σας στο CosmoEstates. Ρωτήστε με οτιδήποτε σχετικά με αγορά, πώληση ή ενοικίαση ακινήτου στην Κύπρο — είμαι διαθέσιμος 24/7.',
      headerName: 'Υποστήριξη CosmoEstates',
      headerStatus: 'AI Βοηθός · Πάντα διαθέσιμος',
      placeholder: 'Ρωτήστε οτιδήποτε για ακίνητα στην Κύπρο...',
      quickReplies: [
        'Πώς αγοράζω ακίνητο στην Κύπρο;',
        'Καλύτερες περιοχές για expats;',
        'Πώς λειτουργεί η εκτίμηση;',
        'Τι είναι τα τέλη μεταβίβασης στην Κύπρο;',
      ],
    },
    boostCheckout: {
      back: 'Πίσω',
      heroEyebrow: 'Η ΕΝΙΣΧΥΣΗ ΣΑΣ',
      heroTitle: 'Αυξήστε την προβολή της αγγελίας σας',
      heroSub: 'Ασφαλές checkout μέσω Stripe · Εφάπαξ πληρωμή, χωρίς συνδρομή',
      formTitle: 'Επιλέξτε πακέτο',
      formSubtitle: 'Ενισχύστε μία αγγελία',
      title: 'Προωθήστε την Αγγελία σας',
      sub: 'Προτεραιότητα στην αναζήτηση & εναλλαγή στο προβεβλημένο carousel για 14 μέρες',
      emailLabel: 'Διεύθυνση email',
      included: 'Όλα συμπεριλαμβάνονται:',
      redirecting: 'Ανακατεύθυνση στο Stripe…',
      pay: 'Πληρωμή €{price} — Ασφαλής Checkout',
      ssl: '🔒 256-bit SSL · Με τεχνολογία Stripe',
      oneTime: 'εφάπαξ',
      thirtyDayUpTo: '14 μέρες · έως {n} {unit}',
      listingUnit: 'αγγελία',
      listingsUnit: 'αγγελίες',
      orderSummary: 'Σύνοψη Παραγγελίας',
      listingBoost: 'Προώθηση Αγγελίας',
      duration: 'Διάρκεια',
      thirtyDays: '14 μέρες',
      listings: 'Αγγελίες',
      upTo: 'Έως',
      priceVat: 'Τιμή (συμπ. ΦΠΑ)',
      total: 'Σύνολο',
      termsNote: 'Εφάπαξ πληρωμή. Χωρίς συνδρομή.',
      features: [
        {
          title: 'Καλύτερη τοποθέτηση',
          desc: 'Οι ενισχυμένες αγγελίες εμφανίζονται πάνω από τις τυπικές στα αποτελέσματα αναζήτησης, δίνοντας στο ακίνητό σας σαφές πλεονέκτημα προβολής.',
        },
        {
          title: 'Εναλλαγή στο προβεβλημένο carousel',
          desc: 'Η αγγελία σας εναλλάσσεται στο carousel Προβεβλημένων Αγγελιών της αρχικής — οι ενισχυμένες αγγελίες λαμβάνουν προτεραιότητα όταν επισκέπτονται αγοραστές.',
        },
      ],
    },
    mortgage: {
      back: 'Πίσω',
      title: 'Εργαλεία Δανείου',
      heroTitle: 'Οικονομική Πυξίδα',
      sub: 'Υπολογίστε δόσεις και δείτε πόσα εξοικονομείτε με υπερπληρωμή.',
      tabMortgage: 'Υπολογιστής Δανείου',
      tabAffordability: 'Έλεγχος Δυνατότητας',
      tabOverpayment: 'Υπολογιστής Υπερπληρωμής',
      yourDetails: 'Τα Στοιχεία σας',
      propPrice: 'Τιμή Ακινήτου',
      deposit: 'Προκαταβολή',
      loanAmtHint: 'Ποσό δανείου:',
      interestRate: 'Επιτόκιο (ετήσιο)',
      interestRateInfo: 'Το ετήσιο επιτόκιο της τράπεζάς σας. Στην Κύπρο, τα τυπικά επιτόκια κυμαίνονται από 3,5% έως 5,5% για πρώτη κατοικία.',
      mortgageTerm: 'Διάρκεια Δανείου',
      years: 'έτη',
      monthlyPayment: 'Μηνιαία Δόση',
      perMonthFor: 'ανά μήνα για',
      loanAmount: 'Ποσό Δανείου',
      totalInterest: 'Συνολικοί Τόκοι',
      totalRepayment: 'Συνολική Αποπληρωμή',
      downloadReport: 'Αναφορά Δανείου',
      generatingPdf: 'Δημιουργία PDF…',
      proOnly: 'Διαθέσιμο για συνδρομητές Pro',
      repaymentBreakdown: 'Ανάλυση Αποπληρωμής',
      principal: 'Κεφάλαιο',
      interest: 'Τόκος',
      monthlyLabel: 'Μηνιαία',
      perMonth: '€ / μήνα',
      yearByYear: 'Ετήσιες Αποπληρωμές',
      year: 'Έτος',
      yourFinances: 'Τα Οικονομικά σας',
      monthlyIncome: 'Μηνιαίο Καθαρό Εισόδημα',
      monthlyIncomeInfo: 'Το εισόδημά σας μετά φόρων κάθε μήνα.',
      monthlyExpenses: 'Μηνιαία Έξοδα',
      disposableIncome: 'Διαθέσιμο εισόδημα:',
      monthlyExpensesInfo: 'Τακτικά έξοδα — ενοίκιο, δάνεια, συνδρομές, έξοδα διαβίωσης.',
      availableDeposit: 'Διαθέσιμη Προκαταβολή',
      whatCanAfford: 'Τι Μπορείτε να Αντέξετε',
      maxPropPrice: 'Μέγιστη Τιμή Ακινήτου',
      basedOnDTI: 'βάσει αναλογίας χρέους-εισοδήματος {pct}%',
      maxMonthly: 'Μέγιστη Μηνιαία Δόση',
      pctOfDisposable: '35% του διαθέσιμου εισοδήματος',
      maxLoan: 'Μέγιστο Δάνειο',
      yourDeposit: 'Η Προκαταβολή σας',
      depositPct: 'Ποσοστό Προκαταβολής',
      incomeAlloc: 'Κατανομή Εισοδήματος',
      mortgageLabel: 'Δάνειο',
      expensesLabel: 'Έξοδα',
      remainingLabel: 'Υπόλοιπο',
      affordNote: 'Βάσει κριτηρίων κυπριακών τραπεζών: μέγιστο 35% καθαρού διαθέσιμου εισοδήματος.',
      adjustToSee: 'Προσαρμόστε εισόδημα και έξοδα για να δείτε τι μπορείτε να αντέξετε.',
      yourMortgage: 'Το Δάνειό σας',
      loanAmount2: 'Ποσό Δανείου',
      extraMonthly: 'Επιπλέον Μηνιαία Πληρωμή',
      extraMonthlyInfo: 'Πόσο επιπλέον πληρώνετε πέρα από τη συνήθη μηνιαία δόση.',
      standardMonthly: 'Τυπική Μηνιαία Δόση',
      withOverpayment: 'Με Υπερπληρωμή',
      whatOverpaySaves: 'Τι Εξοικονομεί η Υπερπληρωμή',
      timeSaved: 'Εξοικονόμηση Χρόνου',
      noSaving: 'Χωρίς εξοικονόμηση',
      mortgageEnds: 'Το δάνειο ολοκληρώνεται σε',
      interestSaved: 'Εξοικονόμηση Τόκων',
      lessPaidToBank: 'Λιγότερα στην τράπεζα',
      remainingBalance: 'Υπόλοιπο Ανά Έτος',
      standard: 'Τυπικό',
      withOverpaymentLegend: 'Με Υπερπληρωμή',
      overpayNote: 'Η υπερπληρωμή μειώνει γρηγορότερα το υπόλοιπο, εξοικονομώντας σημαντικούς τόκους.',
      ctaTitle: 'Έτοιμοι να βρείτε το ιδανικό σας ακίνητο;',
      ctaSub: 'Εξερευνήστε διαθέσιμες αγγελίες στην Κύπρο και ξεκινήστε τη διαδρομή σας σήμερα.',
      browseProperties: 'Αγγελίες',
      freeValuation: 'Δωρεάν Ενδεικτική Εκτίμηση',
      rateCaption: 'Ενδεικτικό μόνο — μέσος όρος Κύπρου ~3,15% (νέα δάνεια, αρχές 2026). Ελέγξτε τα τρέχοντα επιτόκια σε τράπεζα.',
      dtiGuidelineNote: 'Το 35% είναι κατευθυντήρια οδηγία, όχι κανόνας — τα πραγματικά όρια δανεισμού διαφέρουν ανά τράπεζα και καθορίζονται εξατομικευμένα.',
      purchaseCostsDisclaimer: 'Υπολογίζει μόνο τις δόσεις δανείου — δεν περιλαμβάνει τα έξοδα αγοράς ακινήτου στην Κύπρο (τέλη μεταβίβασης, χαρτόσημο, ΦΠΑ σε νεόδμητα, δικηγορικά). Συμβουλευτείτε αδειούχο σύμβουλο στεγαστικών δανείων για εξατομικευμένες συμβουλές.',
    },
    renovation: {
      back: 'Πίσω',
      title: 'Σχεδιαστής Ανακαίνισης',
      sub: 'Δημιουργήστε λεπτομερή προϋπολογισμό ανακαίνισης ανά δωμάτιο με πραγματικές τιμές Κύπρου και επίπεδα ποιότητας.',
      propertyLocation: 'Τοποθεσία Ακινήτου',
      step1: 'Τι ανακαινίζετε;',
      step1Add: 'Προσθέστε άλλο χώρο',
      step2: 'Επιλογή εργασιών',
      step3: 'Επίπεδο ποιότητας',
      outdoorArea: 'Εξωτερικός χώρος',
      totalFloorArea: 'Συνολική επιφάνεια',
      roomSize: 'Μέγεθος δωματίου',
      continueBtn: 'Συνέχεια',
      selectedCount: 'επιλεγμένα',
      estimatedCostRoom: 'Εκτιμώμενο κόστος για αυτό τον χώρο',
      addToPlan: 'Προσθήκη',
      toPlan: 'στο πλάνο',
      yourPlan: 'Το πλάνο ανακαίνισής σας',
      itemsLabel: 'εργασίες',
      tipsTitle: 'Συμβουλές ανακαίνισης',
      previous: 'Προηγούμενο',
      next: 'Επόμενο',
      costSummary: 'Σύνοψη Κόστους',
      emptyPlan: 'Προσθέστε χώρους στο πλάνο σας για να δείτε πλήρη ανάλυση κόστους.',
      estimatedTotal: 'Συνολική Εκτίμηση :',
      downloadReport: 'Λήψη Αναφοράς',
      contingency: 'Προσθέστε 10–15% για απρόβλεπτα έξοδα',
      costByQuality: 'Κόστος ανά επίπεδο ποιότητας',
      rooms: {
        kitchen: 'Κουζίνα',
        bathroom: 'Μπάνιο',
        bedroom: 'Υπνοδωμάτιο',
        living: 'Σαλόνι',
        outdoor: 'Εξωτερικός / Πισίνα',
        whole: 'Ολόκληρο Ακίνητο',
      },
      quality: {
        budget: { label: 'Οικονομικό', desc: 'Βασικές φινετσιές, τοπικά υλικά', tag: 'Οικονομικό' },
        mid: { label: 'Μεσαίο', desc: 'Ποιοτικές μάρκες, σύγχρονες φινετσιές', tag: 'Πιο συνηθισμένο' },
        luxury: { label: 'Πολυτελές', desc: 'Premium εισαγόμενα υλικά & σχεδιασμός', tag: 'Υψηλής κατηγορίας' },
      },
      renovItems: {
        kitchen: {
          cabinets: 'Ντουλάπια & Πάγκοι',
          appliances: 'Συσκευές',
          tiling: 'Πλακάκια Δαπέδου & Τοίχου',
          plumbing: 'Υδραυλικά',
          electrics: 'Ηλεκτρολογικά',
          painting: 'Βαφή',
        },
        bathroom: {
          fixtures: 'Είδη Υγιεινής & Βρύσες',
          tiling: 'Πλακάκια Δαπέδου & Τοίχου',
          shower: 'Ντους / Μπανιέρα',
          plumbing: 'Υδραυλικά',
          electrics: 'Ηλεκτρολογικά & Φωτισμός',
          vanity: 'Νιπτήρας & Αποθήκευση',
        },
        bedroom: {
          flooring: 'Δάπεδο',
          painting: 'Βαφή',
          wardrobes: 'Εντοιχισμένες Ντουλάπες',
          electrics: 'Ηλεκτρολογικά & Φωτισμός',
          ac: 'Κλιματισμός',
        },
        living: {
          flooring: 'Δάπεδο',
          painting: 'Βαφή',
          electrics: 'Ηλεκτρολογικά & Φωτισμός',
          ac: 'Κλιματισμός',
          fireplace: 'Τζάκι',
          windows: 'Παράθυρα & Πόρτες',
        },
        outdoor: {
          pool: 'Κολυμβητήριο',
          landscaping: 'Διαμόρφωση Κήπου',
          paving: 'Πλακόστρωτο / Deck',
          pergola: 'Πέργκολα / Σκίαστρο',
          fence: 'Φράχτης / Πύλες',
          bbq: 'Ψησταριά / Εξωτερική Κουζίνα',
        },
        whole: {
          structural: 'Δομικές Εργασίες / Τοίχοι',
          flooring: 'Όλα τα Δάπεδα',
          painting: 'Πλήρης Βαφή',
          kitchen: 'Πλήρης Κουζίνα',
          bathrooms: 'Μπάνια (όλα)',
          electrics: 'Πλήρης Ηλεκτρολογική',
          plumbing: 'Πλήρης Υδραυλικά',
          windows: 'Όλα Παράθυρα & Πόρτες',
          ac: 'Πλήρης Σύστημα Κλιματισμού',
        },
      },
      tipCategories: {
        Budget: 'Προϋπολογισμός',
        ROI: 'Αξία',
        Planning: 'Προγραμματισμός',
        Rental: 'Ενοικίαση',
        Materials: 'Υλικά',
      },
      tips: [
        { title: 'Ζητήστε πάντα 3 προσφορές', body: 'Το κόστος εργατικών στην Κύπρο διαφέρει έως 40% μεταξύ εργολάβων. Ζητήστε τουλάχιστον τρεις γραπτές προσφορές πριν δεσμευτείτε — και βεβαιωθείτε ότι περιλαμβάνουν υλικά, απόρριψη και ΦΠΑ.' },
        { title: 'Αγοράστε πλακάκια στο τέλος σεζόν', body: 'Οι προμηθευτές πλακακίων στην Κύπρο προσφέρουν μεγάλες εκπτώσεις σε παλαιά stock τον Οκτώβριο και Μάρτιο. Η αγορά λίγο περισσοτέρων από ό,τι χρειάζεστε προστατεύει επίσης από σπασίματα.' },
        { title: 'Κρατήστε τα υδραυλικά στη θέση τους', body: 'Η μετακίνηση σωληνώσεων σε μπάνιο ή κουζίνα μπορεί να προσθέσει €1.500–€3.000 στο λογαριασμό σας. Αν μπορείτε να εργαστείτε γύρω από τις υπάρχουσες θέσεις σωλήνων, θα εξοικονομήσετε σημαντικά ποσά.' },
        { title: 'Ανακαινίστε τον χειμώνα', body: 'Οι εργολάβοι στην Κύπρο είναι πιο απασχολημένοι Απρίλιο–Σεπτέμβριο. Η κράτηση εργασιών Νοέμβριο–Ιανουάριο συνήθως εξασφαλίζει 10–20% χαμηλότερο εργατικό κόστος και ταχύτερη ολοκλήρωση.' },
        { title: 'Η κουζίνα δίνει την καλύτερη απόδοση', body: 'Μια μεσαίας ποιότητας ανακαίνιση κουζίνας στην Κύπρο προσθέτει συνήθως 1,5–2x το κόστος της στην αξία ακινήτου. Επενδύστε εδώ πρώτα αν στόχος σας είναι η μεταπώληση.' },
        { title: 'Μια πισίνα αυξάνει αξία 8–12%', body: 'Στη Λεμεσό και Πάφο, η προσθήκη πισίνας σε βίλα ή μονοκατοικία αυξάνει κατά μέσο όρο 8–12% την τιμή ζήτησης. Βελτιώνει επίσης σημαντικά την απόδοση βραχυχρόνιας ενοικίασης.' },
        { title: 'Τα ουδέτερα χρώματα πουλάνε γρηγορότερα', body: 'Ακίνητα βαμμένα σε ζεστές ουδέτερες αποχρώσεις πωλούνται κατά μέσο όρο 15 ημέρες γρηγορότερα στην Κύπρο από αυτά με έντονα ή παλαιωμένα χρωματικά σχήματα.' },
        { title: 'Τα διπλά τζάμια αποσβένουν το κόστος', body: 'Η αναβάθμιση σε αλουμινένια κουφώματα με διπλά τζάμια μειώνει το κόστος κλιματισμού έως 30% ετησίως. Οι αγοραστές το ρωτούν ολοένα περισσότερο — είναι ισχυρό πλεονέκτημα πώλησης.' },
        { title: 'Πάρτε άδειες πριν αρχίσετε', body: 'Δομικές αλλαγές, επεκτάσεις και εγκαταστάσεις πισίνας απαιτούν οικοδομική άδεια. Αδήλωτες εργασίες μπορεί να εμποδίσουν πωλήσεις ακινήτων και να προκαλέσουν πρόστιμα.' },
        { title: 'Αναβαθμίστε ηλεκτρολογικά προληπτικά', body: 'Πολλά παλαιότερα ακίνητα στην Κύπρο έχουν μονοφασική σύνδεση 40Α. Η αναβάθμιση σε τριφασική ενώ οι τοίχοι είναι ανοιχτοί κοστίζει €800–€1.200 αλλά αποτρέπει μελλοντικές εργασίες.' },
        { title: 'Μονώστε πριν τοποθετήσετε πλακάκια', body: 'Η θερμομόνωση οροφής είναι πολύ πιο εύκολη πριν μπουν οι εσωτερικές φινετσιές. Στη ζέστη του κυπριακού καλοκαιριού, η καλή μόνωση μειώνει το κόστος κλιματισμού έως 25%.' },
        { title: 'Εγκαταστήστε μαλακτήρα νερού νωρίς', body: 'Η Κύπρος έχει από τα πιο σκληρά νερά στην Ευρώπη. Η εγκατάσταση μαλακτήρα κατά τη διάρκεια ανακαίνισης προστατεύει εξαρτήματα, πλακάκια και συσκευές από ασβεστολιθικές επικαθίσεις.' },
        { title: 'Το en-suite προσθέτει €150/εβδ. στο ενοίκιο', body: 'Η προσθήκη en-suite μπάνιου στο master bedroom στη Λεμεσό ή Πάφο αυξάνει συνήθως το έσοδο βραχυχρόνιας ενοικίασης κατά €100–€200 εβδομαδιαίως στην υψηλή σεζόν.' },
        { title: 'Τα φωτοβολταϊκά προσελκύουν ενοικιαστές', body: 'Ακίνητα με ηλιακούς θερμοσίφωνες και φωτοβολταϊκά πάνελ επιτυγχάνουν 8–12% υψηλότερα μακροπρόθεσμα ενοίκια. Οι ενοικιαστές εκτιμούν τα χαμηλότερα λογαριασμά ρεύματος.' },
        { title: 'Το γρήγορο WiFi είναι πλέον απαραίτητο', body: 'Για βραχυχρόνια ενοικίαση, ακίνητα με οπτική ίνα (100Mbps+) λαμβάνουν καλύτερες κριτικές και χρεώνουν 5–10% premium. Η ίνα Cyta διατίθεται στις περισσότερες αστικές περιοχές.' },
        { title: 'Η σκίαση διπλασιάζει τις καλοκαιρινές κρατήσεις', body: 'Ακίνητα με πέργκολες ή καλυμμένες βεράντες βλέπουν έως 2x περισσότερες καλοκαιρινές κρατήσεις στο Airbnb σε σύγκριση με εκείνα με εκτεθειμένους εξωτερικούς χώρους.' },
        { title: 'Η τοπική πέτρα κοστίζει τη μισή τιμή', body: 'Εισαγόμενο μάρμαρο από Ιταλία κοστίζει 3–4x περισσότερο από την τοπική κυπριακή πέτρα, εξίσου ελκυστική και καλύτερα προσαρμοσμένη στο κλίμα. Ρωτήστε για πέτρα Λεύκαρα ή Πάφου.' },
        { title: 'Η πορσελάνη νικά την κεραμική εξωτερικά', body: 'Τα κεραμικά πλακάκια ραγίζουν από τις κυπριακές θερμοκρασιακές διακυμάνσεις. Τα πλακάκια πορσελάνης (βαθμολογία PEI 4 ή 5) είναι απαραίτητα για εξωτερικές βεράντες και περιβάλλοντες πισίνας.' },
        { title: 'Θαλάσσιας ποιότητας εξαρτήματα κοντά στη θάλασσα', body: 'Σε απόσταση 2χλμ από την ακτή, τα τυπικά ατσάλινα εξαρτήματα διαβρώνονται σε 3–5 χρόνια. Ζητήστε ανοξείδωτο χάλυβα θαλάσσης ή αλουμίνιο για κιγκλιδώματα και εξωτερικά εξαρτήματα.' },
        { title: 'Μεγάλα πλακάκια φαίνονται premium', body: 'Πλακάκια 60x60cm ή 80x80cm σε μπάνιο ή κουζίνα δημιουργούν υψηλού επιπέδου εμφάνιση με λιγότερες αρμοκάλυψες. Η διαφορά κόστους σε σύγκριση με 30x30cm είναι συχνά μόνο €5–€8 ανά m².' },
      ],
      notAQuoteBanner: 'Ενδεικτική εκτίμηση — όχι προσφορά. Με βάση τυπικές τιμές εργολάβων Κύπρου (2024–2025). Οι πραγματικές προσφορές διαφέρουν ανάλογα με την κατάσταση του ακινήτου, τα υλικά και τον εργολάβο. Ζητήστε 2–3 γραπτές προσφορές πριν δεσμευτείτε — μην χρησιμοποιείτε αυτά τα ποσά ως στόχο τιμής σε διαπραγμάτευση.',
    },
    propertyReport: {
      generating: 'Δημιουργία...',
      downloadPdf: 'Λήψη PDF',
      close: 'Κλείσιμο',
      coverTag: 'Αναφορά Ανάλυσης Ακινήτου',
      forRent: 'Προς Ενοικίαση',
      forSale: 'Προς Πώληση',
      askingPrice: 'Ζητούμενη τιμή',
      yieldSuffix: '% απόδοση',
      estRentalYield: 'Εκτιμ. απόδοση ενοικίασης',
      generated: 'Δημιουργήθηκε',
      section01: 'Επισκόπηση ακινήτου',
      bedrooms: 'Υπνοδωμάτια',
      bathrooms: 'Μπάνια',
      floorArea: 'Εμβαδόν',
      type: 'Τύπος',
      featuresAmenities: 'Χαρακτηριστικά και ανέσεις',
      section02: 'Ανάλυση τιμής',
      thisProperty: 'Αυτό το ακίνητο',
      cityAverage: 'Μέσος {city}',
      aboveAvg: 'Τιμή {pct}% πάνω από τον μέσο όρο {city} ανά m² — συνήθως για ακίνητα premium χαρακτηριστικών ή τοποθεσίας.',
      belowAvg: 'Τιμή {pct}% κάτω από τον μέσο όρο {city} ανά m² — πιθανώς καλή αξία στην τρέχουσα αγορά.',
      atAvg: 'Τιμή ακριβώς στον μέσο όρο {city} ανά m².',
      marketTrend: 'Τάση αγοράς {city} (12 μήνες)',
      annualGrowth: 'ετήσια ανάπτυξη',
      section03: 'Επενδυτική επισκόπηση',
      estAnnualYield: 'Εκτιμ. ετήσια απόδοση',
      estRentalIncome: 'Εκτιμ. έσοδο ενοικίασης',
      estAnnualIncome: 'Εκτιμ. ετήσιο εισόδημα',
      yieldNote: 'Εκτίμηση απόδοσης βάσει μέσων αποδόσεων ενοικίασης για {type} ακίνητα στο {city} στις {today}.',
      section04: 'Σύνοψη δανείου',
      deposit: 'Προκαταβολή',
      amount: 'Ποσό',
      loan: 'Δάνειο',
      monthlyPayment: 'Μηνιαία δόση',
      recommended: 'συνιστάται',
      mortgageNote: 'Βάσει 4,5% επιτοκίου σε 25 χρόνια. Συμβουλευτείτε εξουσιοδοτημένο σύμβουλο δανείων.',
      section05: 'Γειτονιά',
      generatingAI: 'Δημιουργία AI αναλύσεων για {region}...',
      schoolsNearby: 'Κοντινά σχολεία',
      transport: 'Μεταφορές',
      airport: 'Αεροδρόμιο',
      expatCommunity: 'Κοινότητα ομογενών',
      intlResidents: '% διεθνείς κάτοικοι',
      aiNote: 'Πληροφορίες γειτονιάς από AI · Βάσει διαθέσιμων δεδομένων για {city}, Κύπρο',
      footerText: 'cosmoestates.cy · Αναφορά Ανάλυσης Ακινήτου · Δημιουργήθηκε {today}',
      footerDisc: 'Όλα τα δεδομένα είναι ενδεικτικά. Η CosmoEstates δεν φέρει ευθύνη για αποφάσεις που λαμβάνονται βάσει αυτής της αναφοράς.',
      scoreLabels: {
        noise: 'Επίπεδο θορύβου',
        schools: 'Σχολεία',
        amenities: 'Παροχές',
        expat: 'Κοινότητα ομογενών',
      },
      scoreRating: {
        excellent: 'Άριστο',
        veryGood: 'Πολύ Καλό',
        good: 'Καλό',
        fair: 'Μέτριο',
      },
    },
    renovationReport: {
      generating: 'Δημιουργία...',
      downloadPdf: 'Λήψη PDF',
      close: 'Κλείσιμο',
      coverTag: 'Αναφορά Κόστους Ανακαίνισης',
      roomPlanTitle: 'Σχέδιο Ανακαίνισης {n} Δωματίων',
      finishSub: '{city}, Κύπρος · Φινίρισμα {quality}',
      roomsPlanned: 'Δωμάτια σε σχέδιο',
      baseEstimate: 'Βασική εκτίμηση',
      withContingency: 'Με αποθεματικό',
      section01: 'Ανάλυση ανά δωμάτιο',
      section02: 'Σύνοψη κόστους',
      contingencyLabel: 'Αποθεματικό 10%',
      totalBudget: 'Συνολικός συνιστώμενος προϋπολογισμός',
      estimatesNote: 'Όλες οι εκτιμήσεις βασίζονται σε υλικά φινιρίσματος {quality} και εργατικά κόστη στο {city}, Κύπρο στις {today}. Τα premium φινιρίσματα μπορεί να αυξήσουν το κόστος κατά 30–50%.',
      valueNote: 'Με αυτόν τον προϋπολογισμό, μια πλήρης ανακαίνιση θα μπορούσε να αυξήσει την ενοικιαστική αξία του ακινήτου κατά περίπου €{rentMin}–€{rentMax}/μήνα και την αξία πώλησης κατά €{saleMin}–€{saleMax}.',
      footerText: 'cosmoestates.cy · Αναφορά Κόστους Ανακαίνισης · Δημιουργήθηκε {today}',
      footerDisc: 'Όλες οι εκτιμήσεις είναι ενδεικτικές. Η CosmoEstates δεν φέρει ευθύνη για πραγματικά κόστη ανακαίνισης.',
    },
    neighbourhood: {
      back: 'Πίσω',
      title: 'Πληροφορίες Γειτονιάς',
      sub: 'Πλήρεις βαθμολογίες για μεταφορές, σχολεία, παροχές και πυκνότητα ομογενών, μαζί με τον επίσημο Δείκτη Τιμών Ακινήτων — βρείτε την περιοχή που ταιριάζει στον τρόπο ζωής σας.',
      signInTitle: 'Συνδεθείτε για πρόσβαση',
      signInSub: 'Οι Πληροφορίες Γειτονιάς είναι δωρεάν για όλα τα εγγεγραμμένα μέλη CosmoEstates. Συνδεθείτε ή δημιουργήστε λογαριασμό για πρόσβαση σε λεπτομερείς βαθμολογίες, AI ανάλυση και επενδυτικά δεδομένα για κάθε γειτονιά στην Κύπρο.',
      features: [
        'Βαθμολογίες μεταφορών & συνδεσιμότητας',
        'Αξιολογήσεις σχολικής εμβέλειας',
        'Δείκτης Τιμών Ακινήτων (δεδομένα ΚΤΚ)',
        'Πυκνότητα ομογενών',
        'Παροχές & μεταφορές',
        'AI ανάλυση γειτονιάς',
      ],
      createAccount: 'Δωρεάν Εγγραφή',
      signIn: 'Σύνδεση',
      freeNote: 'Δωρεάν για πάντα. Δεν απαιτείται κάρτα.',
      aiBadge: 'AI Ανάλυση',
      aiNote: 'Τα αποτελέσματα είναι εκτιμήσεις AI — επαληθεύστε τοπικά',
      clear: '✕ Καθαρισμός',
      fullDetails: 'Πλήρεις πληροφορίες',
      expatResidents: '% ομογενείς κάτοικοι',
      transport: 'Μεταφορές:',
      priceTrend: 'Τάση Τιμών (6 τρίμηνα)',
      estYield: 'Εκτιμ. Απόδοση Ενοικίασης',
      searchPlaceholder: 'Αναζητήστε αποθηκευμένες περιοχές ή πληκτρολογήστε γειτονιά για AI ανάλυση...',
      analysing: 'Ανάλυση...',
      aiAnalyse: 'AI Ανάλυση',
      noMatch: 'Δεν βρέθηκαν αποθηκευμένες περιοχές — πατήστε Enter ή κάντε κλικ στο AI Ανάλυση για πληροφορίες για',
      sortBy: 'Ταξινόμηση:',
      sortOptions: {
        expat: 'Πυκνότητα Ομογενών',
        schools: 'Σχολεία',
        amenities: 'Παροχές',
        transport: 'Μεταφορές',
        development: 'Ανάπτυξη',
      },
      noNeighbourhoods: 'Δεν βρέθηκαν γειτονιές.',
      allCities: 'Όλες οι Πόλεις',
      barLabels: {
        noise: 'Θόρυβος',
        schools: 'Σχολεία',
        amenities: 'Παροχές',
        expat: 'Ομογενείς',
        transport: 'Συγκοινωνίες',
      },
      backToNeighbourhoods: 'Πίσω στις Γειτονιές',
      overall: 'Συνολική',
      avgAcrossFactors: 'Μέσος όρος 5 παραγόντων',
      whatMeans: 'Τι σημαίνει για εσάς',
      howCompares: 'Σύγκριση {name}',
      allNeighbourhoods: 'όλες οι γειτονιές',
      showLess: 'Λιγότερα',
      showMore: 'Εμφάνιση {n} ακόμα',
      nearbySchools: 'Κοντινά Σχολεία',
      keyAmenities: 'Βασικές Παροχές',
      areaHighlights: 'Χαρακτηριστικά Περιοχής',
      ctaTitle: 'Έτοιμοι να εξερευνήσετε ακίνητα στο {name};',
      ctaSub: 'Δείτε διαθέσιμες αγγελίες σε αυτή τη γειτονιά και κλείστε επίσκεψη σήμερα.',
      ctaBtn: 'Αγγελίες στο {name}',
      notFound: 'Η γειτονιά δεν βρέθηκε.',
      transportChip: 'Μεταφορές:',
      expatsChip: '% ομογενείς',
      overPeriod: 'σε αυτή την περίοδο',
      scoreLabels: {
        noise: 'Επίπεδο Θορύβου',
        schools: 'Ποιότητα Σχολείων',
        amenities: 'Παροχές',
        expat: 'Κοινότητα Ομογενών',
      },
      scoreTips: {
        noise: 'Υψηλότερο = πιο ήσυχο. Μετρά επίπεδα θορύβου ημέρας/νύχτας από κυκλοφορία, νυχτερινή ζωή και βιομηχανία.',
        schools: 'Αξιολογεί την ποιότητα και διαθεσιμότητα σχολείων, συμπεριλαμβανομένων διεθνών προγραμμάτων.',
        amenities: 'Καλύπτει εμπόριο, υγειονομική περίθαλψη, εστίαση, αναψυχή και καθημερινές ανέσεις.',
        expat: 'Μετρά το μέγεθος και τη δραστηριότητα της διεθνούς κοινότητας στην περιοχή.',
      },
    },
    faq: {
      badge: 'Κέντρο Βοήθειας',
      title: 'Συχνές Ερωτήσεις',
      subtitle: 'Όλα όσα χρειάζεστε να γνωρίζετε για αγορά, ενοικίαση και καταχώρηση ακινήτου στο CosmoEstates.',
      cat: {
        gettingStarted: 'Ξεκινώντας',
        buyingRenting: 'Αγορά & Ενοικίαση',
        listingProperty: 'Καταχώρηση Ακινήτου',
        proBoost: 'Pro Συνδρομή & Ενίσχυση Αγγελίας',
        privacySafety: 'Απόρρητο & Ασφάλεια',
      },
      items: {
        gs0q: 'Τι είναι το CosmoEstates;',
        gs0a: 'Το CosmoEstates είναι μια πύλη ακινήτων της Κύπρου που συνδέει αγοραστές, ενοικιαστές, πωλητές και ιδιοκτήτες σε όλες τις πέντε επαρχίες — Λεμεσό, Λευκωσία, Πάφο, Λάρνακα και Αμμόχωστο. Παράλληλα με τις αγγελίες προσφέρουμε εργαλείο Ενδεικτικής Εκτίμησης Ακινήτου, υπολογιστή κόστους ανακαίνισης, δεδομένα γειτονιάς από την Κεντρική Τράπεζα της Κύπρου και το OpenStreetMap, και κατάλογο μεσιτών.',
        gs1q: 'Ποιες λειτουργίες απαιτούν συνδρομή Pro;',
        gs1a: 'Το Pro ξεκλειδώνει το premium επίπεδο του CosmoEstates: πλήρεις Πληροφορίες Γειτονιάς (μεταφορές, σχολεία, παροχές, πυκνότητα ομογενών μαζί με τον επίσημο Δείκτη Τιμών Ακινήτων), προτεραιότητα στα εισερχόμενα των πωλητών ώστε τα μηνύματά σας να φαίνονται πρώτα, άμεσες ειδοποιήσεις μόλις βγει στην αγορά ένα ακίνητο που σας ταιριάζει και ειδοποιήσεις πτώσης τιμής στα αποθηκευμένα σας ακίνητα.',
        gs2q: 'Ποιες περιοχές της Κύπρου καλύπτονται;',
        gs2a: 'Καλύπτουμε και τις πέντε επαρχίες της Κυπριακής Δημοκρατίας: Λεμεσό, Λευκωσία, Πάφο, Λάρνακα και Αμμόχωστο (ελεύθερη περιοχή, συμπεριλαμβανομένων Αγίας Νάπας, Πρωταρά και Παραλιμνίου). Νέες αγγελίες από μεσίτες και ιδιώτες προστίθενται καθημερινά.',
        br0q: 'Πώς μπορώ να αναζητήσω ακίνητο;',
        br0a: 'Χρησιμοποιήστε τη γραμμή αναζήτησης στην αρχική σελίδα για να εισάγετε πόλη, γειτονιά ή λέξη-κλειδί. Στη σελίδα Αγγελιών μπορείτε να φιλτράρετε με βάση την τιμή, τον τύπο ακινήτου (σπίτι, διαμέρισμα, βίλα) και τον τύπο καταχώρησης (προς πώληση ή ενοικίαση). Τα αποτελέσματα κατατάσσονται βάσει συνάφειας και ποιότητας αγγελίας — όχι μόνο με βάση αυτούς που πλήρωσαν για ενίσχυση.',
        br1q: 'Πώς μπορώ να επικοινωνήσω με έναν πωλητή ή ιδιοκτήτη;',
        br1a: 'Κάθε σελίδα λεπτομερειών ακινήτου διαθέτει ενσωματωμένο πάνελ ανταλλαγής μηνυμάτων. Γράψτε το μήνυμά σας και πατήστε Αποστολή — ο ιδιοκτήτης ή μεσίτης το λαμβάνει απευθείας στα εισερχόμενά του. Χρειάζεστε λογαριασμό CosmoEstates για να στείλετε μηνύματα. Τα μέλη Pro λαμβάνουν επαληθευμένο σήμα Pro στα μηνύματά τους.',
        br2q: 'Μπορώ να αποθηκεύσω ακίνητα που με ενδιαφέρουν;',
        br2a: 'Ναι. Πατήστε το εικονίδιο καρδιάς σε οποιαδήποτε κάρτα ακινήτου ή σελίδα λεπτομερειών για να το αποθηκεύσετε στη λίστα παρακολούθησής σας. Θα βρείτε όλα τα αποθηκευμένα ακίνητα στην ενότητα Αποθηκευμένα του λογαριασμού σας.',
        br3q: 'Τι είναι η Ενδεικτική Εκτίμηση Ακινήτου;',
        br3a: 'Το εργαλείο Ενδεικτικής Εκτίμησης Ακινήτου είναι δωρεάν για όλους — συμπληρώστε λίγα στοιχεία για το ακίνητο και θα σας στείλουμε με email ένα ενδεικτικό εύρος αγοραίας αξίας. Είναι μια ενδεικτική αφετηρία, όχι επίσημη Ενδεικτική Εκτίμηση Ακινήτου.',
        br4q: 'Τι είναι ο Δείκτης Τιμών Ακινήτων;',
        br4a: 'Η Κεντρική Τράπεζα της Κύπρου δημοσιεύει τριμηνιαία τον Δείκτη Τιμών Κατοικίας για κάθε επαρχία της Κύπρου, με βάση το Q1 2010 = 100. Εμφανίζουμε αυτή τη σειρά σε κάθε σελίδα λεπτομερειών γειτονιάς ώστε να βλέπετε πραγματικές τάσεις τιμών σε επίπεδο επαρχίας αντί για εσωτερικούς μέσους όρους της πλατφόρμας.',
        br5q: 'Γιατί δεν δείχνετε τιμή ανά τετραγωνικό μέτρο;',
        br5a: 'Οι μέσοι όροι €/τ.μ. που υπολογίζονται από αγγελίες προέρχονται από μικρό και προκατειλημμένο δείγμα — μπορούν να παραπλανήσουν περισσότερο απ\' ό,τι να βοηθήσουν. Αντί γι\' αυτό εμφανίζουμε τον Δείκτη Τιμών Ακινήτων της Κεντρικής Τράπεζας της Κύπρου για κάθε επαρχία, που είναι η επίσημη δημοσιευμένη σειρά τιμών.',
        lp0q: 'Πώς καταχωρώ το ακίνητό μου στο CosmoEstates;',
        lp0a: 'Δημιουργήστε λογαριασμό και, στη συνέχεια, κάντε κλικ στο "Καταχώρηση Ακινήτου" στο μενού πλοήγησης. Συμπληρώστε τα στοιχεία ακινήτου, ανεβάστε φωτογραφίες και δημοσιεύστε. Η αγγελία σας ενεργοποιείται αμέσως.',
        lp1q: 'Πόσες φωτογραφίες πρέπει να συμπεριλάβω;',
        lp1a: 'Συνιστούμε τουλάχιστον 8 φωτογραφίες που να καλύπτουν όλα τα κύρια δωμάτια, την εξωτερική όψη και οποιαδήποτε ξεχωριστά χαρακτηριστικά, όπως πισίνα ή κήπο.',
        lp2q: 'Μπορώ να επεξεργαστώ ή να αφαιρέσω την αγγελία μου μετά τη δημοσίευση;',
        lp2a: 'Ναι. Μεταβείτε στο "Οι Αγγελίες Μου" στον λογαριασμό σας και κάντε κλικ στην Επεξεργασία σε οποιαδήποτε αγγελία για να ενημερώσετε στοιχεία, να αλλάξετε φωτογραφίες ή να προσαρμόσετε την τιμή. Μπορείτε επίσης να σημάνετε το ακίνητο ως πωλημένο ή να διαγράψετε οριστικά μια αγγελία ανά πάσα στιγμή. Οι αλλαγές ενεργοποιούνται αμέσως.',
        pb0q: 'Τι περιλαμβάνει η συνδρομή Pro;',
        pb0a: 'Το Pro ξεκλειδώνει τέσσερις λειτουργίες: πλήρεις Πληροφορίες Γειτονιάς (με τον επίσημο Δείκτη Τιμών Ακινήτων), Προτεραιότητα στο Inbox Πωλητών ώστε τα μηνύματά σας να φαίνονται στην κορυφή, Έξυπνες Ειδοποιήσεις μόλις βγει στην αγορά ένα ακίνητο που σας ταιριάζει, και Ειδοποιήσεις Πτώσης Τιμής στα αποθηκευμένα σας ακίνητα.',
        pb1q: 'Τι είναι η Ενίσχυση Αγγελίας;',
        pb1a: 'Η Ενίσχυση είναι μια εφάπαξ, 14ήμερη βελτίωση για μια συγκεκριμένη αγγελία, με χρέωση €10,99 ανά αγγελία. Οι ενισχυμένες αγγελίες εναλλάσσονται στο καρουζέλ Boosted της αρχικής σελίδας και λαμβάνουν πλεονέκτημα κατάταξης στα αποτελέσματα αναζήτησης εντός των ενεργών φίλτρων του αγοραστή.',
        pb2q: 'Πώς διασφαλίζεται η δικαιοσύνη στο σύστημα ενίσχυσης;',
        pb2a: 'Οι ενισχυμένες αγγελίες εμφανίζονται μόνο εντός των ενεργών φίλτρων αναζήτησης του αγοραστή — δεν εισάγονται ποτέ σε άσχετα αποτελέσματα.',
        pb4q: 'Πώς διαχειρίζομαι ή ακυρώνω τη συνδρομή Pro μου;',
        pb4a: 'Μεταβείτε στη σελίδα Προφίλ σας και πατήστε Διαχείριση συνδρομής. Ανοίγει η πύλη πελατών του Stripe όπου μπορείτε να αλλάξετε πλάνο, να ενημερώσετε τη μέθοδο πληρωμής ή να ακυρώσετε. Αν δεν βρεθεί η συνδρομή σας στο σύστημά μας, στείλτε email στο cosmoestatescyprus@gmail.com και θα τη χειριστούμε εμείς.',
        ps1q: 'Πώς αναφέρω μια ύποπτη αγγελία;',
        ps1a: 'Πατήστε το κουμπί σημαίας σε οποιαδήποτε σελίδα λεπτομερειών ακινήτου για να αναφέρετε μια ύποπτη ή δόλια αγγελία — διαθέσιμο σε όλους, δεν απαιτείται συνδρομή Pro. Η ομάδα μας εξετάζει όλες τις αναφορές εντός 24 ωρών.',
        ps2q: 'Πώς διαγράφω τον λογαριασμό μου;',
        ps2a: 'Μπορείτε να ζητήσετε διαγραφή λογαριασμού από τη σελίδα Ρυθμίσεων στη Διαχείριση Λογαριασμού. Όλες οι αγγελίες, τα μηνύματα και τα προσωπικά σας δεδομένα θα αφαιρεθούν οριστικά εντός 30 ημερών σύμφωνα με τις απαιτήσεις GDPR.',
      },
    },
    brokerListings: {
      backToProfile: 'Πίσω στο προφίλ',
      brokerFallback: 'Μεσίτη',
      listingsByPrefix: 'Αγγελίες από ',
      activeListing: 'ενεργή αγγελία',
      activeListings: 'ενεργές αγγελίες',
      empty: 'Ο μεσίτης δεν έχει ενεργές αγγελίες ακόμα.',
    },
    legal: {
      breadcrumb: 'Νομικά',
      effectiveDate: 'Ημερομηνία έναρξης ισχύος:',
      lastUpdated: 'Τελευταία ενημέρωση:',
      onThisPage: 'Σε αυτή τη σελίδα',
      backToAll: '← Επιστροφή σε όλα τα νομικά έγγραφα',
      indexTitle: 'Νομικά έγγραφα',
      indexCrumb: 'Νομικά',
      indexSubtitle: 'Οι νομικοί όροι και οι πολιτικές που διέπουν τη χρήση του CosmoEstates. Ισχύουν για όλους όσοι χρησιμοποιούν την Πλατφόρμα — επισκέπτες, εγγεγραμμένους χρήστες, μεσίτες και συνεργάτες.',
      indexRead: 'Διαβάστε →',
      indexMetaTitle: 'Νομικά Έγγραφα | CosmoEstates',
      indexMetaDesc: 'Ευρετήριο νομικών εγγράφων του CosmoEstates: Όροι, Απόρρητο, Cookies, Αποποίηση, Αποδεκτή Χρήση και όροι χρέωσης.',
      docs: {
        termsTitle: 'Όροι & Προϋποθέσεις',
        termsDesc: 'Οι κανόνες χρήσης του CosmoEstates: λογαριασμός, αγγελίες, εργαλεία, δημιουργία leads, χρεώσεις, ευθύνη και επίλυση διαφορών.',
        privacyTitle: 'Πολιτική Απορρήτου',
        privacyDesc: 'Ποια προσωπικά δεδομένα συλλέγουμε, γιατί τα χρησιμοποιούμε, με ποιους τα μοιραζόμαστε, πόσο καιρό τα κρατάμε και τα δικαιώματά σας βάσει GDPR.',
        cookiesTitle: 'Πολιτική Cookies',
        cookiesDesc: 'Πώς χρησιμοποιούμε cookies, localStorage και sessionStorage — συμπεριλαμβανομένου του «πρόσφατα προβεβλημένα» — και πώς να διαχειριστείτε τη συγκατάθεση.',
        disclaimerTitle: 'Αποποίηση Ευθυνών',
        disclaimerDesc: 'Πώς λειτουργούν τα εργαλεία AI (Εκτίμηση Τιμής, Κόστος Ανακαίνισης, Πληροφορίες Γειτονιάς, Υπολογιστής Στεγαστικού) και τα όριά τους.',
        acceptableUseTitle: 'Αποδεκτή Χρήση & Πολιτική Αγγελιών',
        acceptableUseDesc: 'Τι μπορείτε και τι δεν μπορείτε να δημοσιεύσετε στο CosmoEstates, μετριασμός και συνέπειες παραβίασης.',
        billingTitle: 'Όροι Συνδρομής, Boost & Επιστροφής',
        billingDesc: 'Πλάνα Pro, πακέτα Boost, χρεώσεις CPL και το δικαίωμα υπαναχώρησης 14 ημερών ως καταναλωτής.',
      },
    },
    policyEmbed: {
      privacyTitle: 'Πολιτική Απορρήτου',
      termsTitle: 'Όροι Χρήσης',
      cookiesTitle: 'Πολιτική Cookies',
      subtitle: 'Διατηρείται από το Termageddon — δείτε την ενσωματωμένη πολιτική παρακάτω για την τρέχουσα έκδοση.',
      loadingPre: 'Παρακαλώ περιμένετε ενώ φορτώνεται η πολιτική. Αν δεν φορτώσει, παρακαλώ ',
      clickHere: 'κάντε κλικ εδώ για να δείτε την πολιτική',
      clickHereAria: 'κάντε κλικ εδώ για να δείτε την πολιτική',
    },
    contactBroker: {
      titlePrefix: 'Επικοινωνία με ',
      fallbackName: 'αυτόν τον μεσίτη',
      subtitle: 'Επιλέξτε τον τρόπο επικοινωνίας.',
      call: 'Κλήση',
      email: 'Email',
      empty: 'Δεν υπάρχουν διαθέσιμα στοιχεία επικοινωνίας ακόμη.',
      closeAria: 'Κλείσιμο',
    },
    rateLimit: {
      proOnlyTitle: 'Μόνο για μέλη Pro',
      proOnlyBody: 'Αυτή η λειτουργία είναι διαθέσιμη αποκλειστικά για μέλη CosmoEstates Pro.',
      upgradeToPro: 'Αναβάθμιση σε Pro',
      signInTitle: 'Παρακαλώ συνδεθείτε',
      signInBody: 'Πρέπει να είστε συνδεδεμένοι για να χρησιμοποιήσετε αυτή τη λειτουργία.',
      signIn: 'Σύνδεση',
      limitTitle: 'Έχετε φτάσει το όριό σας',
      limitBodyPrefix: 'Έχετε χρησιμοποιήσει και τα ',
      limitBodyMid: ' ',
      limitBodySuffix: ' αιτήματα για αυτό το τρίωρο.',
      resetsInPrefix: 'Επαναφορά σε ',
      resetsInSuffix: '.',
      contactHelp: 'Επικοινωνήστε μαζί μας για βοήθεια',
      upgradeHigher: 'Αναβάθμιση για υψηλότερα όρια',
      proHintPrefix: 'Τα μέλη Pro παίρνουν ',
      proHintSuffix: ' αιτήματα ανά 3 ώρες.',
    },
    neighbourhoodCompare: {
      title: 'Πώς συγκρίνεται αυτή η γειτονιά',
      thisNeighbourhood: 'Αυτή η γειτονιά',
      cityAverageSuffix: ' μέσος όρος',
      cityAverageTitlePrefix: '',
      cityAverageTitleMid: ' μέσος όρος: ',
    },
    boostPage: {
      eyebrow: 'ΠΡΟΩΘΗΣΗ ΑΓΓΕΛΙΑΣ',
      heroTitle: 'Δώστε στην αγγελία σας μεγαλύτερη προβολή σε αγοραστές.',
      heroSub: 'Προωθήστε το ακίνητό σας σε κορυφαία θέση, προβεβλημένες ενότητες και οπτική προτεραιότητα — για να το δουν πρώτοι οι σοβαροί αγοραστές.',
      cancelled: 'Η ολοκλήρωση ακυρώθηκε — δεν έγινε καμία χρέωση. Επιλέξτε πακέτο και δοκιμάστε ξανά.',
      benefitsTitle: 'Οφέλη της προώθησης',
      benefits: [
        {
          title: 'Καλύτερη τοποθέτηση',
          desc: 'Οι προωθημένες αγγελίες εμφανίζονται πάνω από τις τυπικές στα αποτελέσματα αναζήτησης, δίνοντας στο ακίνητό σας ξεκάθαρο πλεονέκτημα προβολής.',
        },
        {
          title: 'Εναλλαγή στο προβεβλημένο carousel',
          desc: 'Η αγγελία σας εναλλάσσεται στο carousel Προβεβλημένων Αγγελιών της αρχικής — οι προωθημένες αγγελίες προτεραιοποιούνται όταν οι αγοραστές επισκέπτονται το site.',
        },
      ],
      packagesTitle: 'Προωθήστε την Αγγελία σας',
      packageDuration: '14 ημέρες',
      packageListingLabel: '1 Αγγελία',
      cta: 'Προώθηση τώρα →',
    },
    boostConfirm: {
      back: '← Πίσω στις αγγελίες',
      title: 'Επιβεβαιώστε την προώθηση',
      sub: 'Ελέγξτε το πακέτο και τις επιλεγμένες αγγελίες σας πριν την πληρωμή.',
      sectionPackage: 'ΠΑΚΕΤΟ',
      sectionListings: 'ΟΙ ΑΓΓΕΛΙΕΣ ΣΑΣ',
      loading: 'Φόρτωση αγγελιών…',
      untitled: 'Αγγελία χωρίς τίτλο',
      total: 'Σύνολο',
      redirecting: 'Μεταφορά στο Stripe…',
      proceed: 'Συνέχεια στο checkout →',
      note: 'Θα μεταφερθείτε στο Stripe για ασφαλή ολοκλήρωση της πληρωμής.',
    },
    boostSelectPage: {
      back: '← Πίσω στο πακέτο',
      title: 'Επιλέξτε τις αγγελίες σας',
      packageLabel: 'Πακέτο',
      selectedOfCap: '{n}/{cap} επιλεγμένες',
      next: 'Συνέχεια →',
      pickUpTo: 'Επιλέξτε έως {cap} αγγελίες για προώθηση 14 ημερών.',
    },
    proPage: {
      choosePlan: 'Επιλέξτε το πακέτο σας',
      features: {
        priorityInbox: {
          title: 'Προτεραιότητα στο Inbox',
          desc: 'Τα μέλη Pro λαμβάνουν πιο συχνά απάντηση πρώτα, χάρη στην προτεραιότητα εμφάνισης στο inbox.',
        },
        neighbourhood: {
          title: 'Πληροφορίες Γειτονιάς',
          bullets: [
            'Βαθμολογία μεταφορών & προσβασιμότητας',
            'Κοντινά σχολεία & ποιότητα εκπαίδευσης',
            'Δείκτης Τιμών Ακινήτων (δεδομένα ΚΤΚ)',
            'Πυκνότητα ομογενών & παρουσία κοινοτήτων',
          ],
        },
        smartAlerts: {
          title: 'Έξυπνες Ειδοποιήσεις',
          desc: 'Λάβετε άμεση ειδοποίηση τη στιγμή που ένα ακίνητο που ταιριάζει στην αποθηκευμένη σας αναζήτηση βγαίνει στην αγορά — μείνετε ένα βήμα μπροστά.',
        },
        priceDropAlerts: {
          title: 'Ειδοποιήσεις Πτώσης Τιμής',
          desc: 'Ειδοποιηθείτε τη στιγμή που πέφτει η τιμή σε ένα ακίνητο που έχετε αποθηκεύσει. Γίνετε οι πρώτοι που θα κινηθείτε.',
        },
      },
    },
    checkoutHero: {
      eyebrow: 'Η PRO ΣΥΝΔΡΟΜΗ ΣΑΣ',
      title: 'Λίγα βήματα για να ξεκλειδώσετε το Pro',
      sub: 'Ασφαλές checkout μέσω Stripe · Ακύρωση οποτεδήποτε',
    },
  },
    de: {
      // === German (de) — production copy. Glossary: property → Immobilie · listing → Angebot · agent/broker → Makler · boost → Boost · pro → Pro · deed → Urkunde · commission → Provision · valuation → Wertermittlung · watchlist → Merkliste · save → merken ===
      nav: {
        listProperty: "Immobilie inserieren",
        findBroker: "Makler finden",
        valuation: "Schätzung",
        findAgent: "Makler finden",
        notifications: "Benachrichtigungen",
        watchlist: "Merkliste",
        profile: "Profil",
        markAllRead: "Alle als gelesen markieren",
        noNotifications: "Noch keine Benachrichtigungen.",
        viewAllNotifications: "Alle Benachrichtigungen und Nachrichten ansehen",
        myWatchlist: "Meine Merkliste",
        savedSuffix: "gemerkt",
        noSavedProperties: "Noch keine gemerkten Immobilien.",
        browseListings: "Angebote durchsuchen",
        viewAllSaved: "Alle gemerkten Immobilien ansehen",
        perMonthShort: "Mo.",
        myProfile: "Mein Profil",
        myListings: "Meine Angebote",
        inbox: "Posteingang",
        settings: "Einstellungen",
        logout: "Abmelden",
        signIn: "Anmelden",
        signInSub: "Zu deinem Konto",
        register: "Registrieren",
        registerSub: "Kostenloses Konto erstellen",
        menu: "Menü",
        alerts: "Meldungen",
        messages: "Nachrichten",
        noAlerts: "Noch keine Meldungen.",
        noMessages: "Noch keine Nachrichten.",
        noMessagesYet: "Noch keine Nachrichten",
        openFullInbox: "Vollständigen Posteingang öffnen",
        goToMessages: "Zu den Nachrichten",
        justNow: "Gerade eben",
        minutesShort: "Min.",
        hoursShort: "Std.",
        agent: "Makler",
        buyer: "Käufer",
        buy: "Kaufen",
        rent: "Mieten",
        sell: "Verkaufen",
        tools: "Werkzeuge",
        pro: "Pro",
      },
      home: {
        hero: {
          titleA: "Finde deine",
          titleB: "perfekte",
          titleC: "Immobilie noch heute",
          subtitle: "Angebote durchsuchen · Makler finden · Handwerker kontaktieren",
        },
        search: {
          buy: "Kaufen",
          rent: "Mieten",
          location: "Stadt oder Adresse…",
          propertyType: "Immobilientyp",
          priceRange: "Preisspanne",
          upTo: "Bis zu",
          from: "Von",
          to: "bis",
          button: "Suchen",
          types: {
            house: "Haus",
            apartment: "Wohnung",
            villa: "Villa",
            commercial: "Gewerbe",
            underConstruction: "Im Bau",
          },
        },
        sectionLabel: "WAS UNS AUSZEICHNET",
        services: {
          title: "Unsere Leistungen",
          subtitle: "CosmoEstates ist mehr als ein Immobilienmarktplatz. Wir geben dir die Werkzeuge für klügere, sicherere Immobilienentscheidungen in Zypern.",
          valuation: {
            title: "Immobilien-Preisschätzung",
            description: "Hol dir eine kostenlose, marktbasierte Preisschätzung für jede Immobilie in Zypern. Trag ein paar Details ein und wir schicken dir eine Ausgangsschätzung per E-Mail — ein Richtwert, keine offizielle Bewertung.",
            bullets: ["Sofortige Richtschätzung", "Schätzung direkt an deine E-Mail", "100 % kostenlos — kein Konto nötig"],
            cta: "Kostenlose Schätzung erhalten",
          },
          renovation: {
            title: "Renovierungs-Tool",
            description: "Plane deine Renovierung mit Sicherheit. Erhalte eine indikative Gesamtkostenspanne für die Renovierung basierend auf deinen Auswahlen und tritt direkt mit geprüften Handwerkern für jeden Projektschritt in Kontakt.",
            bullets: ["Kontakt zu geprüften Handwerkern", "Indikative Gesamtkostenspanne für die Renovierung", "Kostenaufschlüsselung Raum für Raum"],
            cta: "Renovierungs-Tool testen",
          },
          neighbourhood: {
            title: "Nachbarschafts-Analyse",
            description: "Bevor du kaufst, lerne alles über die Gegend kennen. Unser Daten-Motor bewertet jede Nachbarschaft in Zypern hinsichtlich Verkehr, Schulen, Ausstattung und Expat-Dichte — zusammen mit dem offiziellen Immobilienpreis-Index.",
            bullets: ["Verkehrs- und Anbindungs-Scores", "Karten von Schul-Einzugsgebieten", "Immobilienpreis-Index (CBC-Daten)", "Expat-Community-Dichte"],
            cta: "Nachbarschaften erkunden",
          },
          mortgage: {
            title: "Finanz-Kompass",
            description: "Plane deinen Kauf mit Sicherheit — schätze monatliche Raten, Gesamtzinsen und Sondertilgungs-Szenarien für jede Immobilie in Zypern.",
            bullets: ["Schätzung der monatlichen Rate", "Aufschlüsselung Zinsen vs. Tilgung", "Diagramm Jahr für Jahr", "Kein Konto erforderlich"],
            cta: "Rechner öffnen",
          },
        },
        boostedListings: {
          title: "Empfohlene Angebote",
          viewAll: "Alle Angebote ansehen",
          viewDetails: "Details ansehen",
        },
        style: {
          title: "Finde deinen Stil",
          mediterranean: {
            tag: "SONNENDURCHFLUTETES LEBEN AM MEER",
            name: "Mediterrane Häuser",
          },
          modern: {
            tag: "ARCHITEKTUR TRIFFT INSEL-LUXUS",
            name: "Moderne Villen",
          },
          luxury: {
            tag: "GEHOBENES STADT- UND KÜSTENLEBEN",
            name: "Luxuswohnungen",
          },
        },
        pro: {
          eyebrow: "COSMOESTATES PRO",
          title: "Der klügere Weg, in Zypern zu kaufen",
          subtitle: "Upgrade auf Pro und schalte alle Werkzeuge frei, die du für sichere, datenbasierte Immobilienentscheidungen brauchst — alles an einem Ort.",
          getStarted: "Mit Pro starten",
          seePlans: "Alle Tarife ansehen",
          hidePlans: "Tarife ausblenden",
          getStartedWith: "Mit dem {label}-Tarif starten",
          whatsIncluded: "Was enthalten ist",
          features: {
            renovation: {
              title: "Renovierungs-Tool",
              description: "Plane Renovierungen mit Kostenschätzungen, ROI-Prognosen und regionalen Hinweisen.",
            },
            neighbourhood: {
              title: "Nachbarschafts-Analyse",
              description: "Vollständige Bewertungen zu Verkehr, Schulen, Ausstattung und Expat-Dichte. Plus offizieller Immobilienpreis-Index (CBC-Daten), Verkaufsverlauf und Renditeschätzungen je Bezirk.",
            },
            alerts: {
              title: "Priorisierte Angebots-Benachrichtigungen",
              description: "Werde in dem Moment benachrichtigt, in dem eine passende Immobilie auf den Markt kommt. Verpass nie wieder ein gutes Angebot.",
            },
            inbox: {
              title: "Priorisierter Verkäufer-Posteingang",
              description: "Deine Nachricht erscheint ganz oben im Posteingang jedes Verkäufers. Pro-Mitglieder werden zuerst gesehen, schneller beantwortet und heben sich in umkämpften Situationen ab.",
            },
            reports: {
              title: "Angebots-Detailberichte",
              description: "Erstelle einen vollständigen PDF-Bericht für jedes Immobilien-Angebot — Preisverlauf, Nachbarschafts-Scores, Renditeschätzung und wichtige Details in einem teilbaren Dokument.",
            },
          },
        },
        sponsored: {
          label: "GESPONSERT",
          title: "Hier könnte deine Anzeige stehen",
          description: "Erreiche jeden Tag Tausende Käufer und Mieter in ganz Zypern.",
          cta: "Bei uns werben",
        },
        recent: {
          eyebrow: "Neueste Angebote",
          subtitle: "Die jüngsten Immobilien auf CosmoEstates — frisch am Markt.",
        },
        recentlyViewed: {
          eyebrow: "Ähnlich wie zuletzt angesehen",
          subtitle: "Ausgewählt aus Angeboten, die zu den zuletzt betrachteten Immobilien passen.",
        },
        adSponsored: "Gesponsert",
        adTitle: "Hier könnte deine Anzeige stehen",
        adSub: "Erreiche jeden Tag Tausende Käufer und Mieter in ganz Zypern.",
        adCta: "Bei uns werben",
      },
      maskedPhone: {
        loginToView: "Anmelden, um Kontakt zu sehen",
      },
      listings: {
        pageTitle: "Alle Immobilien",
        propertiesFound: "Immobilien gefunden",
        propertiesIn: "Immobilien in „{q}“",
        listingType: "ANGEBOTSTYP",
        propertyType: "IMMOBILIENTYP",
        sortBy: "SORTIEREN NACH",
        filtersBtn: "Filter",
        filters: {
          all: "Alle",
          forSale: "Zum Verkauf",
          forRent: "Zur Miete",
          house: "Haus",
          apartment: "Wohnung",
          villa: "Villa",
          newestFirst: "Neueste zuerst",
          priceLow: "Preis: aufsteigend",
          priceHigh: "Preis: absteigend",
        },
        advertisement: "Anzeige",
        placeAd: "Hier deine Anzeige platzieren",
        adSub: "Erreiche jeden Tag Tausende Immobiliensuchende in Zypern",
        advertise: "Bei uns werben",
        noProperties: "Keine Immobilien gefunden",
        adjustFilters: "Passe deine Filter an oder suche in einem anderen Gebiet.",
        viewProperty: "Immobilie ansehen",
      },
      property: {
        back: "Zurück",
        save: "Merken",
        saved: "Gemerkt",
        askingPrice: "ANGEBOTSPREIS",
        defaultMessage: "Hallo, ich interessiere mich für diese Immobilie. Könnten wir eine Besichtigung vereinbaren?",
        sendMessage: "Nachricht senden",
        bedrooms: "SCHLAFZIMMER",
        bathrooms: "BADEZIMMER",
        floorArea: "WOHNFLÄCHE",
        type: "TYP",
        similarProperties: "Ähnliche Immobilien",
        notFound: "Immobilie nicht gefunden",
        backToListings: "Zurück zu den Angeboten",
        noPhoto: "Noch kein Foto für diesen Raum hochgeladen",
        photoLabel: "Foto",
        report: "Melden",
        perMonth: "Pro Monat",
        perMonthShort: "/Monat",
        messageSent: "Nachricht gesendet! Weiterleitung zu deinem Posteingang…",
        sending: "Wird gesendet…",
        messageLandlord: "Vermieter kontaktieren",
        description: "Beschreibung",
        features: "Ausstattung und Merkmale",
        rooms: {
          livingRoom: "Wohnzimmer",
          masterBedroom: "Hauptschlafzimmer",
          kitchen: "Küche",
          bathroom: "Badezimmer",
          gardenTerrace: "Garten / Terrasse",
          exterior: "Außenbereich",
        },
      },
      footer: {
        tagline: "Finde den Ort, den du Zuhause nennen kannst.",
        findYourHome: "FINDE DEIN ZUHAUSE",
        services: "LEISTUNGEN",
        company: "UNTERNEHMEN",
        housesForSale: "Häuser zum Verkauf",
        apartments: "Wohnungen",
        villas: "Villen",
        forRent: "Zur Miete",
        commercial: "Gewerbe",
        propertyValuation: "Immobilien-Wertermittlung",
        renovationSimulator: "Renovierungs-Tool",
        neighbourhoodIntel: "Nachbarschafts-Analyse",
        findABroker: "Makler finden",
        listAProperty: "Immobilie inserieren",
        whoWeAre: "Über uns",
        contactUs: "Kontakt",
        privacyPolicy: "Datenschutzrichtlinie",
        termsOfUse: "Nutzungsbedingungen",
        cookiesPolicy: "Cookie-Richtlinie",
        joinAsAgent: "Als Makler beitreten",
        faq: "FAQ",
        legal: "Rechtliches",
        terms: "Bedingungen",
        privacy: "Datenschutz",
        cookies: "Cookies",
        disclaimer: "Haftungsausschluss",
        acceptableUse: "Nutzungsrichtlinien",
        billingRefunds: "Abrechnung & Rückerstattungen",
        manageCookies: "Cookies verwalten",
        copyright: "© 2026 CosmoEstates. Alle Rechte vorbehalten. — Immobilienportal Zypern",
      },
      common: {
        loading: "Wird geladen…",
        error: "Etwas ist schiefgelaufen",
        retry: "Erneut versuchen",
        cancel: "Abbrechen",
        save: "Speichern",
        close: "Schließen",
        next: "Weiter",
        back: "Zurück",
      },
      cta: {
        title: "Bist du Makler oder Immobilieneigentümer?",
        sub: "Inseriere deine Immobilie kostenlos und erreiche jeden Monat über 2,4 Millionen aktive Käufer und Mieter. Bring dein Inserat in wenigen Minuten online.",
        freeListings: "Kostenlose Inserate",
        verifiedBadge: "Verifiziert-Abzeichen",
        analytics: "Analyse-Dashboard",
        directEnquiries: "Direkte Anfragen",
        postListing: "Kostenlos inserieren",
        learnMore: "Mehr erfahren",
      },
      categories: {
        title: "Nach Kategorie stöbern",
        sub: "Entdecke Tausende Angebote in jedem Immobilientyp",
        viewAll: "Alle Kategorien ansehen →",
        listingsSuffix: "Angebote",
        names: {
          houses: "Häuser",
          apartments: "Wohnungen",
          villas: "Villen",
          commercial: "Gewerbe",
          newBuilds: "Neubauten",
          landPlots: "Grundstücke",
          holiday: "Ferien",
          offices: "Büros",
          parking: "Stellplätze",
          foreclosure: "Zwangsversteigerung",
        },
      },
      partners: {
        discover: "Entdecke unsere Partner",
        viewAll: "Alle Partner ansehen",
        eyebrow: "Vertrauenswürdige Partner",
        title: "Mit den Besten Zyperns",
        subtitle: "Geprüfte Agenturen in ganz Zypern, sorgfältig ausgewählt nach Qualität und Service.",
        discoverAll: "Alle Partner entdecken →",
      },
      allPartners: {
        badge: "Unsere Partner",
        title: "Lerne die Partner von CosmoEstates kennen",
        subtitle: "Sieh dir die Agenturen und Fachleute an, die mit CosmoEstates Käufer, Mieter und Verkäufer in ganz Zypern zusammenbringen.",
        loading: "Partner werden geladen…",
        viewListings: "Angebote ansehen",
        fairnessNote: "Die Reihenfolge der Partner wird bei jedem Seitenaufruf zufällig neu gemischt, damit alle Agenturen gleiche Sichtbarkeit erhalten.",
        noPartnersFound: "Keine Partner gefunden",
      },
      card: {
        beds: "Schlafzi.",
        baths: "Bäder",
        bedrooms: "Schlafzimmer",
        bathrooms: "Badezimmer",
        forSale: "Zum Verkauf",
        forRent: "Zur Miete",
        view: "Ansehen →",
        viewDetails: "Details ansehen →",
        perMonth: "/Mo.",
        boosted: "Boosted",
        featured: "Empfohlen",
        soldBadge: "VERKAUFT",
        partnerBadge: "Partner",
        prevPhoto: "Vorheriges Foto",
        nextPhoto: "Nächstes Foto",
        savePropertyAria: "Immobilie merken",
      },
      progate: {
        badge: "CosmoEstates Pro",
        signInRequired: "Anmeldung erforderlich",
        proRequired: "Pro-Zugang erforderlich",
        signInMsg: "Melde dich an und hol dir Premium, um {feature} zu nutzen.",
        proMsg: "{feature} ist eine Pro-exklusive Funktion. Upgrade auf CosmoEstates Pro, um sie freizuschalten.",
        signIn: "Anmelden",
        createAccount: "Konto erstellen",
        viewPlans: "Pro-Tarife ansehen",
        noteAuth: "Nach der Anmeldung auf Pro upgraden",
        notePro: "Jederzeit kündbar · Sichere Zahlung über Stripe",
        features: {
          renovation: "Renovierungs-Tool",
          neighbourhood: "Nachbarschafts-Analyse",
          alerts: "Priorisierte Angebots-Benachrichtigungen",
          inbox: "Priorisierter Verkäufer-Posteingang",
          reports: "Angebots-Detailberichte",
        },
      },
      login: {
        welcomeBack: "Willkommen zurück",
        sub: "Melde dich an, um auf deine Merkliste und Benachrichtigungen zuzugreifen.",
        emailAddress: "E-Mail-Adresse",
        emailRequired: "E-Mail ist erforderlich",
        validEmail: "Gib eine gültige E-Mail-Adresse ein",
        password: "Passwort",
        passwordRequired: "Passwort ist erforderlich",
        forgotPassword: "Passwort vergessen?",
        signIn: "Anmelden",
        noAccount: "Noch kein Konto?",
        registerNow: "Jetzt registrieren",
        continueWithGoogle: "Mit Google fortfahren",
        orSignInEmail: "oder mit E-Mail anmelden",
        heroTitleA: "Beginne deine",
        heroTitleB: "Zypern-Immobilienreise",
        heroTitleC: "hier",
        heroSubtitle: "Entdecke Häuser, vernetze dich mit Maklern und finde deinen Platz unter der Sonne Zyperns.",
      },
      register: {
        createAccount: "Erstelle dein Konto",
        startTrialTitle: "Starte deine kostenlose Testphase",
        sub: "Schließ dich Tausenden cleverer Immobiliensuchender in Zypern an.",
        trialSub: "5 Tage voller Pro-Zugang. Keine Kreditkarte nötig.",
        trialBanner: "Du meldest dich für eine 5-tägige kostenlose Pro-Testphase an",
        trialDesc: "Sofortiger Zugang zu allen Pro-Funktionen — Renovierungsberichte, Nachbarschafts-Analyse, priorisierte Benachrichtigungen und mehr. Keine Karte nötig.",
        firstName: "Vorname",
        lastName: "Nachname",
        emailAddress: "E-Mail-Adresse",
        phoneNumber: "Telefonnummer",
        phoneOptional: "(optional)",
        password: "Passwort",
        passwordPlaceholder: "Mindestens 6 Zeichen",
        firstNameRequired: "Vorname ist erforderlich",
        lastNameRequired: "Nachname ist erforderlich",
        validEmail: "Gib eine gültige E-Mail-Adresse ein",
        passwordMin: "Das Passwort muss mindestens 6 Zeichen lang sein",
        startFreeTrial: "Kostenlose Testphase starten",
        createAccountBtn: "Konto erstellen",
        trialNote: "Die Testphase dauert 5 Tage ab der Anmeldung. Nach Ablauf wirst du automatisch auf den kostenlosen Tarif zurückgestuft. Upgrade jederzeit auf Pro, um vollen Zugang zu behalten.",
        alreadyHaveAccount: "Hast du schon ein Konto?",
        signIn: "Anmelden",
        continueWithGoogle: "Mit Google fortfahren",
        orSignUpEmail: "oder mit E-Mail registrieren",
        heroTitleA: "Finde dein Zuhause",
        heroTitleB: "unter der Sonne",
        heroTitleC: "Zyperns",
        heroSubtitle: "Schließ dich Tausenden an, die Immobilien in Limassol, Paphos, Nikosia und darüber hinaus entdecken.",
      },
      forgot: {
        title: "Passwort zurücksetzen",
        sub: "Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.",
        emailAddress: "E-Mail-Adresse",
        sendLink: "Reset-Link senden",
        rememberIt: "Wieder eingefallen?",
        signIn: "Anmelden",
        checkEmail: "Prüfe deine E-Mails",
        sentMsg: "Wir haben einen Reset-Link an {email} gesendet. Klicke ihn an, um ein neues Passwort festzulegen.",
        notReceived: "Nicht erhalten? Prüfe den Spam-Ordner oder",
        tryAgain: "erneut versuchen",
        validEmail: "Gib eine gültige E-Mail-Adresse ein",
      },
      profile: {
        title: "Mein Profil",
        sub: "Verwalte deine persönlichen Informationen",
        personalDetails: "Persönliche Angaben",
        firstName: "Vorname",
        lastName: "Nachname",
        emailAddress: "E-Mail-Adresse",
        emailCannotChange: "E-Mail kann hier nicht geändert werden",
        phoneNumber: "Telefonnummer",
        changesSaved: "Änderungen gespeichert",
        saveChanges: "Änderungen speichern",
        saving: "Wird gespeichert...",
        proMembership: "Pro-Mitgliedschaft",
        proActive: "Pro aktiv",
        freePlan: "Kostenloser Tarif",
        proActiveMsg: "Du hast vollen Zugriff auf alle Funktionen von CosmoEstates Pro.",
        freeMsg: "Upgrade auf CosmoEstates Pro, um Renovierungsberichte, Nachbarschafts-Analyse, priorisierte Benachrichtigungen und mehr freizuschalten.",
        upgradePro: "Auf Pro upgraden",
        manageSubscription: "Abonnement verwalten",
        portalLoading: "Wird geöffnet…",
        portalNotSignedIn: "Bitte melde dich erneut an, um dein Abonnement zu verwalten.",
        portalNoSubscription: "Es ist kein Abonnement mit deinem Konto verknüpft. Schreib an support@cosmoestates.cy und wir kümmern uns darum.",
        portalFailed: "Abo-Verwaltung konnte nicht geöffnet werden. Bitte versuche es erneut oder kontaktiere den Support.",
        portalMissingBefore: "Wir finden deine Abo-Daten in unserem System nicht. Um dein Pro-Abo zu kündigen oder zu verwalten, schreib bitte an ",
        portalMissingAfter: " und wir kümmern uns innerhalb von 24 Stunden darum.",
        failedSave: "Änderungen konnten nicht gespeichert werden. Bitte versuche es erneut.",
      },
      checkout: {
        back: "Zurück",
        choosePlan: "Wähle deinen Tarif",
        unlockPro: "Schalte das volle CosmoEstates-Pro-Erlebnis frei",
        emailAddress: "E-Mail-Adresse",
        everythingIncluded: "Alles enthalten:",
        paySecure: "Zahle €{amount} — Sicherer Checkout",
        ssl: "256-bit SSL",
        pci: "PCI DSS",
        poweredByStripe: "Powered by Stripe",
        terms: "Du wirst zur sicheren Zahlungsseite von Stripe weitergeleitet. Mit deinem Kauf stimmst du unseren Nutzungsbedingungen zu. Jederzeit über dein Profil kündbar.",
        termsLink: "Nutzungsbedingungen",
        orderSummary: "Bestellübersicht",
        productName: "CosmoEstates Pro",
        priceVat: "Preis (inkl. MwSt.)",
        total: "Gesamt",
        youSave: "Du sparst {discount}",
        moreFeatures: "+ {n} weitere Funktionen",
        planSuffix: "-Tarif",
        freeTrial: "Kostenlose Testphase",
        trialDays: "5 Tage komplett kostenlos",
        trialSub: "Voller Pro-Zugang. Keine Kreditkarte. Jederzeit kündbar.",
        yourName: "Dein Name",
        startTrialBtn: "Meine 5-tägige kostenlose Testphase starten",
        noPayment: "Keine Zahlungsinfos nötig. Nach 5 Tagen wirst du auf den kostenlosen Tarif verschoben, sofern du kein Upgrade machst.",
        whatYouGetFree: "Was du kostenlos erhältst",
        daysProAccess: "5 Tage Pro-Zugang",
        noCreditCard: "Keine Kreditkarte erforderlich.",
        viewPaidPlans: "Stattdessen kostenpflichtige Tarife ansehen",
        trialLive: "Deine Testphase läuft!",
        trialMsg: "Du hast 5 Tage kostenlosen Pro-Zugang. Keine Kreditkarte nötig — entdecke alles, was CosmoEstates Pro zu bieten hat.",
        account: "Konto",
        trialEnds: "Testphase endet",
        cardNeeded: "Karte nötig?",
        no: "Nein",
        trialNote: "Wir senden eine Erinnerung, bevor deine Testphase endet. Du kannst jederzeit über dein Profil upgraden oder kündigen.",
        startExploring: "Loslegen",
        validEmail: "Gültige E-Mail erforderlich",
        nameRequired: "Name erforderlich",
        features: {
          priorityInbox: "Priorisierter Posteingang",
          neighbourhoodIntel: "Nachbarschafts-Analyse",
          smartAlerts: "Smarte Benachrichtigungen",
          priceDropAlerts: "Preissenkungs-Alerts",
          pdfReports: "PDF-Berichte",
        },
        mostPopular: "Am beliebtesten",
        plans: {
          monthly: {
            label: "Monatlich",
            note: "Monatliche Abrechnung",
          },
          quarterly: {
            label: "Vierteljährlich",
            note: "EUR 10,19 / Monat",
            badge: "Am beliebtesten",
            discount: "21 % sparen",
          },
          yearly: {
            label: "Jährlich",
            note: "EUR 9,54 / Monat",
            discount: "26 % sparen",
          },
        },
      },
      settings: {
        title: "Einstellungen",
        sub: "Verwalte deine Kontopräferenzen",
        notifications: "Benachrichtigungen",
        notif: {
          newListings: "Neue passende Angebote",
          newListingsDesc: "Werde benachrichtigt, wenn eine Immobilie, die zu deiner Suche passt, online geht",
          priceDrops: "Preissenkungen",
          priceDropsDesc: "Werde benachrichtigt, wenn eine gemerkte Immobilie im Preis sinkt",
          priceDropsLocked: "Upgrade auf Pro, um Preissenkungs-Alerts zu aktivieren",
          messages: "Nachrichten",
          messagesDesc: "Erhalte Benachrichtigungen für neue Nachrichten von Verkäufern",
          newsletter: "Wöchentlicher Newsletter",
          newsletterDesc: "Marktupdates und ausgewählte Immobilien jede Woche",
        },
        security: "Sicherheit",
        dangerZone: "Konto löschen",
        dangerDesc: "Lösche dein Konto und alle zugehörigen Daten dauerhaft, einschließlich deiner Inserate, Merkliste und Benachrichtigungen. Dies kann nicht rückgängig gemacht werden.",
        confirmDelete: "Bist du dir absolut sicher? Tippe DELETE zur Bestätigung:",
        deletePlaceholder: "DELETE tippen",
        deleting: "Wird gelöscht...",
        confirmDeleteBtn: "Ja, mein Konto löschen",
        deleteBtn: "Konto löschen",
        cancel: "Abbrechen",
        settingsSaved: "Einstellungen gespeichert",
        saveSettings: "Einstellungen speichern",
        saving: "Wird gespeichert…",
        password: {
          signedInAs: "Angemeldet als {email}. Zur Sicherheit senden wir dir einen Bestätigungscode per E-Mail, um diese Änderung zu bestätigen.",
          updated: "Passwort erfolgreich aktualisiert.",
          newPassword: "Neues Passwort",
          confirmPassword: "Neues Passwort bestätigen",
          placeholder: "Mindestens 6 Zeichen",
          confirmPlaceholder: "Neues Passwort wiederholen",
          updating: "Wird aktualisiert...",
          updateBtn: "Passwort aktualisieren",
          minLength: "Das Passwort muss mindestens 6 Zeichen lang sein",
          noMatch: "Passwörter stimmen nicht überein",
          sessionExpired: "Sitzung abgelaufen — ab- und wieder anmelden und erneut versuchen.",
          error: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
          sendCode: "Bestätigungscode senden",
          sending: "Code wird gesendet...",
          codeSent: "Wir haben einen Bestätigungscode an {email} gesendet. Gib ihn unten mit deinem neuen Passwort ein.",
          code: "Bestätigungscode",
          codePlaceholder: "Bestätigungscode",
          codeRequired: "Gib den Bestätigungscode ein, den wir dir gesendet haben.",
          invalidCode: "Ungültiger oder abgelaufener Code. Bitte fordere einen neuen an.",
          resendCode: "Neuen Code senden",
          reauthFailed: "Bestätigungscode konnte nicht gesendet werden. Bitte erneut versuchen.",
          cancelChange: "Abbrechen",
          resetTitle: "Passwort zurücksetzen",
          resetIntro: "Wir senden dir einen 8-stelligen Bestätigungscode per E-Mail an {email}, um zu bestätigen, dass du es bist. Anschließend gibst du dein aktuelles Passwort ein und wählst ein neues.",
          getCodeBtn: "Bestätigungscode anfordern",
          currentPassword: "Aktuelles Passwort",
          currentPasswordPlaceholder: "Aktuelles Passwort eingeben",
          currentPasswordRequired: "Gib dein aktuelles Passwort ein.",
          wrongCurrentPassword: "Das aktuelle Passwort ist falsch.",
          mustBeDifferent: "Das neue Passwort muss sich von deinem aktuellen unterscheiden.",
          tooWeak: "Das Passwort ist zu schwach. Verwende ein längeres, schwerer zu erratendes Passwort.",
        },
      },
      saved: {
        title: "Gemerkte Immobilien",
        propertySaved: "{n} Immobilie gemerkt",
        propertiesSaved: "{n} Immobilien gemerkt",
        sortBy: "Sortieren nach",
        savedOrder: "Reihenfolge des Merkens",
        advertisement: "Anzeige",
        placeAd: "Hier deine Anzeige platzieren",
        adSub: "Erreiche jeden Tag Tausende Immobiliensuchende in Zypern",
        getInTouch: "Kontakt aufnehmen →",
        noSaved: "Noch keine gemerkten Immobilien",
        heartProperty: "Klick auf das Herz einer Immobilie, um sie hier zu merken.",
        browseListings: "Angebote durchsuchen",
      },
      agentDashboard: {
        loading: "Dein Dashboard wird geladen…",
        pageTitle: "Meine Angebote",
        pageSubViews: "{views} Aufrufe gesamt · {active} aktiv",
        tabs: {
          listings: "Angebote",
          agentProfile: "Makler-Profil",
        },
        markSold: {
          title: "Als verkauft markieren?",
          message: "Das Angebot zeigt 4 Stunden lang ein „VERKAUFT“-Banner und wird dann automatisch archiviert.",
          confirm: "Ja, als verkauft markieren",
        },
        restore: {
          title: "Angebot wiederherstellen?",
          message: "Das Angebot wird wieder auf aktiv gesetzt und ist erneut in öffentlichen Suchergebnissen sichtbar.",
          confirm: "Ja, wiederherstellen",
        },
        delete: {
          title: "Dieses Angebot löschen?",
          message: "Damit wird das Angebot dauerhaft entfernt. Diese Aktion kann nicht rückgängig gemacht werden.",
          confirm: "Ja, löschen",
        },
      },
      valuation: {
        title: "Kostenlose Immobilien-Preisschätzung",
        eyebrow: "KOSTENLOSE PREISSCHÄTZUNG",
        subtitle: "Eine kostenlose Preisschätzung für jede Immobilie in Zypern. Ein Ausgangspunkt, keine offizielle Bewertung.",
        trust1: "100 % kostenlos",
        trust2: "Marktbasiert",
        trust3: "Sofortiges Ergebnis",
        trust4: "Keine Anmeldung nötig",
        back: "Zurück",
        backHome: "Zurück zur Startseite",
        continue: "Weiter",
        sending: "Wird gesendet...",
        getValuation: "Meine Schätzung erhalten",
        cityAvg: "Stadt-Durchschnitt",
        steps: ["Typ", "Lage", "Details", "Zustand", "E-Mail"],
        step1Title: "Welcher Immobilientyp ist es?",
        step2Title: "Wo liegt die Immobilie?",
        step3Title: "Größe und Details",
        step4Title: "Was ist der aktuelle Zustand?",
        step5Title: "Wohin sollen wir deinen Bericht senden?",
        step5Sub: "Wir senden dir eine Preisspanne für deine Immobilie, den üblichen Preis pro m² in deiner Gegend und eine indikative monatliche Mietspanne — als Ausgangspunkt, nicht als offizielle Bewertung.",
        gdpr: "Mit dem Absenden stimmst du zu, deine Schätzung per E-Mail zu erhalten. Wir respektieren deine Privatsphäre.",
        consent: {
          label: "Ich erlaube, dass diese Informationen an Immobilienmakler weitergeleitet werden",
          detail: "Geprüfte zyprische Makler, die auf deine Region spezialisiert sind, kontaktieren dich innerhalb von 48 Stunden. Du kannst dies jederzeit in deinen Profileinstellungen ändern.",
        },
        cityLabel: "Stadt",
        selectCity: "Stadt auswählen...",
        areaLabel: "Gebiet",
        optional: "(optional)",
        usingCityAvg: "{city}-Durchschnitt wird verwendet",
        plotSize: "Grundstücksgröße (m²)",
        coveredArea: "Wohnfläche (m²)",
        floorLabel: "Etage (optional)",
        bedroomsLabel: "Schlafzimmer",
        bathroomsLabel: "Badezimmer",
        featuresLabel: "Immobilien-Merkmale",
        firstNameLabel: "Vorname",
        emailLabel: "E-Mail-Adresse",
        resultTitle: "Deine Schätzung ist fertig",
        resultBasedOn: "Basierend auf",
        salePriceEstimate: "Schätzung Verkaufspreis",
        monthlyRentEstimate: "Schätzung monatliche Miete",
        perMonth: "pro Monat",
        summaryProperty: "Immobilie",
        summaryCity: "Stadt",
        summaryPlotSize: "Grundstücksgröße",
        summaryCoveredArea: "Wohnfläche",
        summaryBedrooms: "Schlafzimmer",
        summaryCondition: "Zustand",
        summaryFeatures: "Ausstattung",
        reportSent: "Eine indikative Schätzung wurde gesendet an",
        propertyTypes: {
          Apartment: "Wohnung",
          House: "Haus",
          Villa: "Villa",
          Commercial: "Gewerbe",
          Land: "Grundstück",
        },
        conditions: {
          brandNew: "Neu",
          brandNewDesc: "Neu gebaut oder nie bewohnt",
          excellent: "Sehr gut",
          excellentDesc: "Kürzlich renoviert, keine Arbeiten nötig",
          good: "Gut",
          goodDesc: "Gut gepflegt, kleinere Auffrischungen",
          needsRenovation: "Renovierungsbedürftig",
          needsRenovationDesc: "Umfangreiche Arbeiten erforderlich",
        },
        features: {
          seaView: "Meerblick",
          privatePool: "Privater Pool",
          coveredParking: "Überdachter Stellplatz",
          communalPool: "Gemeinschaftspool",
          balconyTerrace: "Balkon / Terrasse",
          airConditioning: "Klimaanlage",
          solarPanels: "Solaranlage",
          fireplace: "Kamin",
        },
        heroEyebrow: "ZYPERN-IMMOBILIEN-TOOL",
        heroTitle: "Immobilien-Preisschätzung",
        heroSubtitle: "Erhalte eine kostenlose Orientierung zur wahrscheinlichen Preisspanne deiner Immobilie. Unsere Schätzung ist datenbasiert, aber keine offizielle Bewertung. Für eine zertifizierte Bewertung ",
        requestPro: "kontaktiere einen zugelassenen Sachverständigen",
        reasonLabel: "Was ist dein Grund für eine Schätzung?",
        reasonOptional: "optional",
        reasons: {
          just_exploring: "Nur zur Orientierung",
          thinking_about: "Denke über einen Verkauf nach",
          within_6_months: "Innerhalb von 6 Monaten",
          within_1_year: "Verkauf innerhalb 1 Jahr",
        },
        cities: {
          Limassol: "Limassol",
          Paphos: "Paphos",
          Nicosia: "Nikosia",
          Larnaca: "Larnaka",
          "Ayia Napa": "Agia Napa",
          Protaras: "Protaras",
          Paralimni: "Paralimni",
        },
        areaPlaceholder: "z. B. Germasogeia, Mackenzie...",
        plotPlaceholder: "z. B. 500",
        coveredPlaceholder: "z. B. 120",
        landPlotPlaceholder: "z. B. 1000",
        firstNamePlaceholder: "z. B. Nikolas",
        emailPlaceholder: "du@beispiel.com",
        disclaimerNote: "Hinweis:",
        disclaimerText: "Dies ist eine automatisierte Schätzung basierend auf zyprischen Marktdaten und aktuellen vergleichbaren Transaktionen. Sie ist",
        disclaimerNotProfessional: "keine professionelle Bewertung",
        disclaimerTextCont: "und sollte nicht für rechtliche, finanzielle oder Transaktionszwecke verwendet werden. Für eine offizielle Bewertung wende dich an einen zugelassenen RICS-Sachverständigen.",
        privacyPrefix: "Ich stimme den",
        privacyPolicy: "Datenschutzrichtlinien",
        privacyAnd: "und",
        termsOfService: "Nutzungsbedingungen",
        privacySuffix: " von CosmoEstates zu und willige ein, dass meine Daten zur Erstellung dieser Schätzung verarbeitet werden.",
        privacyAlert: "Bitte stimme den Datenschutzrichtlinien und Nutzungsbedingungen zu, um fortzufahren.",
        successTitle: "Deine Schätzung ist unterwegs",
        successMsg: "Wir bereiten deine indikative Immobilien-Schätzung vor und senden sie per E-Mail an",
        successMsgSuffix: "innerhalb der nächsten Minuten.",
        successHint: "Prüfe dein Postfach (auch den Spam-Ordner). Du erhältst eine Verkaufspreisspanne, eine indikative monatliche Mietspanne und den üblichen Preis pro m² in deiner Gegend. Betrachte es als Ausgangspunkt — ",
        successBackHome: "Zurück zur Startseite →",
        selectOption: "Auswählen…",
        dontKnow: "Weiß ich nicht",
        improvesAccuracy: "verbessert die Genauigkeit",
        yearBuiltLabel: "Baujahr",
        yearBuilt: {
          opt2020newer: "2020 oder neuer",
          opt2010_2019: "2010–2019",
          opt2000_2009: "2000–2009",
          opt1990_1999: "1990–1999",
          optBefore1990: "Vor 1990",
          optUnknown: "Weiß ich nicht",
        },
        energyRatingLabel: "Energieausweis",
        titleDeedLabel: "Titelurkunde",
        titleDeedTooltip: "Der Status der Titelurkunde beeinflusst zyprische Immobilienwerte erheblich.",
        titleDeed: {
          optAvailable: "Vorhanden",
          optPending: "In Bearbeitung",
          optNone: "Keine",
        },
        parkingLabel: "Parken",
        parking: {
          optCovered: "Überdacht / Privat",
          optOpen: "Offen / Zugewiesen",
          optStreet: "Nur Straße",
          optNone: "Keiner",
        },
        furnishedLabel: "Möblierung",
        furnished: {
          optFully: "Voll möbliert",
          optPartially: "Teilmöbliert",
          optUnfurnished: "Unmöbliert",
        },
        distanceToSeaLabel: "Entfernung zum Meer",
        seaDistance: {
          optSeafront: "Direkt am Meer (0–100 m)",
          optWalking: "Zu Fuß erreichbar (100–500 m)",
          optDrive: "Kurze Fahrt (500 m–2 km)",
          optInland: "Im Landesinneren (2 km+)",
        },
        step5TitleV2: "Details, die deine Schätzung verbessern",
        step5SubV2: "Überspringe, wenn unsicher — aber diese Angaben verbessern die Genauigkeit deiner Schätzung.",
        step5Empty: "Keine zusätzlichen Details treffen auf diese Immobilie zu. Weiter, um die Schätzung zu senden.",
        renovationYearLabel: "Letzte Renovierung",
        renovation: {
          opt2020plus: "2020+",
          opt2015_2019: "2015–2019",
          opt2010_2014: "2010–2014",
          optOriginal: "Original / nie renoviert",
          optNotSure: "Unsicher",
        },
        orientationLabel: "Hauptausrichtung",
        orientation: {
          optSouth: "Süden",
          optNorth: "Norden",
          optEast: "Osten",
          optWest: "Westen",
          optCorner: "Eckposition (mehrere)",
          optNotSure: "Unsicher",
        },
        buildingTypeLabel: "Gebäudetyp",
        buildingType: {
          optLow: "Niedrigbau (1–3 Etagen)",
          optMid: "Mittelhoch (4–8 Etagen)",
          optHigh: "Hochhaus (9+)",
          optBoutique: "Boutique-Gebäude (<10 Einheiten)",
        },
        confidence: {
          high: "Hohe Verlässlichkeit",
          medium: "Mittlere Verlässlichkeit",
          low: "Geringe Verlässlichkeit",
          estimateOnly: "Nur Schätzung",
          subHigh: "Basierend auf einer starken Auswahl an Vergleichsobjekten und detaillierten Angaben.",
          subMedium: "Basierend auf soliden Marktdaten. Für höhere Genauigkeit ergänze die optionalen Angaben in Schritt 5.",
          subLow: "Begrenzte Vergleichsobjekte in dieser Region — als Richtungsangabe verstehen.",
          subEstimate: "Nicht genug Daten in dieser Region für eine datengestützte Schätzung.",
        },
        result: {
          eyebrow: "DEINE SCHÄTZUNG",
          title: "Immobilien-Preisschätzung",
          saleRange: "Indikative Verkaufsspanne",
          rentRange: "Indikative monatliche Miete",
          perMonth: " /Mo.",
          pricePerSqm: "Üblicher Preis pro m²",
          howCalculated: "Wie wir diese Schätzung berechnet haben",
          baseLabel: "Basis pro m² ({source})",
          basedOnN: "Basierend auf {n} aktuellen Angeboten",
          totalAdjustment: "Gesamtanpassung",
          cappedNote: "Die Gesamtanpassung wurde von {raw} begrenzt, um in sicheren Bereichen zu bleiben.",
          comparablesTitle: "Ähnliche Angebote als Referenz",
          comparablesLoading: "Ähnliche Angebote werden geladen…",
          comparablesEmpty: "Wir haben in deiner Region noch nicht genug Angebote für Vergleichsobjekte.",
          findAgentCTA: "Mit einem geprüften Makler sprechen",
          newEstimate: "Neue Schätzung starten",
          legal: "Diese Schätzung ist datenbasiert, aber keine offizielle Bewertung. Für eine zertifizierte Bewertung kontaktiere einen zugelassenen Sachverständigen.",
        },
        errors: {
          propertyType: "Bitte wähle einen Immobilientyp",
          city: "Stadt ist erforderlich",
          plotSize: "Grundstücksgröße ist erforderlich",
          coveredArea: "Wohnfläche ist erforderlich",
          condition: "Bitte wähle einen Zustand",
          firstName: "Vorname ist erforderlich",
          email: "E-Mail ist erforderlich",
          emailInvalid: "Gib eine gültige E-Mail-Adresse ein",
          yearBuilt: "Bitte wähle, wann die Immobilie gebaut wurde",
          titleDeed: "Bitte wähle den Status der Titelurkunde",
          parkingType: "Bitte wähle den Parkplatztyp",
          furnished: "Bitte wähle den Möblierungsstatus",
          distanceToSea: "Bitte wähle die Entfernung zum Meer",
        },
      },
      findAgent: {
        stepIndicator: "Schritt {step} von {total}",
        continue: "Weiter →",
        back: "← Zurück",
        finding: "Deine Makler werden gesucht...",
        findMyAgents: "Meine Makler finden",
        errorGeneric: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
        step1Title: "Wonach suchst du?",
        step1Sub: "Lass uns den passenden Makler für deine Situation finden.",
        lookingFor: {
          sell: "Meine Immobilie verkaufen",
          sellSub: "Inserieren und den richtigen Käufer finden",
          buy: "Eine Immobilie kaufen",
          buySub: "Finde dein nächstes Zuhause",
          rent: "Eine Immobilie mieten",
          rentSub: "Finde einen Ort zum Wohnen",
          rent_out: "Meine Immobilie vermieten",
          rent_outSub: "Gute Mieter finden",
        },
        step2Title: "Erzähl uns von der Immobilie",
        step2Sub: "Nur die Basics — deine passenden Makler verfeinern die Details.",
        propertyTypeLabel: "Immobilientyp",
        propertyTypes: {
          apartment: "Wohnung",
          house: "Haus",
          villa: "Villa",
          land: "Grundstück",
          commercial: "Gewerbe",
        },
        cityLabel: "Stadt",
        selectCity: "Stadt auswählen",
        districtLabel: "Bezirk",
        districtPlaceholder: "z. B. Germasogeia",
        optional: "(optional)",
        bedroomsLabel: "Schlafzimmer",
        monthlyBudget: "Monatliches Budget (EUR)",
        budgetRange: "Budget-Spanne (EUR)",
        fromPlaceholder: "Von",
        toPlaceholder: "Bis",
        step3TitleSell: "Erzähl uns von deiner Situation",
        step3TitleBuy: "Ein bisschen mehr über dich",
        step3SubSell: "Das hilft deinen Maklern, den richtigen Ansatz vorzubereiten.",
        step3SubBuy: "Das hilft uns, die für dich passenden Makler auszuwählen.",
        situationLabel: "Was beschreibt dich am besten?",
        timeframeSellLabel: "Wann willst du am Markt sein?",
        timeframeBuyLabel: "Wann möchtest du umziehen?",
        situations: {
          sell: {
            specific_timeline: "Ich muss innerhalb eines bestimmten Zeitraums verkaufen",
            exploring: "Ich prüfe meine Optionen",
            inheritance: "Ich habe die Immobilie geerbt",
            relocate: "Ich möchte umziehen",
          },
          buy: {
            first_time: "Ich bin Erstkäufer",
            investor: "Ich investiere für Mieteinnahmen",
            relocating_to_cyprus: "Ich ziehe nach Zypern",
            golden_visa: "Kauf für Aufenthalt / Golden Visa",
          },
          rent: {
            short_term: "Kurzfristig (unter 6 Monate)",
            long_term: "Langfristig (1 Jahr oder mehr)",
            relocating_to_cyprus: "Ich ziehe nach Zypern",
            students_or_family: "Mieten mit Familie oder Studierenden",
          },
          rent_out: {
            short_term_rental: "Als Kurzzeitmiete einrichten (Airbnb)",
            long_term_rental: "Langfristige Mieter finden",
            need_management: "Ich brauche komplette Immobilienverwaltung",
            just_tenant_matching: "Nur Mietervermittlung, ich verwalte selbst",
          },
        },
        timeframesSell: {
          immediate: "So bald wie möglich",
          within_3_months: "Innerhalb von 3 Monaten",
          within_6_months: "Innerhalb von 6 Monaten",
          within_12_months: "Innerhalb von 12 Monaten",
          within_24_months: "Innerhalb von 24 Monaten",
          just_exploring: "Ich bin flexibel",
        },
        moveIn: {
          asap: "So bald wie möglich",
          within_6_months: "Innerhalb von 6 Monaten",
          within_12_months: "Innerhalb von 12 Monaten",
          within_24_months: "Innerhalb von 24 Monaten",
          flexible: "Ich bin flexibel",
        },
        step4Title: "Was ist dir am wichtigsten?",
        step4Sub: "Wähle das, was am meisten zählt.",
        priorities: {
          sell: {
            best_price: "Den besten Preis erzielen, auch wenn es länger dauert",
            fast_sale: "Schnell verkaufen, auch wenn ich etwas Geld liegen lasse",
            full_process: "Makler übernimmt den kompletten Prozess",
            language: "Kommunikation in meiner Wunschsprache",
            privacy: "Verkauf privat und diskret halten",
          },
          buy: {
            best_location: "Die perfekte Lage finden",
            best_value: "Das beste Preis-Leistungs-Verhältnis für mein Geld",
            exclusive: "Zugang zu Off-Market- und exklusiven Angeboten",
            language: "Kommunikation in meiner Wunschsprache",
            full_process: "Makler übernimmt Papierkram und Rechts-Koordination",
          },
          rent: {
            best_location: "Die perfekte Lage finden",
            best_value: "Das beste Preis-Leistungs-Verhältnis für mein Budget",
            fast_response: "Schnelle Antworten und rasche Besichtigungen",
            language: "Kommunikation in meiner Wunschsprache",
            furnished_ready: "Möbliert und einzugsbereit",
          },
          rent_out: {
            vetted_tenants: "Geprüfte, zuverlässige Mieter",
            max_yield: "Maximale Mietrendite",
            full_management: "Ohne Aufwand — volle Immobilienverwaltung",
            language: "Kommunikation in meiner Wunschsprache",
            fast_fill: "Leerstand so schnell wie möglich füllen",
          },
        },
        step5Title: "Wie können Makler dich erreichen?",
        step5Sub: "3 passende Makler melden sich innerhalb von 24–48 Stunden.",
        fullNameLabel: "Vollständiger Name",
        fullNamePlaceholder: "Dein Name",
        emailLabel: "E-Mail",
        emailPlaceholder: "du@email.com",
        phoneLabel: "Telefon",
        phonePlaceholder: "+357 99 123456",
        contactMethodLabel: "Kontaktmethode",
        contactMethods: {
          email: "E-Mail",
          call: "Anrufen",
          whatsapp: "WhatsApp",
        },
        consentLabel: "Ich erlaube, dass meine Kontaktdaten mit bis zu 3 geprüften Immobilienmaklern geteilt werden, die zu meinen Anforderungen passen",
        success: {
          headline: "Du bist bereit!",
          sublinePrefix: "Wir haben dich vermittelt an",
          sublineMid: "top bewertete Makler in",
          sublineSuffix: "Sie melden sich innerhalb von 24–48 Stunden mit ihrem vorgeschlagenen Vorgehen.",
          yourArea: "deiner Region",
          step1Title: "Makler prüfen deine Angaben",
          step1Text: "Jeder passende Makler bereitet sein personalisiertes Angebot vor.",
          step2Title: "Du erhältst ihre Angebote",
          step2Text: "Innerhalb von 24–48 Stunden über deine bevorzugte Kontaktmethode.",
          step3Title: "Vergleichen und wählen",
          step3Text: "Wähle den Makler, der sich richtig anfühlt — oder keinen, wenn du lieber warten willst.",
          matchedHeading: "Deine passenden Makler",
          loading: "Die besten Makler für dich werden gefunden...",
          emptyPrefix: "Wir vermitteln dich persönlich mit Maklern in",
          emptySuffix: "Du hörst innerhalb von 48 Stunden von uns.",
          backHome: "Zurück zur Startseite",
        },
      },
      featured: {
        boostedListings: "Empfohlene Angebote",
        viewAll: "Alle ansehen",
        viewAllListings: "Alle Angebote ansehen",
      },
      authModal: {
        loginTitle: "Willkommen zurück",
        registerTitle: "Erstelle dein Konto",
        loginSub: "Melde dich an, um auf deine Merkliste und Benachrichtigungen zuzugreifen.",
        registerSub: "Werde Teil von CosmoEstates, um Immobilien zu merken und Benachrichtigungen zu erhalten.",
        firstName: "Vorname",
        lastName: "Nachname",
        email: "E-Mail-Adresse",
        phone: "Telefonnummer",
        password: "Passwort",
        confirmPassword: "Passwort bestätigen",
        repeatPassword: "Passwort wiederholen",
        forgotPassword: "Passwort vergessen?",
        signIn: "Anmelden",
        createAccount: "Konto erstellen",
        noAccount: "Noch kein Konto?",
        registerNow: "Jetzt registrieren",
        haveAccount: "Hast du schon ein Konto?",
        signInLink: "Anmelden",
        atLeast6: "Mindestens 6 Zeichen",
        firstNameRequired: "Vorname ist erforderlich",
        lastNameRequired: "Nachname ist erforderlich",
        phoneRequired: "Telefonnummer ist erforderlich",
        passwordsNoMatch: "Passwörter stimmen nicht überein",
        passwordMin: "Das Passwort muss mindestens 6 Zeichen lang sein",
        emailInvalid: "Gib eine gültige E-Mail-Adresse ein",
        passwordRequired: "Passwort ist erforderlich",
      },
      statsBar: {
        activeListings: "Aktive Angebote",
        propertyTypes: "Immobilientypen",
        citiesCovered: "Abgedeckte Städte",
        registeredUsers: "Registrierte Nutzer",
      },
      boostSuccess: {
        activating: "Dein Boost wird aktiviert…",
        activatingSub: "Bitte warte, während wir deine Zahlung bestätigen.",
        success: "Dein Angebot ist geboostet!",
        successSub: "Dein Angebot erhält jetzt vorrangige Platzierung in den Suchergebnissen und rotiert 14 Tage lang durch das Featured-Karussell auf der Startseite.",
        viewMyListings: "Meine Angebote ansehen",
        backHome: "Zurück zur Startseite",
      },
      partner: {
        notFound: "Partner nicht gefunden",
        back: "Zurück",
        backHome: "Zurück zur Startseite",
        founded: "Gegründet",
        contactUs: "Kontakt",
        listings: "Angebote",
        citiesServed: "Bediente Städte",
        loading: "Wird geladen...",
        noListings: "Noch keine Angebote.",
      },
      notifications: {
        title: "Benachrichtigungen",
        pageTitle: "Posteingang",
        inbox: "Posteingang",
        alerts: "Meldungen",
        loading: "Wird geladen...",
        noMessagesYet: "Noch keine Nachrichten",
        markAllRead: "Alle als gelesen markieren",
        noAlerts: "Noch keine Meldungen.",
        noAlertsSub: "Preissenkungen, neue Treffer und Kontoaktivitäten erscheinen hier.",
        selectConvo: "Konversation auswählen",
        selectConvoSub: "Wähle links einen Nachrichten-Thread, um ihn zu lesen und zu antworten",
        preview: "Vorschau",
        demoPlaceholder: "Dies ist eine Demo-Konversation…",
        typeMessage: "Nachricht schreiben…",
        back: "Zurück",
        loadingConvos: "Konversationen werden geladen…",
        noConvos: "Noch keine Konversationen",
        noConvosSub: "Kontaktiere einen Makler in einem beliebigen Angebot, um eine Konversation zu starten",
        browseListings: "Angebote durchsuchen",
        sayHello: "Noch keine Nachrichten. Sag Hallo!",
        justNow: "Gerade eben",
      },
      inbox: {
        title: "Posteingang",
        sub: "Nachrichten von Käufern und Maklern zu deinen Angeboten.",
        loading: "Nachrichten werden geladen…",
        empty: "Noch keine Nachrichten",
        emptySub: "Wenn sich Käufer oder Makler zu einem Angebot melden, landet die Konversation hier.",
        browseListings: "Angebote durchsuchen",
        unreadOne: "1 ungelesen",
        unreadMany: "{n} ungelesen",
      },
      myListings: {
        title: "Meine Angebote",
        sub: "Immobilien, die du auf CosmoEstates inseriert hast",
        loading: "Deine Angebote werden geladen…",
        noListings: "Noch keine Angebote",
        noListingsSub: "Du hast noch keine Immobilien eingestellt. Leg los und erreiche Tausende Käufer.",
        postListing: "+ Angebot einstellen",
        addListing: "+ Angebot hinzufügen",
        boostListings: "Angebote boosten",
        boost: "Boost",
        boosted: "Boosted",
        daysLeft: "Noch {n} Tage",
        edit: "Bearbeiten",
        view: "Ansehen",
        delete: "Löschen",
        markSold: "Als verkauft markieren",
        restore: "Wiederherstellen",
        filterActive: "Aktiv",
        filterSold: "Verkauft",
        filterArchived: "Archiviert",
        filterAll: "Alle",
        filterEmpty: "Keine Angebote in dieser Ansicht.",
        statusSold: "VERKAUFT",
        statusArchived: "ARCHIVIERT",
        markSoldTitle: "Als verkauft markieren?",
        markSoldMessage: "Das Angebot wird 4 Stunden lang als VERKAUFT angezeigt und danach automatisch archiviert. Du kannst es jederzeit über den Archiviert-Filter wiederherstellen.",
        markSoldConfirm: "Ja, als verkauft markieren",
        restoreTitle: "Angebot wiederherstellen?",
        restoreMessage: "Das Angebot wird wieder aktiv und ist erneut öffentlich sichtbar.",
        restoreConfirm: "Ja, wiederherstellen",
        untitled: "Ohne Titel",
        listingCount: "{n} Angebot",
        listingsCount: "{n} Angebote",
        tipsTitle: "Tipps zum Inserieren",
        tips: [
          {
            title: "Professionelle Fotos hinzufügen",
            desc: "Angebote mit hochwertigen Fotos erhalten mehr Anfragen.",
          },
          {
            title: "Eine detaillierte Beschreibung schreiben",
            desc: "Hebe einzigartige Merkmale, nahegelegene Annehmlichkeiten und den Zustand hervor.",
          },
          {
            title: "Richtig bepreisen",
            desc: "Nutze unser kostenloses Bewertungstool, um den richtigen Preis zu finden.",
          }
        ],
        deleteConfirm: "Bist du sicher, dass du dieses Angebot löschen möchtest? Dies kann nicht rückgängig gemacht werden.",
        boostModal: {
          title: "Boost deine Angebote",
          sub: "Erhalte vorrangige Platzierung in Suchergebnissen und 14 Tage Rotation im Featured-Karussell.",
          choosePackage: "Paket wählen",
          thirtyDays: "14 Tage · bis zu {n} Angebot",
          thirtyDaysPlural: "14 Tage · bis zu {n} Angeboten",
          selectListings: "Angebote auswählen ({selected}/{max})",
          active: "✓ Aktiv",
          selected: "✓ Ausgewählt",
          select: "Auswählen",
          continue: "Weiter zur Kasse →",
          selectOne: "Bitte wähle mindestens ein Angebot.",
        },
      },
      boostSelect: {
        title: "Angebote boosten",
        sub: "Wähle genau 1 Angebot aus, das für 14 Tage geboostet werden soll.",
        loading: "Deine Angebote werden geladen…",
        noListings: "Keine Angebote zum Boosten",
        noListingsSub: "Stelle zuerst ein Angebot ein und komm dann zurück, um es zu boosten.",
        noListingsCta: "+ Angebot einstellen",
        signInTitle: "Melde dich an, um Angebote zu boosten",
        signInSub: "Du brauchst ein Konto, um Angebote auf CosmoEstates zu boosten.",
        signIn: "Anmelden",
        maxNotice: "Du kannst 1 Angebot gleichzeitig boosten.",
        alreadyBoosted: "Bereits geboostet",
        selectedCount: "{n} ausgewählt",
        perListing: "€10,99 pro Angebot · 14 Tage",
        total: "Gesamt",
        boostSelected: "Auswahl boosten →",
        redirecting: "Weiterleitung zur Kasse…",
        back: "Zurück zu meinen Angeboten",
      },
      reset: {
        invalidLink: "Dieser Reset-Link ist ungültig oder abgelaufen.",
        minLength: "Das Passwort muss mindestens 6 Zeichen lang sein",
        noMatch: "Passwörter stimmen nicht überein",
        success: "Passwort aktualisiert!",
        successSub: "Du wirst zur Anmeldung weitergeleitet...",
        title: "Neues Passwort festlegen",
        sub: "Wähle ein starkes Passwort für dein Konto.",
        newPassword: "Neues Passwort",
        confirmPassword: "Neues Passwort bestätigen",
        placeholder: "Mindestens 6 Zeichen",
        confirmPlaceholder: "Neues Passwort wiederholen",
        submit: "Passwort aktualisieren",
        requestNew: "Neuen Reset-Link anfordern",
      },
      verifyEmail: {
        noEmailFound: "Keine E-Mail gefunden. Bitte erneut registrieren.",
      },
      privacy: {
        title: "Datenschutzrichtlinie",
        lastUpdated: "Zuletzt aktualisiert: April 2026",
        comingSoon: "Demnächst verfügbar.",
      },
      terms: {
        title: "Nutzungsbedingungen",
        lastUpdated: "Zuletzt aktualisiert: April 2026",
        comingSoon: "Demnächst verfügbar.",
      },
      contact: {
        tag: "Kontakt aufnehmen",
        title: "Kontakt",
        sub: "Frage, Fehler entdeckt oder Idee zu teilen? Wir lesen jede Nachricht.",
        categoryLabel: "Worum geht es?",
        category: {
          support: "Support",
          tips: "Tipps & Vorschläge",
          bug: "Fehlermeldung",
          improvement: "Verbesserungs-Idee",
        },
        nameLabel: "Dein Name",
        namePlaceholder: "z. B. Maria Ioannou",
        emailLabel: "Deine E-Mail",
        emailPlaceholder: "z. B. maria@example.com",
        messageLabel: "Nachricht",
        messagePlaceholder: "Erzähl uns so viel du möchtest…",
        submit: "Nachricht senden",
        sending: "Wird gesendet…",
        errorMsg: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
        successTitle: "Nachricht gesendet!",
        successSub: "Danke — wir melden uns bald bei dir.",
        sendAnother: "Eine weitere Nachricht senden",
      },
      about: {
        tag: "Über uns",
        title: "Der neue",
        titleHighlight: "Immobilienmarktplatz Zyperns",
        sub: "Gebaut für Käufer, Verkäufer, Makler und Unternehmen.",
        missionLabel: "Unsere Mission",
        missionTitle: "Zypern-Immobilien für alle zugänglich machen",
        missionText: "Wir haben CosmoEstates entwickelt, um die Suche nach einem neuen Zuhause in Zypern einfach und reibungslos zu machen. Wie stellen wir das sicher? Durch die Integration von Werkzeugen wie KI, intelligenter Suche und weiteren Möglichkeiten, gut über den aktuellen Wohnungsmarkt informiert zu sein, damit du den richtigen Ort findest, der deine Kriterien erfüllt.",
        statsListings: "Aktive Angebote",
        statsUsers: "Nutzer",
        statsAI: "KI-basierte Einblicke",
        statsTools: "Einzigartige Pro-Tools",
        valuesTitle: "Was uns antreibt",
        value1Title: "Transparenz",
        value1: "Echte Preise, echte Daten, keine versteckten Gebühren. Was du siehst, ist, was du bekommst.",
        value2Title: "Intelligenz",
        value2: "KI-gestützte Nachbarschafts-Einblicke und Marktdaten, damit du dich mit Sicherheit entscheidest.",
        value3Title: "Community",
        value3: "Verbindet Käufer, Verkäufer und Makler in ganz Zypern auf einer vertrauenswürdigen Plattform.",
        ctaTitle: "Bereit, dein Zuhause in Zypern zu finden?",
        ctaSub: "Durchsuche Hunderte Immobilien in ganz Zypern.",
        ctaBtn: "Angebote durchsuchen",
        diffSectionTitle: "Was uns unterscheidet",
        diff1Title: "Werkzeuge für einfachere Entscheidungen",
        diff1Body: "Renovierungs-Kostenschätzungen, Nachbarschafts-Scores aus öffentlichen Quellen und Preisvergleiche — gebaut, um Käufern Kontext zu geben, nicht nur Angebote.",
        diff2Title: "Geprüfte Makler finden",
        diff2Body: "Makler werden vor der Freischaltung geprüft. Erhalte Angebote von Maklern, die dir beim Kaufen, Verkaufen, Mieten oder Vermieten helfen.",
        diff3Title: "Hochwertige Angebote",
        diff3Body: "Angebote werden geprüft, um Spam und minderwertige Inserate zu vermeiden.",
        diff4Title: "Für Zypern gebaut",
        diff4Body: "Speziell für den zyprischen Immobilienmarkt entwickelt.",
        coverageSectionTitle: "Was wir abdecken",
        coverageDistricts: "Abgedeckte Bezirke",
        coverageListings: "Angebote auf der Plattform",
        coverageFounded: "Gegründet",
        getStartedTitle: "Loslegen",
        ctaBuyersHeading: "Käufer",
        ctaBuyersBody: "Durchsuche Zypern-Angebote mit verifizierten Nachbarschafts-Daten.",
        ctaBuyersBtn: "Angebote durchsuchen →",
        ctaSellersHeading: "Verkäufer",
        ctaSellersBody: "Inseriere deine Immobilie und erreiche ernsthafte Käufer.",
        ctaSellersBtn: "Immobilie inserieren →",
        ctaAgentsHeading: "Makler",
        ctaAgentsBody: "Werde Teil der Plattform als geprüfter Makler. Werde mit qualifizierten Käufern zusammengebracht.",
        ctaAgentsBtn: "Als Makler bewerben →",
      },
      listProperty: {
        title: "Immobilie inserieren",
        sub: "Fülle die Details unten aus, um dein Angebot zu veröffentlichen.",
        editTitle: "Angebot bearbeiten",
        editSub: "Aktualisiere deine Immobilien-Details unten.",
        signInTitle: "Anmelden, um deine Immobilie zu inserieren",
        signInSub: "Du brauchst ein Konto, um auf CosmoEstates ein Angebot einzustellen.",
        editSignInTitle: "Anmelden, um Angebote zu bearbeiten",
        loadingListing: "Angebot wird geladen...",
        signIn: "Anmelden",
        register: "Registrieren",
        steps: ["Immobilie", "Lage", "Details", "Kontakt", "Vorschau"],
        step1Title: "Immobilien-Details",
        step1Desc: "Beginne mit den Grundlagen — was du inserierst und was es besonders macht. Je klarer du bist, desto passendere Käufer schicken wir dir.",
        step1TipsLabel: "Was du schreiben solltest",
        step1Tip1: "Sei spezifisch im Titel — Käufer scannen Ergebnisse in Sekunden",
        step1Tip2: "Nenne die Stadt oder Nachbarschaft für sofortigen Kontext",
        step1Tip3: "Vermeide GROSSBUCHSTABEN, Emojis oder Verkaufssprache",
        step2Title: "Lage",
        step2Desc: "Die Lage ist der größte Faktor bei der Immobiliensuche. Käufer filtern zuerst nach Stadt und drillen dann in bestimmte Nachbarschaften. Je spezifischer, desto besser passen die Leads.",
        step2TipsLabel: "Zur Adresse",
        step2Tip1: "Die vollständige Adresse bleibt privat, bis ein Käufer dir schreibt",
        step2Tip2: "Dein Angebot erscheint auf der Zypern-Karte basierend auf der Stadt",
        step2Tip3: "Konkrete Nachbarschaften helfen Käufern, dich in Filtern zu finden",
        step3Title: "Beschreibung und Ausstattung",
        step3Desc: "Eine lebendige Beschreibung und großartige Fotos halten Käufer beim Scrollen an. Führe mit dem markantesten Merkmal an — dem Meerblick, der renovierten Küche, der Dachterrasse.",
        step3TipsLabel: "Fotos und Beschreibung",
        step3Tip1: "Fotografiere tagsüber mit offenen Fenstern und Rollos",
        step3Tip2: "Das erste Foto wird zum Titelbild des Angebots",
        step3Tip3: "Erwähne nahegelegene Strände, Schulen oder Verkehrsanbindungen",
        step4Title: "Kontaktdaten",
        step4Desc: "Käufer erreichen dich zuerst über die CosmoEstates-Nachrichten. Sende und empfange Nachrichten in deinem eigenen Tempo.",
        step4TipsLabel: "So funktioniert der Kontakt",
        step4Tip1: "Käufer erreichen dich zuerst über die CosmoEstates-Nachrichten",
        step4Tip2: "Du kannst in deinem eigenen Tempo aus deinem Posteingang antworten",
        step5Title: "Vorschau und Bestätigung",
        step5Sub: "Prüfe dein Angebot unten. Klicke einen beliebigen Abschnitt an, um ihn zu bearbeiten.",
        editStep5Sub: "Prüfe deine Änderungen. Klicke einen beliebigen Abschnitt an, um ihn zu bearbeiten.",
        forSale: "Zum Verkauf",
        forRent: "Zur Miete",
        propertyType: "Immobilientyp",
        selectType: "Typ auswählen...",
        listingTitle: "Angebots-Titel",
        monthlyRent: "Monatliche Miete (EUR)",
        askingPrice: "Angebotspreis (EUR)",
        city: "Stadt",
        regionDistrict: "Region / Bezirk",
        fullAddress: "Vollständige Adresse",
        plotSize: "Grundstücksgröße (m²)",
        coveredArea: "Wohnfläche (m²)",
        bedrooms: "Schlafzimmer",
        bathrooms: "Badezimmer",
        select: "Auswählen...",
        description: "Beschreibung",
        keyFeatures: "Hauptmerkmale",
        commaSeparated: "(kommagetrennt)",
        photos: "Fotos",
        photosUpTo: "(bis zu 10)",
        addPhotos: "Fotos hinzufügen",
        addMore: "+ Weitere hinzufügen",
        mainPhoto: "Titelbild",
        yourName: "Dein Name",
        fullName: "Vollständiger Name",
        phoneNumber: "Telefonnummer",
        emailAddress: "E-Mail-Adresse",
        agencyName: "Agentur-Name",
        optional: "(optional)",
        edit: "Bearbeiten",
        back: "Zurück",
        continue: "Weiter",
        submitting: "Wird gesendet...",
        submitListing: "Angebot einreichen",
        saving: "Wird gespeichert...",
        saveChanges: "Änderungen speichern",
        successTitle: "Angebot eingereicht!",
        successSub: "Deine Immobilie ist jetzt live auf CosmoEstates.",
        editSuccessTitle: "Angebot aktualisiert!",
        editSuccessSub: "Deine Änderungen wurden erfolgreich gespeichert.",
        backToMyListings: "Zurück zu meinen Angeboten",
        viewListing: "Angebot ansehen",
        boostTitle: "Mehr Sichtbarkeit gewünscht?",
        boostSub: "Booste dein Angebot für vorrangige Platzierung in den Suchergebnissen und 14 Tage Rotation im Featured-Karussell.",
        boostCta: "Dieses Angebot boosten",
        skipForNow: "Vorerst überspringen",
        previewType: "Typ",
        previewTitle: "Titel",
        previewPrice: "Preis",
        previewAddress: "Adresse",
        previewPlotSize: "Grundstücksgröße",
        previewCoveredArea: "Wohnfläche",
        previewBedsBaths: "Schlafzi. / Bäder",
        previewPhotos: "Fotos",
        previewName: "Name",
        previewPhone: "Telefon",
        previewEmail: "E-Mail",
        previewAgency: "Agentur",
        previewSectionProperty: "Immobilien-Details",
        previewSectionLocation: "Lage",
        previewSectionDetails: "Beschreibung und Ausstattung",
        previewSectionContact: "Kontakt",
        photoSingular: "Foto",
        photoPlural: "Fotos",
        bed: "Schlafzi.",
        bath: "Bad",
        errors: {
          propertyType: "Wähle einen Immobilientyp",
          title: "Titel ist erforderlich",
          titleShort: "Bitte gib einen aussagekräftigen Titel ein (mindestens {min} Zeichen).",
          titleGibberish: "Bitte gib einen echten Titel ein — das sieht nach Platzhalter-Text aus.",
          price: "Preis ist erforderlich",
          city: "Stadt ist erforderlich",
          address: "Adresse ist erforderlich",
          bedrooms: "Anzahl der Schlafzimmer ist erforderlich",
          bathrooms: "Anzahl der Badezimmer ist erforderlich",
          size: "Wohnfläche ist erforderlich",
          plotSize: "Grundstücksgröße ist erforderlich",
          description: "Beschreibung ist erforderlich",
          descriptionShort: "Beschreibung benötigt mindestens {min} Zeichen (aktuell {have}).",
          descriptionGibberish: "Bitte schreibe eine echte Beschreibung — das sieht nach Platzhalter-Text aus.",
          photos: "Bitte füge mindestens {min} Fotos hinzu, um dein Angebot zu veröffentlichen.",
          photosNeed: "Füge {missing} weitere{plural} Fotos hinzu, um zu veröffentlichen ({have} von {min}).",
          contactName: "Name ist erforderlich",
          contactPhone: "Telefon ist erforderlich",
          contactEmail: "Gültige E-Mail erforderlich",
        },
      },
      categoryGrid: {
        title: "Stöbern nach",
        titleHighlight: "Kategorie",
        sub: "Entdecke jeden Immobilientyp an einem Ort",
        viewAll: "Alle Kategorien ansehen →",
        listingsSuffix: "Angebote",
      },
      findBroker: {
        eyebrow: "GEPRÜFTE FACHLEUTE",
        trustVerified: "CREAA verifiziert",
        trustResponse: "Schnelle Antwort",
        trustFree: "Kostenlos kontaktieren",
        title: "Makler finden",
        sub: "Verbinde dich mit geprüften Immobilienprofis in ganz Zypern.",
        applyAsBroker: "Als Makler bewerben",
        loading: "Makler werden geladen...",
        noVerified: "Noch keine geprüften Makler",
        noVerifiedSub: "Wir onboarden gerade unsere ersten Makler. Schau bald wieder vorbei.",
        contact: "Kontakt",
        modal: {
          eyebrow: "COSMOESTATES",
          title: "Als Makler bewerben",
          sub: "Fülle deine Angaben aus, wir prüfen deine Bewerbung.",
          fullName: "Vollständiger Name",
          agencyName: "Agentur-Name",
          phone: "Telefonnummer",
          email: "E-Mail-Adresse",
          etek: "ETEK-Nummer (optional)",
          speciality: "Spezialisierung (optional)",
          languages: "Sprachen",
          city: "Stadt",
          selectCity: "Stadt auswählen...",
          description: "Über dich",
          descriptionPlaceholder: "Erzähle Kunden von deiner Erfahrung, deinem Track Record und was dich als Immobilienprofi in Zypern auszeichnet...",
          submit: "Bewerbung senden",
          successTitle: "Bewerbung eingereicht!",
          successSub: "Wir prüfen deine Bewerbung und melden uns innerhalb von 2 Werktagen.",
          done: "Fertig",
        },
        errors: {
          required: "Erforderlich",
          validEmail: "Gültige E-Mail erforderlich",
        },
        viewProfile: "Profil ansehen",
      },
      brokerProfile: {
        back: "Zurück zu den Maklern",
        verified: "Geprüfter Makler",
        call: "Anrufen",
        emailBtn: "E-Mail",
        about: "Über",
        speciality: "Spezialisierung",
        languages: "Sprachen",
        etek: "ETEK-Nr.",
        location: "Ansässig in",
        notFound: "Makler nicht gefunden.",
        noBio: "Keine Biografie verfügbar.",
        noSpeciality: "Allgemeine Praxis",
        editProfile: "Profil bearbeiten",
        saveChanges: "Änderungen speichern",
        saving: "Wird gespeichert...",
        saved: "Profil aktualisiert",
        cancelEdit: "Abbrechen",
        editPhone: "Telefonnummer",
        editDescription: "Über dich",
        editDescPlaceholder: "Erzähle Kunden von deiner Erfahrung, deinem Track Record und was dich auszeichnet...",
        editSpeciality: "Spezialisierung",
        editSpecPlaceholder: "z. B. Luxusvillen, Gewerbe",
        editLanguages: "Sprachen",
        editLangPlaceholder: "z. B. Englisch, Griechisch, Russisch",
        yourListings: "Deine aktiven Angebote",
        noListings: "Noch keine aktiven Angebote.",
      },
      supportChat: {
        initialMessage: "Hi! Ich bin dein CosmoEstates-Assistent. Frag mich alles rund um Kauf, Verkauf oder Miete von Immobilien in Zypern — ich bin rund um die Uhr da.",
        headerName: "CosmoEstates Support",
        headerStatus: "KI-Assistent · Immer online",
        placeholder: "Frage alles rund um Zypern-Immobilien...",
        quickReplies: ["Wie kaufe ich eine Immobilie in Zypern?", "Beste Regionen für Expats?", "Wie funktioniert die Bewertung?", "Wie hoch sind Übertragungsgebühren in Zypern?"],
      },
      boostCheckout: {
        back: "Zurück",
        heroEyebrow: "DEIN LISTING-BOOST",
        heroTitle: "Bring dein Angebot vor mehr Käufer",
        heroSub: "Sicherer Checkout via Stripe · Einmalige Zahlung, kein Abo",
        formTitle: "Wähle dein Paket",
        formSubtitle: "Ein einzelnes Angebot boosten",
        title: "Dein Angebot boosten",
        sub: "Vorrangige Platzierung in der Suche & Rotation im Featured-Karussell für 14 Tage",
        emailLabel: "E-Mail-Adresse",
        included: "Alles enthalten:",
        redirecting: "Weiterleitung zu Stripe…",
        pay: "Zahle €{price} — Sicherer Checkout",
        ssl: "🔒 256-bit SSL · Powered by Stripe",
        oneTime: "einmalig",
        thirtyDayUpTo: "14 Tage · bis zu {n} {unit}",
        listingUnit: "Angebot",
        listingsUnit: "Angebote",
        orderSummary: "Bestellübersicht",
        listingBoost: "Angebots-Boost",
        duration: "Laufzeit",
        thirtyDays: "14 Tage",
        listings: "Angebote",
        upTo: "Bis zu",
        priceVat: "Preis (inkl. MwSt.)",
        total: "Gesamt",
        termsNote: "Einmalige Zahlung. Kein Abo.",
        features: [
          {
            title: "Bessere Platzierung",
            desc: "Geboostete Angebote rotieren über dem Standard-Pool in den Suchergebnissen und geben deiner Immobilie einen klaren Sichtbarkeitsvorteil.",
          },
          {
            title: "Featured-Karussell-Rotation",
            desc: "Dein Angebot rotiert durch das Featured-Karussell auf der Startseite — geboostete Angebote erhalten vorrangige Platzierung, wenn Käufer die Seite besuchen.",
          }
        ],
      },
      renovation: {
        back: "Zurück",
        title: "Renovierungs-Tool",
        sub: "Erstelle ein detailliertes Renovierungsbudget Raum für Raum mit echten Zypern-Preisen und Qualitätsstufen.",
        propertyLocation: "Immobilien-Lage",
        step1: "Was renovierst du?",
        step1Add: "Weiteren Raum hinzufügen",
        step2: "Arbeitspunkte wählen",
        step3: "Qualitätsstufe",
        outdoorArea: "Außenbereich",
        totalFloorArea: "Gesamtwohnfläche",
        roomSize: "Raumgröße",
        continueBtn: "Weiter",
        selectedCount: "ausgewählt",
        estimatedCostRoom: "Geschätzte Kosten für diesen Raum",
        addToPlan: "Hinzufügen",
        toPlan: "zum Plan",
        yourPlan: "Dein Renovierungsplan",
        itemsLabel: "Positionen",
        tipsTitle: "Renovierungs-Tipps",
        previous: "Zurück",
        next: "Weiter",
        costSummary: "Kostenübersicht",
        emptyPlan: "Füge Räume zu deinem Plan hinzu, um eine vollständige Kostenaufschlüsselung zu sehen.",
        estimatedTotal: "Geschätzte Summe:",
        downloadReport: "Bericht herunterladen",
        contingency: "Rechne 10–15 % Puffer für unerwartete Kosten ein",
        costByQuality: "Kosten nach Qualitätsstufe",
        rooms: {
          kitchen: "Küche",
          bathroom: "Badezimmer",
          bedroom: "Schlafzimmer",
          living: "Wohnzimmer",
          outdoor: "Außenbereich / Pool",
          whole: "Komplettes Objekt",
        },
        quality: {
          budget: {
            label: "Budget",
            desc: "Funktionale Ausstattung, lokale Materialien",
            tag: "Wirtschaftlich",
          },
          mid: {
            label: "Mittelklasse",
            desc: "Qualitätsmarken, moderne Ausstattung",
            tag: "Am häufigsten",
          },
          luxury: {
            label: "Luxus",
            desc: "Premium-Importmaterialien & Design",
            tag: "Highend",
          },
        },
        renovItems: {
          kitchen: {
            cabinets: "Schränke & Arbeitsplatten",
            appliances: "Geräte",
            tiling: "Boden- & Wandfliesen",
            plumbing: "Sanitärinstallation",
            electrics: "Elektrik",
            painting: "Anstrich",
          },
          bathroom: {
            fixtures: "Sanitärkeramik & Armaturen",
            tiling: "Boden- & Wandfliesen",
            shower: "Dusche / Wanne",
            plumbing: "Sanitärinstallation",
            electrics: "Elektrik & Beleuchtung",
            vanity: "Waschtisch & Stauraum",
          },
          bedroom: {
            flooring: "Bodenbelag",
            painting: "Anstrich",
            wardrobes: "Einbauschränke",
            electrics: "Elektrik & Beleuchtung",
            ac: "Klimaanlage",
          },
          living: {
            flooring: "Bodenbelag",
            painting: "Anstrich",
            electrics: "Elektrik & Beleuchtung",
            ac: "Klimaanlage",
            fireplace: "Kamin",
            windows: "Fenster & Türen",
          },
          outdoor: {
            pool: "Swimmingpool",
            landscaping: "Landschaftsgestaltung",
            paving: "Pflasterung / Decking",
            pergola: "Pergola / Beschattung",
            fence: "Zaun / Tore",
            bbq: "Grill / Outdoor-Küche",
          },
          whole: {
            structural: "Statik / Wände",
            flooring: "Gesamter Bodenbelag",
            painting: "Kompletter Anstrich",
            kitchen: "Komplette Küche",
            bathrooms: "Badezimmer (alle)",
            electrics: "Komplette Neuverkabelung",
            plumbing: "Komplette Sanitär",
            windows: "Alle Fenster & Türen",
            ac: "Komplettes Klimasystem",
          },
        },
        tipCategories: {
          Budget: "Budget",
          ROI: "Wertsteigerung",
          Planning: "Planung",
          Rental: "Miete",
          Materials: "Materialien",
        },
        tips: [
          {
            title: "Hol dir immer 3 Angebote",
            body: "Lohnkosten in Zypern variieren zwischen Handwerkern um bis zu 40 %. Hol dir immer mindestens drei schriftliche Angebote, bevor du dich festlegst — und prüfe, ob Material, Abfallentsorgung und MwSt. enthalten sind.",
          },
          {
            title: "Fliesen am Saisonende kaufen",
            body: "Fliesenhändler in Zypern reduzieren Auslaufware im Oktober und März stark. Etwas mehr zu kaufen, als du brauchst, schützt außerdem vor Bruch und späterem Ersatz.",
          },
          {
            title: "Sanitärinstallation nicht verlegen",
            body: "Das Verlegen von Rohren im Bad oder in der Küche kann €1.500–€3.000 zusätzlich kosten. Wenn du mit bestehenden Rohrpositionen arbeiten kannst, sparst du erheblich.",
          },
          {
            title: "Im Winter renovieren",
            body: "Handwerker in Zypern haben von April bis September am meisten zu tun. Aufträge im November bis Januar bringen dir meist 10–20 % niedrigere Stundensätze und schnellere Ausführung.",
          },
          {
            title: "Küchen bringen den meisten Mehrwert",
            body: "Eine Mittelklasse-Küchenrenovierung in Zypern erhöht den Immobilienwert typischerweise um das 1,5- bis 2-Fache ihrer Kosten. Konzentriere dein Budget hier, wenn Wiederverkaufswert dein Ziel ist.",
          },
          {
            title: "Ein Pool bringt 8–12 % Wertsteigerung",
            body: "In Limassol und Paphos erhöht der Bau eines Pools bei einer Villa oder einem freistehenden Haus den Angebotspreis um durchschnittlich 8–12 %. Er verbessert auch die Rendite bei Kurzzeitmieten dramatisch.",
          },
          {
            title: "Neutrale Farben verkaufen sich schneller",
            body: "Immobilien in warmen Neutraltönen (Off-White, Greige, Warmgrau) verkaufen sich in Zypern im Schnitt 15 Tage schneller als solche mit kräftigen oder veralteten Farbschemata.",
          },
          {
            title: "Doppelverglasung amortisiert sich",
            body: "Ein Upgrade auf doppelverglaste Aluminiumfenster reduziert Klimaanlagenkosten in Zypern um bis zu 30 % jährlich. Käufer fragen zunehmend danach — ein starkes Verkaufsargument.",
          },
          {
            title: "Genehmigungen vor Baubeginn einholen",
            body: "Bauliche Änderungen, Anbauten und Poolbau erfordern eine Baugenehmigung der Gemeinde. Ungenehmigte Arbeiten können den Immobilienverkauf blockieren und Strafen nach sich ziehen.",
          },
          {
            title: "Elektrik proaktiv aufrüsten",
            body: "Viele ältere Immobilien in Zypern laufen mit 40-Ampere-Einphasen-Systemen. Ein Upgrade auf Dreiphasenstrom bei offenen Wänden kostet €800–€1.200 zusätzlich, spart aber späteres Wiedereröffnen.",
          },
          {
            title: "Vor dem Fliesen dämmen",
            body: "Dachdämmung ist deutlich einfacher und günstiger, bevor die Innenausbauten fertig sind. In der Sommerhitze Zyperns senkt gute Dämmung die Klimakosten um bis zu 25 %.",
          },
          {
            title: "Wasserenthärter früh installieren",
            body: "Zypern hat eines der härtesten Wasser Europas. Ein Wasserenthärter, der während einer Bad- oder Küchenrenovierung installiert wird, schützt Armaturen, Fliesen und Geräte vor Kalkschäden.",
          },
          {
            title: "Ensuite bringt €150/Woche mehr Miete",
            body: "Ein Ensuite-Bad zum Hauptschlafzimmer in Limassol oder Paphos steigert das Kurzzeit-Miet-Einkommen in der Hauptsaison typischerweise um €100–€200 pro Woche.",
          },
          {
            title: "Solaranlagen ziehen Mieter an",
            body: "Immobilien mit Solarthermie und Photovoltaik erzielen in Zypern 8–12 % höhere Langzeitmieten. Mieter schätzen niedrigere Stromrechnungen im heißen Klima.",
          },
          {
            title: "Schnelles WLAN ist Pflicht",
            body: "Für Kurzzeitmieten erzielen Immobilien mit Glasfaser (100 Mbit/s+) deutlich bessere Bewertungen und können 5–10 % Aufschlag verlangen. Cyta-Glasfaser ist in den meisten städtischen Gebieten verfügbar.",
          },
          {
            title: "Beschattung verdoppelt Sommer-Buchungen",
            body: "Immobilien mit Pergolen, Segeln oder überdachten Terrassen erhalten in der Zypern-Hitze bis zu doppelt so viele Sommer-Buchungen auf Airbnb wie solche mit ungeschützten Außenbereichen.",
          },
          {
            title: "Lokaler Stein ist halb so teuer",
            body: "Importierter Marmor aus Italien kostet 3–4 mal so viel wie lokaler zyprischer Stein, der genauso attraktiv und besser klimatauglich ist. Frage deinen Lieferanten nach Lefkara- oder Paphos-Stein.",
          },
          {
            title: "Feinsteinzeug schlägt Keramik im Außenbereich",
            body: "Keramikfliesen brechen bei den Temperaturschwankungen zwischen Winter und Sommer in Zypern. Feinsteinzeugfliesen (PEI 4 oder 5) sind für Außenterrassen und Poolumrandungen unerlässlich.",
          },
          {
            title: "Marine-Beschläge in Küstennähe",
            body: "Innerhalb von 2 km zur Küste korrodieren Standard-Stahlbeschläge innerhalb von 3–5 Jahren. Spezifiziere Marine-Edelstahl oder Aluminium für Geländer, Schrauben und Außenbeschläge.",
          },
          {
            title: "Großformatige Fliesen wirken hochwertig",
            body: "60x60-cm- oder 80x80-cm-Fliesen in Bad oder Küche schaffen einen hochwertigen Look mit weniger Fugen. Der Preisunterschied zu 30x30-cm-Fliesen liegt oft nur bei €5–€8 pro m².",
          }
        ],
        notAQuoteBanner: "Indikative Schätzung — kein Angebot. Basierend auf typischen Handwerker-Sätzen in Zypern (2024–2025). Tatsächliche Angebote variieren je nach Zustand, Materialien und Handwerker. Hol dir 2–3 schriftliche Angebote, bevor du dich festlegst — verwende diese Zahlen nicht als Preisziele in Verhandlungen.",
      },
      mortgage: {
        back: "Zurück",
        title: "Hypotheken-Tools",
        heroTitle: "Finanz-Kompass",
        sub: "Berechne Raten und sieh, wie Sondertilgungen dir Geld sparen.",
        tabMortgage: "Hypothekenrechner",
        tabAffordability: "Leistbarkeits-Check",
        tabOverpayment: "Sondertilgungs-Rechner",
        yourDetails: "Deine Angaben",
        propPrice: "Immobilienpreis",
        deposit: "Eigenkapital",
        loanAmtHint: "Darlehensbetrag:",
        interestRate: "Zinssatz (p. a.)",
        interestRateInfo: "Der jährliche Zinssatz deiner Bank. In Zypern liegen Hypothekenzinsen für Hauptwohnsitze typischerweise zwischen 3,5 % und 5,5 %.",
        mortgageTerm: "Laufzeit",
        years: "Jahre",
        monthlyPayment: "Monatliche Rate",
        perMonthFor: "pro Monat für",
        loanAmount: "Darlehensbetrag",
        totalInterest: "Gesamtzinsen",
        totalRepayment: "Gesamtrückzahlung",
        downloadReport: "Hypotheken-Bericht",
        generatingPdf: "PDF wird erstellt…",
        proOnly: "Verfügbar für Pro-Abonnenten",
        repaymentBreakdown: "Rückzahlungs-Aufschlüsselung",
        principal: "Tilgung",
        interest: "Zinsen",
        monthlyLabel: "Monatlich",
        perMonth: "€ / Monat",
        yearByYear: "Rückzahlung Jahr für Jahr",
        year: "Jahr",
        yourFinances: "Deine Finanzen",
        monthlyIncome: "Monatliches Nettoeinkommen",
        monthlyIncomeInfo: "Dein Nettogehalt nach Steuern pro Monat.",
        monthlyExpenses: "Monatliche Ausgaben",
        disposableIncome: "Verfügbares Einkommen:",
        monthlyExpensesInfo: "Regelmäßige feste Ausgaben — Miete, Kredite, Abos, Lebenshaltungskosten.",
        availableDeposit: "Verfügbares Eigenkapital",
        whatCanAfford: "Was du dir leisten kannst",
        maxPropPrice: "Maximaler Immobilienpreis",
        basedOnDTI: "basierend auf einem Schulden-Einkommens-Verhältnis von {pct} %",
        maxMonthly: "Maximale monatliche Rate",
        pctOfDisposable: "35 % des verfügbaren Einkommens",
        maxLoan: "Maximaler Darlehensbetrag",
        yourDeposit: "Dein Eigenkapital",
        depositPct: "Eigenkapital %",
        incomeAlloc: "Einkommensverteilung",
        mortgageLabel: "Hypothek",
        expensesLabel: "Ausgaben",
        remainingLabel: "Rest",
        affordNote: "Basierend auf Kreditvergabekriterien zyprischer Banken: max. 35 % des verfügbaren Nettoeinkommens.",
        adjustToSee: "Passe dein Einkommen und deine Ausgaben an, um zu sehen, was du dir leisten kannst.",
        yourMortgage: "Deine Hypothek",
        loanAmount2: "Darlehensbetrag",
        extraMonthly: "Zusätzliche monatliche Zahlung",
        extraMonthlyInfo: "Wie viel du zusätzlich zur Standardrate zahlst.",
        standardMonthly: "Standard-Rate",
        withOverpayment: "Mit Sondertilgung",
        whatOverpaySaves: "Was Sondertilgungen dir sparen",
        timeSaved: "Gesparte Zeit",
        noSaving: "Keine Ersparnis",
        mortgageEnds: "Hypothek endet",
        interestSaved: "Gesparte Zinsen",
        lessPaidToBank: "Weniger an die Bank gezahlt",
        remainingBalance: "Restschuld über die Zeit",
        standard: "Standard",
        withOverpaymentLegend: "Mit Sondertilgung",
        overpayNote: "Sondertilgungen senken die Restschuld schneller und sparen erhebliche Zinsen.",
        ctaTitle: "Bereit, deine perfekte Immobilie zu finden?",
        ctaSub: "Durchsuche verfügbare Angebote in Zypern und starte deine Reise heute.",
        browseProperties: "Immobilien durchsuchen",
        freeValuation: "Kostenlose Preisschätzung erhalten",
        rateCaption: "Nur ein Beispiel — Zypern-Durchschnitt ~3,15 % (Neukredite, Anfang 2026). Prüfe aktuelle Sätze bei einer Bank.",
        dtiGuidelineNote: "35 % ist eine Richtlinie, keine Regel — tatsächliche Kreditlimits variieren je Bank und werden individuell festgelegt.",
        purchaseCostsDisclaimer: "Berechnet nur Darlehenszahlungen — enthält keine zyprischen Kaufnebenkosten (Übertragungsgebühren, Stempelsteuer, MwSt. auf Neubauten, Anwaltskosten). Für individuelle Beratung wende dich an einen zugelassenen Hypothekenberater.",
      },
      propertyReport: {
        generating: "Wird erstellt...",
        downloadPdf: "PDF herunterladen",
        close: "Schließen",
        coverTag: "Immobilien-Intelligenzbericht",
        forRent: "Zur Miete",
        forSale: "Zum Verkauf",
        askingPrice: "Angebotspreis",
        yieldSuffix: "% Rendite",
        estRentalYield: "Gesch. Mietrendite",
        generated: "Erstellt",
        section01: "Immobilien-Übersicht",
        bedrooms: "Schlafzimmer",
        bathrooms: "Badezimmer",
        floorArea: "Wohnfläche",
        type: "Typ",
        featuresAmenities: "Ausstattung und Merkmale",
        section02: "Preisanalyse",
        thisProperty: "Diese Immobilie",
        cityAverage: "{city}-Durchschnitt",
        aboveAvg: "Bepreist {pct} % über dem Städte-Durchschnitt von {city} pro m² — typisch für Immobilien mit Premium-Merkmalen oder Lage.",
        belowAvg: "Bepreist {pct} % unter dem Städte-Durchschnitt von {city} pro m² — potenziell gutes Preis-Leistungs-Verhältnis im aktuellen Markt.",
        atAvg: "Bepreist genau am Städte-Durchschnitt von {city} pro m².",
        marketTrend: "{city}-Markttrend (12 Monate)",
        annualGrowth: "jährliches Wachstum",
        section03: "Investitions-Übersicht",
        estAnnualYield: "Gesch. Jahresrendite",
        estRentalIncome: "Gesch. Miet-Einnahmen",
        estAnnualIncome: "Gesch. Jahres-Einnahmen",
        yieldNote: "Rendite-Schätzung basiert auf durchschnittlichen Mietrenditen für {type}-Immobilien in {city} zum {today}.",
        section04: "Hypotheken-Zusammenfassung",
        deposit: "Eigenkapital",
        amount: "Betrag",
        loan: "Darlehen",
        monthlyPayment: "Monatliche Rate",
        recommended: "empfohlen",
        mortgageNote: "Basierend auf 4,5 % Zinsen über 25 Jahre. Für individuelle Beratung wende dich an einen zugelassenen Hypothekenberater.",
        section05: "Nachbarschaft",
        generatingAI: "KI-Einblicke für {region} werden erstellt...",
        schoolsNearby: "Schulen in der Nähe",
        transport: "Verkehr",
        airport: "Flughafen",
        expatCommunity: "Expat-Community",
        intlResidents: "% internationale Einwohner",
        aiNote: "Nachbarschafts-Einblicke von KI erstellt · Basierend auf verfügbaren Daten für {city}, Zypern",
        footerText: "cosmoestates.cy · Immobilien-Intelligenzbericht · Erstellt {today}",
        footerDisc: "Alle Daten sind indikativ. CosmoEstates übernimmt keine Haftung für Entscheidungen, die auf diesem Bericht basieren.",
        scoreLabels: {
          noise: "Geräuschpegel",
          schools: "Schulen",
          amenities: "Ausstattung",
          expat: "Expat-Community",
        },
        scoreRating: {
          excellent: "Ausgezeichnet",
          veryGood: "Sehr gut",
          good: "Gut",
          fair: "Ausreichend",
        },
      },
      renovationReport: {
        generating: "Wird erstellt...",
        downloadPdf: "PDF herunterladen",
        close: "Schließen",
        coverTag: "Renovierungs-Kostenbericht",
        roomPlanTitle: "Renovierungsplan mit {n} Räumen",
        finishSub: "{city}, Zypern · {quality}-Ausstattung",
        roomsPlanned: "Geplante Räume",
        baseEstimate: "Basis-Schätzung",
        withContingency: "Mit Puffer",
        section01: "Aufschlüsselung Raum für Raum",
        section02: "Kostenübersicht",
        contingencyLabel: "10 % Puffer",
        totalBudget: "Empfohlenes Gesamtbudget",
        estimatesNote: "Alle Schätzungen basieren auf {quality}-Ausstattungsmaterialien und Lohnsätzen in {city}, Zypern zum {today}. Premium-Ausstattungen können die Kosten um 30–50 % erhöhen.",
        valueNote: "Bei diesem Budget könnte eine Vollrenovierung den Mietwert der Immobilie um geschätzt €{rentMin}–€{rentMax}/Monat und ihren Verkaufswert um €{saleMin}–€{saleMax} erhöhen.",
        footerText: "cosmoestates.cy · Renovierungs-Kostenbericht · Erstellt {today}",
        footerDisc: "Alle Schätzungen sind indikativ. CosmoEstates übernimmt keine Haftung für tatsächliche Renovierungskosten.",
      },
      neighbourhood: {
        back: "Zurück",
        title: "Nachbarschafts-Analyse",
        sub: "Vollständige Scores zu Verkehr, Schulen, Ausstattung und Expat-Dichte — neben dem offiziellen Immobilienpreis-Index. Finde die Gegend, die wirklich zu deinem Lebensstil passt.",
        signInTitle: "Anmelden zum Freischalten",
        signInSub: "Nachbarschafts-Analyse ist für alle registrierten CosmoEstates-Mitglieder kostenlos. Melde dich an oder erstelle ein Konto, um auf detaillierte Scores, KI-Analysen und Investitionsdaten für jede Nachbarschaft in Zypern zuzugreifen.",
        features: ["Verkehrs- und Anbindungs-Scores", "Schul-Einzugsbewertungen", "Immobilienpreis-Index (CBC-Daten)", "Expat-Community-Dichte", "Ausstattungs- und Verkehrsbewertungen", "KI-Nachbarschaftsanalyse"],
        createAccount: "Kostenloses Konto erstellen",
        signIn: "Anmelden",
        freeNote: "Für immer kostenlos. Keine Kreditkarte nötig.",
        aiBadge: "KI-Analyse",
        aiNote: "Ergebnisse sind KI-geschätzt — vor Ort verifizieren",
        clear: "✕ Löschen",
        fullDetails: "Alle Details",
        expatResidents: "% Expat-Einwohner",
        transport: "Verkehr:",
        priceTrend: "Preistrend (6 Quartale)",
        estYield: "Gesch. Mietrendite",
        searchPlaceholder: "Gespeicherte Gebiete durchsuchen oder eine beliebige zyprische Nachbarschaft für KI-Analyse eingeben...",
        analysing: "Wird analysiert...",
        aiAnalyse: "KI analysieren",
        noMatch: "Keine gespeicherten Gebiete passen — drücke Enter oder klicke KI analysieren, um Insights zu generieren für",
        sortBy: "Sortieren nach:",
        sortOptions: {
          expat: "Expat-Dichte",
          schools: "Schulen",
          amenities: "Ausstattung",
          transport: "Verkehr",
          development: "Entwicklung",
        },
        noNeighbourhoods: "Keine Nachbarschaften passen zu deiner Suche.",
        allCities: "Alle Städte",
        barLabels: {
          noise: "Geräusch",
          schools: "Schulen",
          amenities: "Ausstattung",
          expat: "Expats",
          transport: "Verkehr",
        },
        backToNeighbourhoods: "Zurück zu den Nachbarschaften",
        overall: "Gesamt",
        avgAcrossFactors: "Durchschnitt über 5 Faktoren",
        whatMeans: "Was das für dich bedeutet",
        howCompares: "Wie {name} abschneidet",
        allNeighbourhoods: "alle Nachbarschaften",
        showLess: "Weniger anzeigen",
        showMore: "{n} mehr anzeigen",
        nearbySchools: "Schulen in der Nähe",
        keyAmenities: "Wichtige Ausstattung",
        areaHighlights: "Highlights der Gegend",
        ctaTitle: "Bereit, Immobilien in {name} zu erkunden?",
        ctaSub: "Durchsuche verfügbare Angebote in dieser Nachbarschaft und buche heute eine Besichtigung.",
        ctaBtn: "Immobilien in {name} durchsuchen",
        notFound: "Nachbarschaft nicht gefunden.",
        transportChip: "Verkehr:",
        expatsChip: "% Expats",
        overPeriod: "im Zeitraum",
        scoreLabels: {
          noise: "Geräuschpegel",
          schools: "Schulqualität",
          amenities: "Ausstattung",
          expat: "Expat-Community",
        },
        scoreTips: {
          noise: "Höher = ruhiger. Misst Tag-/Nacht-Geräuschpegel durch Verkehr, Nachtleben und Industrie.",
          schools: "Bewertet Qualität und Verfügbarkeit von Schulen, einschließlich internationaler Lehrpläne.",
          amenities: "Umfasst Einzelhandel, Gesundheit, Gastronomie, Freizeit und Alltagsbequemlichkeiten.",
          expat: "Misst Größe und Aktivität der internationalen Community in der Gegend.",
        },
      },
      faq: {
        badge: "Hilfe-Center",
        title: "Häufig gestellte Fragen",
        subtitle: "Alles, was du über Kaufen, Mieten und Inserieren auf CosmoEstates wissen musst.",
        cat: {
          gettingStarted: "Erste Schritte",
          buyingRenting: "Kaufen & Mieten",
          listingProperty: "Immobilie inserieren",
          proBoost: "Pro-Mitgliedschaft & Listing-Boost",
          privacySafety: "Datenschutz & Sicherheit",
        },
        items: {
          gs0q: "Was ist CosmoEstates?",
          gs0a: "CosmoEstates ist ein Immobilienportal für Zypern, das Käufer, Mieter, Verkäufer und Vermieter in allen fünf Bezirken verbindet — Limassol, Nikosia, Paphos, Larnaka und Famagusta. Neben Angeboten bieten wir ein Immobilien-Preisschätzungs-Tool, einen Renovierungs-Kostenrechner, Nachbarschaftsdaten aus Quellen der Zentralbank Zyperns und von OpenStreetMap sowie ein Makler-Verzeichnis.",
          gs1q: "Welche Funktionen benötigen eine Pro-Mitgliedschaft?",
          gs1a: "Pro schaltet die Premium-Ebene von CosmoEstates frei: die vollständige Nachbarschafts-Analyse (Verkehr, Schulen, Ausstattung, Expat-Dichte plus offizieller Immobilienpreis-Index), vorrangige Platzierung im Posteingang von Verkäufern, sofortige Benachrichtigungen, wenn eine passende Immobilie auf den Markt kommt, und Preissenkungs-Alerts für gemerkte Immobilien.",
          gs2q: "Welche Regionen Zyperns werden abgedeckt?",
          gs2a: "Wir decken alle fünf Bezirke der Republik Zypern ab: Limassol, Nikosia, Paphos, Larnaka und Famagusta (freies Gebiet, einschließlich Agia Napa, Protaras und Paralimni). Neue Angebote von Maklern und privaten Eigentümern werden täglich hinzugefügt.",
          br0q: "Wie suche ich nach einer Immobilie?",
          br0a: "Nutze die Suchleiste auf der Startseite, um eine Stadt, Nachbarschaft oder ein Stichwort einzugeben. Auf der Angebots-Seite kannst du dann nach Preis, Immobilientyp (Haus, Wohnung, Villa) und Angebotstyp (Verkauf oder Miete) filtern. Ergebnisse werden nach Relevanz und Angebots-Qualität sortiert — nicht nur nach denjenigen, die für einen Boost bezahlt haben.",
          br1q: "Wie kontaktiere ich einen Verkäufer oder Vermieter?",
          br1a: "Jede Immobilien-Detailseite hat ein eingebautes Nachrichten-Panel. Schreibe deine Nachricht und tippe auf Senden — der Eigentümer oder Makler erhält sie direkt in seinen Posteingang. Du brauchst ein CosmoEstates-Konto, um Nachrichten zu senden. Pro-Mitglieder erhalten ein verifiziertes Pro-Abzeichen an ihren Nachrichten.",
          br2q: "Kann ich Immobilien merken, die mich interessieren?",
          br2a: "Ja. Tippe auf das Herz-Symbol einer Immobilien-Karte oder Detailseite, um sie auf deine Merkliste zu setzen. Alle gemerkten Immobilien findest du im Merkliste-Bereich deines Kontos.",
          br3q: "Was ist die Immobilien-Preisschätzung?",
          br3a: "Unser Immobilien-Preisschätzungs-Tool ist für alle kostenlos — trag ein paar Details zur Immobilie ein und wir senden dir eine indikative Marktwertspanne per E-Mail. Es ist eine Ausgangsschätzung, keine offizielle Bewertung.",
          br4q: "Was ist der Immobilienpreis-Index?",
          br4a: "Die Zentralbank Zyperns veröffentlicht einen vierteljährlichen Wohnimmobilien-Preisindex für jeden Bezirk Zyperns, mit Q1 2010 = 100 als Basis. Wir zeigen diese Reihe auf jeder Nachbarschafts-Detailseite, damit du echte Preistrends auf Bezirksebene sehen kannst — nicht plattforminterne Durchschnitte.",
          br5q: "Warum zeigt ihr keinen Preis pro Quadratmeter?",
          br5a: "Plattforminterne €/m²-Durchschnitte werden aus Angebotspreisen berechnet, was eine kleine, verzerrte Stichprobe ist — sie können mehr irreführen als informieren. Stattdessen zeigen wir den Immobilienpreis-Index der Zentralbank Zyperns für jeden Bezirk, der die maßgebliche veröffentlichte Preisreihe ist.",
          lp0q: "Wie inseriere ich meine Immobilie auf CosmoEstates?",
          lp0a: "Erstelle ein Konto, klicke dann auf „Immobilie inserieren“ im Navigationsmenü. Fülle die Immobilien-Details aus, lade Fotos hoch und veröffentliche. Dein Angebot geht sofort live.",
          lp1q: "Wie viele Fotos sollte ich hinzufügen?",
          lp1a: "Wir empfehlen mindestens 8 Fotos, die alle Hauptraume, den Außenbereich und besondere Merkmale wie Pool oder Garten abdecken.",
          lp2q: "Kann ich mein Angebot nach der Veröffentlichung bearbeiten oder entfernen?",
          lp2a: "Ja. Gehe in deinem Konto zu „Meine Angebote“ und klicke auf Bearbeiten bei einem beliebigen Angebot, um Details zu aktualisieren, Fotos zu tauschen oder den Preis anzupassen. Du kannst die Immobilie auch als verkauft markieren oder ein Angebot jederzeit dauerhaft löschen. Änderungen gehen sofort live.",
          pb0q: "Was ist in der Pro-Mitgliedschaft enthalten?",
          pb0a: "Pro schaltet vier Funktionen frei: die vollständige Nachbarschafts-Analyse (mit offiziellem Immobilienpreis-Index), den priorisierten Verkäufer-Posteingang, damit deine Nachrichten oben erscheinen, Smart Alerts in dem Moment, in dem eine passende Immobilie online geht, und Preissenkungs-Alerts für gemerkte Immobilien.",
          pb1q: "Was ist ein Listing-Boost?",
          pb1a: "Ein Boost ist eine einmalige 14-tägige Aufwertung für ein bestimmtes Angebot, kostet €10,99 pro Angebot. Geboostete Angebote rotieren durch das Boosted-Karussell auf der Startseite und erhalten einen Ranking-Vorteil in den Suchergebnissen innerhalb der aktiven Filter des Käufers.",
          pb2q: "Wie wird das Boost-System fair gehalten?",
          pb2a: "Geboostete Angebote erscheinen nur innerhalb der aktiven Suchfilter des Käufers — sie werden niemals in nicht verwandte Ergebnisse eingefügt.",
          pb4q: "Wie verwalte oder kündige ich mein Pro-Abo?",
          pb4a: "Gehe zu deiner Profil-Seite und klicke auf Abonnement verwalten. Damit öffnet sich das Stripe-Kundenportal, in dem du den Tarif wechseln, die Zahlungsmethode aktualisieren oder kündigen kannst. Falls wir dein Abo nicht in unseren Unterlagen finden, schreib an cosmoestatescyprus@gmail.com und wir kümmern uns darum.",
          ps1q: "Wie melde ich ein verdächtiges Angebot?",
          ps1a: "Tippe auf das Melden-Symbol auf einer Immobilien-Detailseite, um ein verdächtiges oder betrügerisches Angebot zu melden — verfügbar für alle, keine Pro-Mitgliedschaft nötig. Unser Team prüft alle Meldungen innerhalb von 24 Stunden.",
          ps2q: "Wie lösche ich mein Konto?",
          ps2a: "Du kannst die Kontolöschung auf der Einstellungen-Seite unter Konto-Verwaltung beantragen. Alle deine Angebote, Nachrichten und persönlichen Daten werden gemäß den DSGVO-Anforderungen innerhalb von 30 Tagen dauerhaft entfernt.",
        },
      },
      brokerListings: {
        backToProfile: "Zurück zum Profil",
        brokerFallback: "Makler",
        listingsByPrefix: "Angebote von ",
        activeListing: "aktives Angebot",
        activeListings: "aktive Angebote",
        empty: "Dieser Makler hat noch keine aktiven Angebote.",
      },
      legal: {
        breadcrumb: "Rechtliches",
        effectiveDate: "Gültig ab:",
        lastUpdated: "Zuletzt aktualisiert:",
        onThisPage: "Auf dieser Seite",
        backToAll: "← Zurück zu allen rechtlichen Dokumenten",
        indexTitle: "Rechtliche Dokumente",
        indexCrumb: "Rechtliches",
        indexSubtitle: "Die rechtlichen Bestimmungen und Richtlinien, die deine Nutzung von CosmoEstates regeln. Diese Dokumente gelten für alle Nutzer der Plattform — Besucher, registrierte Nutzer, Makler und Partner.",
        indexRead: "Lesen →",
        indexMetaTitle: "Rechtliche Dokumente | CosmoEstates",
        indexMetaDesc: "Verzeichnis der rechtlichen Dokumente von CosmoEstates: AGB, Datenschutz, Cookies, Haftungsausschluss, Nutzungsrichtlinien und Abrechnung.",
        docs: {
          termsTitle: "AGB",
          termsDesc: "Die Regeln für die Nutzung von CosmoEstates: Konto, Angebote, Werkzeuge, Lead-Generierung, Gebühren, Haftung und Streitbeilegung.",
          privacyTitle: "Datenschutzrichtlinie",
          privacyDesc: "Welche personenbezogenen Daten wir erheben, warum wir sie nutzen, an wen wir sie weitergeben, wie lange wir sie speichern und deine Rechte gemäß DSGVO.",
          cookiesTitle: "Cookie-Richtlinie",
          cookiesDesc: "Wie wir Cookies, localStorage und sessionStorage nutzen — einschließlich der Funktion „zuletzt angesehen“ — und wie du die Zustimmung verwaltest.",
          disclaimerTitle: "Haftungsausschluss",
          disclaimerDesc: "Wie KI-Tools (Preisschätzung, Renovierungskosten, Nachbarschafts-Analyse, Hypothekenrechner) funktionieren und wo ihre Grenzen liegen.",
          acceptableUseTitle: "Nutzungsrichtlinien & Angebots-Richtlinie",
          acceptableUseDesc: "Was du auf CosmoEstates einstellen darfst und was nicht, Moderation und Folgen bei Verstößen.",
          billingTitle: "Abo-, Boost- und Rückerstattungsbedingungen",
          billingDesc: "Pro-Tarife, Boost-Pakete, CPL-Gebühren und dein 14-tägiges Widerrufsrecht als Verbraucher.",
        },
      },
      policyEmbed: {
        privacyTitle: "Datenschutzrichtlinie",
        termsTitle: "Nutzungsbedingungen",
        cookiesTitle: "Cookie-Richtlinie",
        subtitle: "Gepflegt von Termageddon — siehe die eingebettete Richtlinie unten für die aktuelle Version.",
        loadingPre: "Bitte warte, während die Richtlinie geladen wird. Falls sie nicht lädt, bitte ",
        clickHere: "hier klicken, um die Richtlinie anzuzeigen",
        clickHereAria: "hier klicken, um die Richtlinie anzuzeigen",
      },
      contactBroker: {
        titlePrefix: "Kontakt ",
        fallbackName: "dieser Makler",
        subtitle: "Wähle, wie du Kontakt aufnehmen möchtest.",
        call: "Anrufen",
        email: "E-Mail",
        empty: "Noch keine Kontaktdaten verfügbar.",
        closeAria: "Schließen",
      },
      rateLimit: {
        proOnlyTitle: "Nur für Pro-Mitglieder",
        proOnlyBody: "Diese Funktion ist ausschließlich für CosmoEstates-Pro-Mitglieder verfügbar.",
        upgradeToPro: "Auf Pro upgraden",
        signInTitle: "Bitte anmelden",
        signInBody: "Du musst angemeldet sein, um diese Funktion zu nutzen.",
        signIn: "Anmelden",
        limitTitle: "Du hast dein Limit erreicht",
        limitBodyPrefix: "Du hast alle ",
        limitBodyMid: " ",
        limitBodySuffix: " Anfragen für dieses 3-Stunden-Fenster verbraucht.",
        resetsInPrefix: "Zurücksetzung in ",
        resetsInSuffix: ".",
        contactHelp: "Kontaktiere uns für Hilfe",
        upgradeHigher: "Upgrade für höhere Limits",
        proHintPrefix: "Pro-Mitglieder erhalten ",
        proHintSuffix: " Anfragen pro 3 Stunden.",
      },
      neighbourhoodCompare: {
        title: "Wie diese Nachbarschaft abschneidet",
        thisNeighbourhood: "Diese Nachbarschaft",
        cityAverageSuffix: "-Durchschnitt",
        cityAverageTitlePrefix: "",
        cityAverageTitleMid: "-Durchschnitt: ",
      },
      boostPage: {
        eyebrow: "LISTING-BOOST",
        heroTitle: "Bring dein Angebot vor mehr Käufer.",
        heroSub: "Bewirb deine Immobilie an Top-Platzierungen, Featured-Bereichen und mit visueller Prominenz — damit ernsthafte Käufer sie zuerst sehen.",
        cancelled: "Checkout abgebrochen — es wurden keine Gebühren erhoben. Wähle ein Paket und versuche es erneut.",
        benefitsTitle: "Vorteile des Boostens",
        benefits: [
          {
            title: "Bessere Platzierung",
            desc: "Geboostete Angebote rotieren über dem Standard-Pool in den Suchergebnissen und geben deiner Immobilie einen klaren Sichtbarkeitsvorteil.",
          },
          {
            title: "Featured-Karussell-Rotation",
            desc: "Dein Angebot rotiert durch das Featured-Karussell auf der Startseite — geboostete Angebote erhalten vorrangige Platzierung, wenn Käufer die Seite besuchen.",
          }
        ],
        packagesTitle: "Boost dein Angebot",
        packageDuration: "14 Tage",
        packageListingLabel: "1 Angebot",
        cta: "Jetzt boosten →",
      },
      boostConfirm: {
        back: "← Zurück zu den Angeboten",
        title: "Boost bestätigen",
        sub: "Prüfe dein Paket und die ausgewählten Angebote vor der Zahlung.",
        sectionPackage: "PAKET",
        sectionListings: "DEINE ANGEBOTE",
        loading: "Angebote werden geladen…",
        untitled: "Angebot ohne Titel",
        total: "Gesamt",
        redirecting: "Weiterleitung zu Stripe…",
        proceed: "Weiter zur Kasse →",
        note: "Du wirst zu Stripe weitergeleitet, um die Zahlung sicher abzuschließen.",
      },
      boostSelectPage: {
        back: "← Zurück zum Paket",
        title: "Wähle deine Angebote",
        packageLabel: "Paket",
        selectedOfCap: "{n}/{cap} ausgewählt",
        next: "Weiter →",
        pickUpTo: "Wähle bis zu {cap} Angebote aus, um sie für 14 Tage zu boosten.",
      },
      proPage: {
        choosePlan: "Wähle deinen Tarif",
        features: {
          priorityInbox: {
            title: "Priorisierter Posteingang",
            desc: "Pro-Mitglieder erhalten aufgrund ihrer Posteingangs-Platzierung mit höherer Wahrscheinlichkeit als Erste eine Antwort.",
          },
          neighbourhood: {
            title: "Nachbarschafts-Analyse",
            bullets: ["Verkehrs- und Anbindungs-Bewertungen", "Nahegelegene Schulen & Bildungsqualität", "Immobilienpreis-Index (CBC-Daten)", "Expat-Dichte & Community-Präsenz"],
          },
          smartAlerts: {
            title: "Smart Alerts",
            desc: "Erhalte sofortige Benachrichtigungen in dem Moment, in dem eine Immobilie, die zu deiner gespeicherten Suche passt, online geht — bleib dem Markt voraus.",
          },
          priceDropAlerts: {
            title: "Preissenkungs-Alerts",
            desc: "Werde benachrichtigt, sobald eine gemerkte Immobilie im Preis sinkt. Sei der Erste, der handelt, wenn Verkäufer sich bewegen.",
          },
        },
      },
      checkoutHero: {
        eyebrow: "DEINE PRO-MITGLIEDSCHAFT",
        title: "Nur noch wenige Schritte bis zum Pro-Zugang",
        sub: "Sicherer Checkout via Stripe · Jederzeit kündbar",
      },
    },
};
